# 05/01/23

## 🚀 Features

#### Introduce transcriptsByInquiry gql endpoint and retire threadsByInquiry (#16756) -
For better integration between Lisa and AppFolio's CRM, we have introduced a new endpoint to more easily process requests for thread transcripts from the CRM. This endpoint provides a transcript that includes messages, showings, and showing histories. 


## 🐛 Bug Fixes

#### Fix guest card sync error (#16751) - 
We have resolved an error with syncing guest cards arising from situations where not all outbound messages on a thread are classified yet. Now, we can sync guest cards even when not all outbound messages are classified.

#### Update existing pmItem instead of creating a new one (#16750) -
We have fixed an issue where new pmItems are created in the database when we should be updating existing pmItems.



# 04/17/23

## 🚀 Features

#### Use current thread info when updating PM items (#16737) -
We have changed our source for updating PM items on the client side from the database to thread info.

#### Unarchive messages where prospects request 7am or 7pm showings (#16729) -
To ensure accurate scheduling for prospects, we will unarchive messages where the showing preference is 7am or 7pm.



# 04/03/23

## 🚀 Features

#### Update Customer Steward Permissions (#16706) -
We have added useful admin job permissions to the Customer Steward usertypes.



# 03/27/23

## 🚀 Features

#### Add Customer Steward Usertypes (#16668, #16677, #16673) -
We have added new usertypes to support Operators who handle only threads coming from our customers and clients. We have also added the necessary functionality for these usertypes to utilize bots in Slack.



# 03/20/23

## 🚀 Features

#### Update deprecated fields in leasing info when updating a property in APM (#16665) -
We have added missing fields for better updating of APM from Lisa.


## 🐛 Bug Fixes

#### Check for invalid task address when sync fails (#16660) - 
We will now get an exception that includes the task ID when a sync fails due to issues with the address field. This will allow us to more easily correct the error.

#### Don't send calendar uninvites when `offsiteProspectConfirmationOptional` is enabled (#16666) - 
We have resolved an issue where we would send calendar uninvites to agents when a prospect doesn't confirm a showing and `offsiteProspectConfirmationOptional` is set to `true` for the firm.



# 03/06/23

## 🚀 Features

#### Ignore spam messages classified as human fallback (#16637) -
Lisa will now more aggressively filter spam messages out of the inbox rather than showing them to Operators.


## 🐛 Bug Fixes

#### Record correct `ThreadId` for `Update_Thread` action (#16647) -
Previously, the `Update_Thread` action recorded the `ThreadId` of the contact's preferred thread rather than the thread where the action actually took place. This has since been fixed.



# 02/20/23

## 🚀 Features

#### Don't tag ILS inbound for feedback (#16621) - 
ILS messages will no longer be tagged for feedback and included in reports sent to agents.


## 🐛 Bug Fixes

#### Low `BOT_ONLY` messages always unarchive (#16620) -
Under certain conditions, Galaxy could take action to predict `NO_ACTION` on messages likely to require Operator intervention. This has since been fixed. 



# 02/13/23

## 🚀 Features

#### Mark prospect thread as inactive when call is routed to the office (#16597) -
Lisa now detects that a thread is inactive if a prospect is calling by phone and being routed to the office.

#### Adjust Senior Operator slots (#16616) -
We have adjusted the distribution of Senior Operator shift slots based on feedback.

#### Remove outdated code (#16614) -
We have removed outdated code associated with FullStory.



# 02/06/23

## 🚀 Features

#### Sync inactive threads with APM upon conversation conclusion (#16572) -
Lisa now will sync prospect guest cards with APM when using a wide variety of QAs that indicate the end of a conversation.



# 01/30/23

## 🚀 Features

#### Further new Operator usertype adjustments (#16560, #16562) -
Ensure new Operator usertype is not automatically clocked out of Command Center and properly receives responses to escalations on Slack.



# 01/23/23

## 🚀 Features

#### New Operator usertype adjustments (#16541) -
Continue refining functionality for new Operator usertypes, including visibility for unread messages in Command Center.



# 01/16/23

## 🚀 Features

#### Add new Operator usertype (#16510) -
We have added new usertypes to support the variety of Operators that are part of our team.

#### Do not create support cases for NPS detractors (#16518) -
Detractor NPS survey responses will no longer create new cases in the support queue.

#### Skip inbox refractor adjustments (#16493, #16504, #16505) -
We are continuing to adjust our skip inbox refractor to best handle inbound that does not require Operator attention.


## 🐛 Bug Fixes

#### Fix APM onboarding sync: always update property policies (#16515) -
We have resolved an issue with the APM onboarding sink that would sometimes result in outdated property policies in Command Center.



# 01/09/23

## 🚀 Features

#### ILS move-in date backstop (#16486) -
Our ILS parser will no longer update a prospect's desired move-in date to any time prior to the current date.


## 🐛 Bug Fixes

#### Add email parsing for swamprentals.com (#16487) - 
Previously, we were not attributing ILS email inbound from swamprentals.com correctly. This has since been fixed.



# 12/19/22

## 🚀 Features

#### Increase number of Senior Operator slots for busy shifts (#16460) -
We have added Senior Operator slots for the M-F 4am-9am and M-Th 9am-2pm shifts.

#### Run Operator draft on Monday instead of Wednesday (#16424) -
The Operator draft will now run on Monday instead of Wednesday. This provides a full week for Operators to schedule the following week's shifts.

#### Increase max number of Operator shifts (#16458) -
Operators can now be assigned a maximum of seven shifts by the draft.



# 12/12/22

## 🚀 Features

#### Increase Operator draft floor (#16443) -
There is now a minimum of 275 Operator slots created per draft.



# 12/5/22

## 🚀 Features

#### Add holidays to Lisa database (#16406) -
We have added observed dates for Christmas Day and New Year's Day to easily block calendars for firms that will have widespread office closures on those dates.

#### Enforce minimum number of slots during weekly draft creation (#16392) -
We now enforce a minimum number of Operator shifts that can be created during the draft.



# 11/28/22

## 🚀 Features

#### Don't accept showing if all showable units are applied for (#16361) -
In cases where all of a task's showable units have approved applications, Lisa will no longer `counteroffer` interested prospects. 

Operators will also no longer be able to accept showings for these tasks and will instead have a QA that lets the prospect know all units are leased.



# 11/21/22

## 🚀 Features

#### Improve `REQUEST_MULTIPLE_SHOW` prediction accuracy (#16371) -
We have continued to refine Lisa's required confidence score to reply to an inbound message with `REQUEST_MULTIPLE_SHOW`.

#### Remove `null` label for `REQUEST_SHOW` `REQUEST_MULTIPLE_SHOW` (#16367) -
In scenarios where Lisa would return a `null` prediction for `REQUEST_SHOW` and `REQUEST_MULTIPLE_SHOW` she will now return a `0.0`.



# 10/31/22

## 🚀 Features

#### Remove operator survey from clock out (#16334) -
Operators will no longer be surved when clocking out.



# 10/10/22

## 🚀 Features

#### Add office phone null check for "Office Phone" and "Realtor" QAs (#16288) -
We have improved the UI for rare situations when neither a task nor firm have a phone number associated with them. 

Previously, the `realtor` and `office` QAs would not work in those instances without any explanation. Now, we present a helpful message to Operators that will help them escalate the issue and collect the missing information.

![Updated realtor and office QA messages](https://user-images.githubusercontent.com/59895301/204657838-e4583f57-d18b-41f9-8639-e55d196d1e58.png)



# 10/03/22

## 🚀 Features

#### Add `What's your email` QA (#16178) -
We now have a QA that will send the email associated with the task to a prospect so they are able to initiate communication with Lisa via email if they prefer. 

#### Include task name when Lisa is identifying themself to prospect (#16263) -
Previously, when prospects asked about who Lisa is, Lisa would reply `My name is Lisa`. Now, on response to these kinds of questions, Lisa will include the task name in the format of `My name is Lisa. I schedule tours for [task].`

#### Use email in calendar uninvite messages to agents in cases of a `null` name (#16271) -
We generally expect all agent and boss threads to have a name associated with them. There can be times though, where we don't have that information. Now, when sending the calendar uninvite message, we will use the agent's email in the text of the message in cases when their name is `null`.



# 09/19/22

## 🚀 Features

#### `REQUEST_MULTIPLE_SHOW` labeling adjustment (#16218) - 
Galaxy will no longer label messages as `REQUEST_MULTIPLE_SHOW` if the prospect has a confirmed appointment within the last 30 days.

#### Updated standalone compatibility (#16215) -
We have made an adjustment to better track the vacancy date of properties from some standalone PM software. 



# 9/12/22

## 🚀 Features

#### Improve CrossSellAction speed (#15982) -
We have optimized the way CrossSellAction serves text when cross selling by QA. 

Previously, the selection of which variation of the QA to use could cause some slowness. That will no longer be an issue.

#### Use prospect from context data when generating prospect for Entrata (#16161) - 
When creating a guest card for Entrata, we will fetch from their database less frequently to avoid errors. 


## 🐛 Bug Fixes

#### Account for null bestUnit (#16210) -
We have resolved a bug that would cause an error if the `findBestUnitForShowing` bot action returned `null`.

#### Create new inquiry if thread missing inquiry ID (#16209) -
To improve APM guest card syncing, Lisa now ensures threads where a showing is scheduled have an `inquiry` ID.



# 9/5/22

## 🚀 Features

#### Edit `LISTINGS` QA (#16177) -
The `LISTINGS` QA now has new copy to minimize the necessity of editing by Operators.

Before:

![Listings QA before edit](https://user-images.githubusercontent.com/7215139/186980417-0f9f8f93-f952-4566-8e02-b4f7e758c285.png)

After:

![Listings QA after edit](https://user-images.githubusercontent.com/7215139/186980319-edf9c306-07f6-4338-8f13-a92d9879ffc6.png)

#### Improve prospect tracking through Lisa (#16172, #16202, #16203) -
We have made some backend changes to ensure that individual prospect inquiries correspond to specific properties. This will enhance our ability to group all the information about a prospect inquiring about a specific property.

We have also had to make numerous adjustments to legacy features to ensure backwards compatibility.

#### Cross sell adjustment for AIR (#16190) -
We have excluded some properties from cross selling to ensure prospects are receiving actionable cross sells.


## 🐛 Bug Fixes

#### Eliminate policy duplication (#16197) -
We have corrected an error where Lisa would attempt to update a task's policies with duplicates of the firm level policy. Now, Lisa will only attempt to update a task's policies when they are different from the firm level policies.

#### Improve self-guided compatibility for standalone customers (#16183) -
Previously, we had cases where Lisa might not send the SmartRent code for some standalone customers. This has since been fixed.



# 8/29/22

## 🚀 Features

#### Automate `REQUEST_SHOW` (#16135) -
Lisa will now respond automatically to `REQUEST_SHOW` messages.

#### `REQUEST_MULTIPLE_SHOW` labeling adjustment (#16151) -
Galaxy will no longer label messages as `REQUEST_MULTIPLE_SHOW` if the prospect has a confirmed appointment within the last hour.



# 08/22/22

## 🚀 Features

#### Add `REQUEST_MULTIPLE_SHOW` automation (#16123) -
By leveraging some of our existing bot logic, we are able to send automated responses to `REQUEST_MULTIPLE_SHOW` inbound from prospects.

#### Don't label `REQUEST_MULTIPLE_SHOW` if a prospect is already scheduled (#16139) -
We have made a change to our `REQUEST_MULTIPLE_SHOW` labeling. Now, if a prospect already has a scheduled showing, we won't label their inbound as `REQUEST_MULTIPLE_SHOW`

#### Send all `REQUEST_MULTIPLE_SHOW` messages to the inbox (#16136) -
To ensure we are correctly scheduling prospects, all inbound labeled `REQUEST_MULTIPLE_SHOW` will enter the inbox for Operator review.

#### Create LeasingAttributionSource for DIGIBLE_NATIVE_ADS (#16132) -
We have added an endpoint for Digible Native Ads.

#### Resolve errors around empty fields when saving policies (#16142, #16128) -
We have resolved a number of errors that would arise with some pieces of missing information for tasks or firms.



# 08/15/22

## 🚀 Features

#### Use APM as the source of truth for policies (#16093, #16088, #16116) -
Moving forward, we want customer set policies in APM to be reflected in Command Center consistently. Accordingly, we won't be automatically updating data in APM when we change policy information within Command Center.

We have also updated our internal database to be consistent with customer onboarding.

#### Send a request to APM to update policies when they get updated in Lisa (#16097) -
If we make a change to policy information in Lisa, we will now prompt APM with a request to accept the change as an update to APM. 

#### Update `LEGACY` action name for `OFFER_SHOW` (#16121) -
To enhance our data accuracy, we have updated an outstanding bot action still classified as `LEGACY`. Now, every time Lisa sends an `OfferShowing` or `CounterofferOfferShowing` message, those will be categorized as `OFFER_SHOW` in our database. 



# 07/25/22

## 🚀 Features

#### Don't overrule `REQUEST_SOONEST_SHOW` when time preference is not parsed (#16045) -
In cases when Lisa is not able to parse a time preference from an inbound message, she will more often send `REQUEST_SOONEST_SHOW` in order to get the prospect into the scheduling flow more quickly.

#### Record new Operator statuses (#16017) -
In a continued iteration of our data collection, we'll be recording new statuses including `ON_BREAK` for when a user clocks out while still on shift.

#### Send all support cases through SalesForce (#15996) -
We are retiring the use of handling support cases through support@dynasty.com. Now, all support cases will be created through SalesForce.



# 07/18/22

## 🚀 Features

#### Sync Agent availability (#16011) -
We have updated our conditions for syncing agent availability from APM. Now, even if Lisa is disabled on a property task, we will still sync agent availability as long as the task is currently onboarded.

#### Add new marketing endpoint (#16032) -
We have added a new endpoint at a customer's request.


## 🐛 Bug Fixes

#### Update APM for tasks with multiple corresponding properties (#16006) -
We have pushed a temporary fix in situations where we have issues updating APM because a task corresponds to multiple different properties. 



# 07/11/22

## 🚀 Features

#### Sync APM firm info during Lisa Onboarding Wizard (#15998) -
We have eliminated an additional step in updating firm info during the onboarding process. Now firm info for Lisa will be updated more quickly as firms are onboarded.


## 🐛 Bug Fixes

#### Fix tour type preference keyboard shortcuts (#15988) -
We encountered a bug where the keyboard shortcuts that change a prospect's tour type were not working as intended. Now, using `shift + v`, `shift + i`, and `shift + s` will change a prospect's tour type preference without issue.



# 07/04/22

## 🚀 Features

#### Record Operator Status as PROJECT when shift not scheduled (#15939) - 
Operator's who clock in through Command Center will have their status recorded as "PROJECT" if they do not have a scheduled shift.

#### Add more logging to ILS parser responses (#15958, #15964) -
We are now tracking the actions the ILS parser takes with a greater amount of detail.
 


# 06/20/22

## 🚀 Features

#### Add `showingTimePreference` to message info when message is reclassified to `ACCEPT_SHOW` (#15931) -
When Operators manually reclassify an inbound message as `ACCEPT_SHOW`, Lisa will attempt to parse the prospect's desired showing time for the purposes of sending automated counteroffers. 

#### Improve `ACCEPT_SHOW` automation and parse `showingTimePreference` (#15919) -
We have made a change to the automation surrounding `ACCEPT_SHOW` to ensure Lisa replies to show acceptances reliably. 

We are also taking additional steps to collect a prospect's desired showing time to improve our automated counteroffers.



# 06/13/22

## 🚀 Features

#### Automate `RESPOND_APPLICATION` and `REMIND_APPOINTMENT` (#15907) -
Lisa will now send automated responses to messages where Galaxy predicts `RESPOND_APPLICATION` and `REMIND_APPOINTMENT` with high confidence.

#### Track and log Command Center statuses (#15867, #15866) -
We now have greater visibility and tracking for the status of Command Center users. Data regarding CC statuses will be stored in its own database table going forward.



# 06/06/22

## 🚀 Features

#### Automate `REQUEST_VOICE_CALL` (#15895) -
We have added automation for `REQUEST_VOICE_CALL`. Lisa can now reply with that request when she has a sufficiently high confidence score.

#### Add Q_CANT_CALL tag and Galaxy prediction (#15896, #15890) -
We now have a message tag for inbound where a prospect asks to call Lisa at the number where they are texting her. 

Lisa will also reply to prospects with the `CANT CALL` message when she believes prospects are asking to call her directly.


## 🐛 Bug Fixes

#### Fix `REQUEST_VOICE_CALL` error for prospects with no phone number (#15900) -
We have resolved an error that occurred when Lisa predicted `REQUEST_VOICE_CALL` in cases where the prospect has no phone number on file. Now, in those situations, we will fall back to Operators.



# 05/30/22

## 🚀 Features 

#### Automate positive and negative feedback responses (#15877) - 
We now have automation in place when Lisa accurately predicts `POSITIVE_FEEDBACK_FOR_SITE` or `NEGATIVE_FEEDBACK_FOR_SITE`



# 05/23/22

## 🚀 Features 

#### Split positive and negative feedbacks for galaxy (#15842, #15844, #15847) -
The `Tour Feedback` QA has been updated to split [the positive and negative](https://appfolio.slack.com/archives/C02CMG7H96J/p1652737225093309) acknowledgement for feedback. This split will help with training the bot around how to acknowledge positive versus negative feedback.

#### Add `Tour types offered` (#15856) - 
We now have a [QA for `Tour types offered`](https://appfolio.slack.com/archives/C02CMG7H96J/p1654027928058029) as well as a message tag of the same name. The tag and QA will now pull the tour types that are offered at a given property to say something like:
> We are offering in-person and virtual tours at this property. Which would you prefer?

This should be helpful for answering prospect questions about the different touring options offered at a property.

Once a tour gets scheduled, you'll see that the old `Tour Instructions` QA is available to use again: this QA helps give prospects advice on how to either connect with an agent or join a virtual tour.

#### Phone tree admin job (#15838) -
A new admin job has been added to CC that allows support to view phone tree routings.


## 🐛 Bug Fixes

#### Fix operator inbox router loop (#15845, #15846) -
We recently had an issue where the inbox router service got into an infinite loop if all Operator inboxes were full. This has since been fixed so that threads will be routed to clocked in inboxes as expected.

#### Fix ‘tour types offered’ issue (#15862) - 
We have resolved a bug where part of the new ‘tour types offered’ feature was having some trouble getting parsed.

#### Remove phone from `Outside scheduling` message to prospect (#15852) -
The `Outside scheduling` message sent to prospects was not sending the correct phone number in several cases. In an effort to avoid this mistake and to save on bot performance, the reference to a phone number in that message has been removed.

#### Fix unassigned sitting threads (#15851, #15858) - 
We recently encountered some threads that would sit without getting assigned to an Operator inbox. This has since been fixed so that all threads make it to an Operator inbox.



# 05/16/22

## 🚀 Features

#### Tracking improvements for Galaxy (#15828, #15811) -
We have updated Galaxy to track new action names and message labels.


## 🐛 Bug Fixes

#### Don't ping Operators for @help (#15823) -
We have resolved a bug where an Operator might be pinged in Slack by the @help bot. Now, @help will correctly ping Senior Operators and Team Leads where appropriate.



# 05/09/22

## 🚀 Features

#### Other Line QA (#15807) -
Welcome to the [`Other Line` QA](https://appfolio.slack.com/archives/C02CMG7H96J/p1651764416530269)! Operators can use this QA to quickly inform a prospect that they'll reach out to them from our other number or email address. This is especially useful when a prospect is interested in multiple properties and asks Lisa questions on a thread that is not associated with the relevant property task.

The QA can be evoked by typing `other`, `line`, or `other line` into the QA bar and you can choose between `EMAIL` and `SMS`.

Example:

![Other Line QA in the QA bar](https://user-images.githubusercontent.com/62905507/166830497-f4185273-89b6-4d61-9c8d-605a5f9765aa.png)

![Other Line QA message](https://user-images.githubusercontent.com/62905507/166830521-5dcb7db3-fc97-469b-8d75-1478828af705.png)

#### Use unit type from ILS parser prediction (#15791) -
Our ILS parser will now update the prospect's desired unit type based on their initial inbound message.


## 🐛 Bug Fixes

#### Improve interaction with current resident guest cards for stand alone (#15798) -
We have fixed a bug that would interrupt a lease in progress if we edit guest cards for some stand alone customers. 



# 05/02/22

## 🚀 Features

#### ILS parser improvements (#15783, #15788, #15789) -
The ILS parser now stores and generates data more effectively.


## 🐛 Bug Fixes

#### Improve email address parsing (#15787) -
We have resolved a bug where a new line character could cause Lisa to believe an email field is empty when parsing a new lead. Now, Lisa will correctly identify a prospect's provided email even when it is preceded by a line break.



# 04/25/22

## 🚀 Features

#### Add unit type to ILS parser (#15747) -
The ILS parser is now able to identify and store a prospect's desired unit type.



# 04/18/22

## 🚀 Features

#### Add `UNPARSED` thread type and QA (#15743) -
We have added a new [thread type](https://appfolio.slack.com/archives/C02CMG7H96J/p1649798733157999) for threads that receive many unparsed leads. Now, these threads will be designated `UNPARSED` and Lisa will automatically attempt to parse inbound messages as leads. 

Operators can use the `Mark UNPARSED` QA to change a thread's type to `UNPARSED`.

#### `Prefer SMS/email` QA (#15752) -
We have added [a new QA](https://appfolio.slack.com/archives/C02CMG7H96J/p1650057159084289) so Operators can more easily set a Prospect's preferred form of contact. 

#### Polish feedback QA (#15748) -
We have refined the `feedback` QA to better reflect its use cases.

#### Always capitalize parsed names (#15758) -
Our name parser will now automatically capitalize the first letters of first and last names.

#### Filter out INQUIRY INFO as name (#15751) -
Our name parser will now deliberately filter out `INQUIRY INFO` as a possible name.



# 04/11/22

## 🚀 Features

#### Reduce clicks when ranking shifts (#15731) - 
We have reduced the number of clicks required to rank a shift in Command Center. 

Now Operators will be able to:
* Move mouse to a shift
* Click shift
* Enter rank
* Press `Enter`

#### Classify ranked shifts by color (#15733) -
We have added a color bar to the left of a shift to let Operators know if they have ranked the shift or not. A yellow bar means the shift is ranked, a green bar means the shift is still available but has not been ranked. 

#### Open search results in new tabs (#15732) -
You can now open search results in Command Center into a new tab.

#### Don't show search results when bar is empty (#15736) -
The Command Center search bars will no longer populate results when empty. You will only see results once you have begun typing. 

#### Add action names (#15706) -
We have added additional labels for message actions for the purposes of clean and accurate data.


## 🐛 Bug Fixes

#### Unarchive if Lisa's time slot predictions are ambiguous (#15725) -
When Lisa returns high confidence scores for two or more time slots, we will unarchive the thread and let an Operator make the determination rather than accepting a potentially incorrect showing for the prospect.

#### Don't `ACCEPT_SHOW` if unit or tour type preference changes (#15714) -
If a prospect changes their unit or tour type preference, Lisa will no longer let the prospect accept showings offered before that change.



# 04/04/22

## 🚀 Features

#### QA to reply to tour feedback (#15724) -
Operators can now [use a QA](https://appfolio.slack.com/archives/C02CMG7H96J/p1648848838882639) to reply to prospects' feedback after they have toured the property. The QA can also be used more generally in situations where an Operator may want to add a prospect's most recent message to the feedback report.

#### Select first option by default when reassigning shift (#15719) -
Previously, Operators would have to navigate a dropdown menu when reassigning shifts. This added extra work for the currently scheduled Operator, who is the only selectable option. Now, the reassign shift menu will select the first option on the list by default. 

#### Add shortcut to navigate into Draft page (#15718) -
We have added a keyboard shortcut, 'd', which will navigate to the draft page when pressed in Command Center.

#### Deprecate `ACCEPT_MULTIPLE_SHOW` (#15500) -
Because `ACCEPT_MULTIPLE_SHOW` is redundant with `ACCEPT_SHOW`, we have eliminated the former.

#### Add action names (#15705) -
We have added additional labels for message actions for the purposes of clean and accurate data.

#### Redact SSN from messages (#15712) -
Lisa will now automatically redact social security numbers in inbound messages.


## 🐛 Bug Fixes

#### Correctly classify messages with specific time preference (#15691) -
We have resolved an issue where prospects providing concrete time preferences for an appointment in their messages were being classified as `REQUEST SOONEST SHOW`.



# 03/28/22

## 🚀 Features

#### Set default Operator shift to four hours (#15685) -
Operator shift lengths are now [set to four hours](https://appfolio.slack.com/archives/C02CMG7H96J/p1647474522035359).

#### Message volume forecaster update (#15678) -
The message volume forecasting model will now take the number of units onboarded on Lisa along with volume over time when calculating upcoming volume. 

#### Update name parser (#15681, #15671) -
We have updated Lisa's ability to parse names with a new model to improve accuracy.

#### Improve Galaxy training model (#15672) -
We have adjusted the messages we use to train Galaxy, focusing on threads where every message was looked at by an Operator.

#### Galaxy parser update (#15692) -
We have improved the Galaxy lead parser by increasing the amount of messages used to train it. Now, Galaxy will look at the last eight months of inbound messages to calculate confidence scores about leads. We have also lowered the confidence score threshold for Galaxy to parse a lead.

#### Add action names (#15683, #15664) -
We have added additional labels for message actions for the purposes of clean and accurate data.


## 🐛 Bug Fixes

#### Fix draft page not loading (#15687) -
Operators outside of the Pacific time zone were unable to load the Operator draft page. This has since been fixed.



# 03/21/22

## 🚀 Features

#### Use images.dynasty.com for email images (#15628) -
We now use a new image host for images we receive from prospects that enhances security and privacy. 

#### Add Lisa active status to guest card for APM (#15636, #15655) -
We have pushed an update that makes Lisa guest card data more robust for our APM customers. Now, we will indicate on the guest card whether or not Lisa is actively engaged with the prospect.

#### Use ILS parser to parse name in conversation (#15654, #15668) -
To improve the frequency and accuracy of Lisa parsing prospect names, we will now use the ILS parser as well as SGD (Stochastic Gradient Descent) to parse names. We also log the thread URL for ILS name parsing to help with spot checking when necessary.

#### Encrypt sensitive information (#15599, #15651) -
We have encrypted more information for customer safety. Now, the AuthToken and ApiKey for our firms only appear as part of firmSecrets. 



# 03/14/22

## 🐛 Bug Fixes

#### Fix failing test due to DST (#15650) - 
We have resolved a calendar block test failure that occurred as a result of the change to daylight savings time. 



# 03/7/22

## 🚀 Features

#### Show total number of shifts slots and slots filled (#15598) -
It was previously cumbersome to know how many shift slots are scheduled and how many people are assigned to a shift on the CC Draft page. Now, that information is displayed without needing to hover over each individual shift time.

Before:
![Original CC Draft page](https://user-images.githubusercontent.com/21322866/155588386-02842ace-cd6b-4222-b4d4-2369b1f422fa.png)

After:
![Updated CC Draft page](https://user-images.githubusercontent.com/21322866/155588406-2f13e476-51fd-4dd8-b5ba-e561fd54c674.png)

#### Add action names (#15612, #15613) -
We have added additional labels for message actions for the purposes of clean and accurate data.


## 🐛 Bug Fixes

#### Fix to missing times in showings.csv (#15578) -
We had an issue where the times related to when an agent and prospect joined a virtual tour were not appearing in the showings.csv file that we share with clients. This has since been fixed.

#### Fix missing showings from Agent thread sidebar (#15602) -
We used to not display past showings on an Agent’s thread for a property they were unassigned from. This recently caused some confusion when troubleshooting an Agent’s schedule after they were removed from a property. Now, we look back to the past 5 days to display showings even if the agent was recently removed from a given property.



# 02/28/22

## 🚀 Features

#### `RESCHEDULE_SHOW` integration (#15572) -
Lisa will now offer to reschedule prospects more often after they cancel an appointment.

#### Add update contact name label (#15604) -
We have added a label to Galaxy that will allow us to more accurately predict whether or not a prospect is giving us their name.

#### Add action names (#15601, #15595, #15588) -
We have added additional labels for message actions for the purposes of clean and accurate data.


## 🐛 Bug Fixes

#### Fix `RESCHEDULE_SHOW` check (#15606) -
We have made changes to how we check whether or not to classify a message as `RESCHEDULE_SHOW` for reliability and accuracy.

#### Fix `AckConfirmShowAction` when using counteroffer (#15579) -
We now only attach a showing ID to the `ACK_CONFIRM_SHOW` message if a showing ID already exists in the database. This resolves an error that would appear when creating new showings using a counteroffer. 



# 02/21/22

## 🚀 Features

#### Add Google Login (#15514, #15576) -
You will now be able to [login to Command Center](https://appfolio.slack.com/archives/C02CMG7H96J/p1645233004289009) using your AppFolio email credentials. This login method enhances security and convenience for our team. 

Lisa also keeps track of each CC user's AppFolio email address.

#### Lisa settings page (#15538) -
We have added a [Lisa settings page](https://appfolio.slack.com/archives/C02CMG7H96J/p1645111751903699). Now, CC admins can adjust the weight of each agent thread to increase or decrease the number of prospect threads filtered into a Senior Operator's inbox.

#### Add action names (#15561, #15557, #15552, #15540) -
We have added additional labels for message actions for the purposes of clean and accurate data.


## 🐛 Bug Fixes

#### Clear `authToken` when users logout (#15553) -
We now completely clear a user's `authToken` whenever they logout of CC. 



# 02/14/22

## 🚀 Features

#### Sync with all properties in a merged  task (#15460) -
For some of our standalone clients, we have changed how we sync prospect guest cards in order to avoid duplicates. 

#### Record FeatureFlag changes (#15533) -
We now record when a feature flag has been enabled or disabled and display it on the firm task's Action Log. This provides an audit trail to help with later troubleshooting.

#### Add action names (#15531, #15521, #15520, #15517, #15516, #15515, #15504, #15502, #15497, #15495) -
We have added additional labels for message actions for the purposes of clean and accurate data.


## 🐛 Bug Fixes

#### Fix to confirm show (#15519) -
We have fixed a bug where confirmation messages were sent, but the prospect was not actually confirmed for their showing. 

#### Remove discrepancy in AGENT CSV (#15511) -
Previously, we included times an agent was marked as available when a showing was scheduled as an accepted showing. Now, that count will only include showings an agent attended or planned to attend.

#### Ignore self scheduled showings for archived tasks (#15505) -
We will no longer create new threads for archived tasks when running an APM sync.



# 02/07/22

## 🚀 Features

#### Remove context checking for setting unit type (#15482) -
Previously, we only updated a thread's preferred unit type when predicting a fixed set of inbound message classes. We have eliminated that context check and will update unit types based on the bot's predictive algorithm. 

#### Remove context checking for tour type (#15461) -
As above, we have removed the context check for changing the desired tour type based on the bot prediction. 

#### Ignore spoofed email (#15469) -
We have identified some spoofed email addresses and will now ignore them.

#### Showing negotiator timestamp update (#15474) -
We have adjusted the rate at which the showing negotiator includes the month within its timestamp. This will continue to help the negotiator better understand months when making predictions.

#### Showing negotiator date parsing (#15494) - 
Now that we predict the month directly, the showing negotiator can more accurately determine the day within a month with fewer checks.

#### Add action names (#15480, #15422, #15416) -
We have added additional labels for message actions for the purposes of clean and accurate data.


## 🐛 Bug Fixes

#### Return bad request if nps survey contact is invalid (#15471) -
Lisa was recently unable to create new support tickets around detractor NPS scores due to responses from spoofed emails. We now check if the contact information in the request is valid. If not, we return an error to avoid blocking new support tickets.

#### Fix Monarch Realty audit keys (#15493) -
We have made adjustments to the backend information for Monarch Realty threads. This ensures they can be properly synched with the firm's guest cards.



# 01/31/22

## 🚀 Features

#### Cross sell fallback (#15432) -
If for any reason our cross sell recommender model does not allow a thread to be cross sold, we will fall back to heuristic sorting.

#### Calendar block update (#15433) -
Lisa is now better at appropriately detecting blocked time on agents' calendars.

####  Improved message interaction within CC threads (#15398, #15417) -
The feature around deep-linking to old messages in CC threads has been fine-tuned to avoid inhibiting Operator efficiency. Some of those improvements include:
- Highlighting text within a message to copy/paste is possible again without needing to explicitly select the message first
- Clicking 'Add tag...' and interacting with the dropdown/type-forward modal does not de-select the highlighted message
- Selecting a message/deep-linking to a previous message now shows the previous 40 instead of just the previous 3 messages

#### Add action names (#15414, #15347, #15346) -
We have added additional labels for message actions for the purposes of clean and accurate data.


## 🐛 Bug Fixes

#### Remove duplicate validation error messages (#15427) -
We resolved an issue where an error message when bulk updating agent information would appear twice.

#### Code base clean up (#15424) -
We have eliminated code that is no longer used from our code base.



# 01/24/22

## 🚀 Features

#### Add Keyboard Shortcuts  (#15374) -
We have added tour type selection as a [keyboard shortcut](https://appfolio.slack.com/archives/C02CMG7H96J/p1642534049001700) in Command Center!

Operators have the option to use these shortcuts to change a prospect's desired tour type:

- **press "shift+v" to toggle virtual tour type selection**
- **press "shift+i" to toggle in-person tour type selection**
- **press "shift+s" to toggle self-guided tour type selection**

#### Add action names (#15341, #15340, #15333, #15332, #15330) -
We have updated our labeling for message actions for the purposes of clean and accurate data.

#### Showing negotiator update (#15383, #15395) -
Our showing negotiator can now better and more accurately handle months. This will help in situations where a prospect's preference for showing date does not specify a month.

#### Update summary text for calendar block (#15382) -
We have made a change to the `Block` QA for agents. The summary text in CC has been reverted to read something like “Lisa block day Sat Jan 29” so that Senior Operators know the block was placed correctly. The agent will still see a title, location, and description on their side of the calendar block.


## 🐛 Bug Fixes

#### Calendar block fix (#15388) -
We have resolved a bug where agent calendars were not being blocked correctly.



# 01/17/22

## 🚀 Features

#### Add Senior Operator shift (#15309) -
We've added a Senior Operator shift from 11am-4pm PT starting the week of 1/31/22. This additional shift will help cover the high volume of Agent/Boss messages in the early afternoon. 

#### Update copy for Agent Calendar for clarity (#15366) -
On the calendar blocks we add to agent calendars, we now add a title, location, and description to the calendar block.

#### Add month label to showing negotiator (#15331) -
We now track the month when generating offers through the showing negotiator. We previously inferred the month with some different logic, but that occasionally failed when the date was more than 1 month in the future.

#### Update Bayshore phone tree (#15343) -
We've updated the maintenance number for the firm, Bayshore.


## 🐛 Bug Fixes

#### Fix showing calendar error (#15354) -
We have resolved an error that resulted from creating a calendar event for a showing when a prospect is missing a first name. We can now generate a title for a showing using just the prospect's last name.

#### Do not uninvite prospect from past showing (#15301) -
We resolved an error in situations where multiple prospects are scheduled for a showing and one prospect notifies us that the agent is a no show. In that scenario, we would attempt to send `SEND_CAL_UNINVITE` to the prospects we haven't heard from. Because the showing had already begun, this caused the error.

Now, we will never send `SEND_CAL_UNINIVTE` in cases where a showing has passed its scheduled start time.

#### Highlighting text in a CC message will not select/re-render the thread (#15337) -
We've fixed an issue where highlighting text in an unselected message would cause the page to reload and the highlight removed.

#### Forward Work Order Notification emails from RentCafe (#15370) -
Lisa endpoints periodically receive work order notification emails from RentCafe and was previously unable to parse them. Now, we forward those emails to the property's on-site team.

#### Use ActionExecService to onboard new task agent (#15368) -
One of our onboarding methods for adding new agents to a task will now update both Command Center and APM.

#### Code base update (#15369) -
We have removed outdated code from the code base, including eliminating vestiges of our old ILS parser.



# 01/10/22

## 🚀 Features

#### Fine-tuning cross sell recommendations (#15302) -
Continuing our refinement of the cross sell recommender, we have pared down the test to only include  the two best performing models.

#### Allow `null` to be inside a word (#15292) -
We will now allow messages to be sent that contain `null` within a word. We will only return an error when `null` is sent as a separate word.


## 🐛 Bug Fixes

#### Date parser year error (#15295) -
Previously, our date parser would always assume a desired move-in date was within the current year if no year was specified. Now, when parsing a date from a message that doesn't contain a year, we assume the date is between `now - 1 month` and `now + 11 month`. 

This avoids situations where a prospect might ask for a `Jan 11` showing at the end of 2021, and Lisa assumes their desired move-in is `1/11/21` as opposed to `1/11/22`.

#### Show “fired agent” action in action log (#15279) -
Details of the `Fire Agent` QA now appear in CC’s action log.

#### Increase limit of messages Milo sends to ops from 10 to 15 (#15293) -
Milo can now return up to 15 possible matching notes.



# 01/03/22

## 🚀 Features

#### Support more foreign languages (#15258) - 
Lisa can now support [16 additional languages](https://appfolio.slack.com/archives/C02CMG7H96J/p1641229717193100)! Among the sixteen new additions are Bulgarian, Czech, Chinese, Danish, German, Greek, Spanish, Estonian, Finnish, French, Hungarian, Italian, Japanese, Lithuanian, Latvian, Dutch, Polish, Portuguese, Romani, Russian, Slovak, Slovenian, Swedish. We now support twenty-four languages total.

#### Improved unit type preference updating (#15282) -
Previously, the unit type preference for a thread would not always update when a prospect mentioned a new preference. Messages classified as counteroffer had this problem most often. Now, counteroffer messages can trigger a change in a thread's unit type preference.



# 12/27/21

## 🚀 Features

#### Allow deep linking to old messages (#15236) -
We previously only displayed the most recent fifty messages on a thread within CC. Now, any message is visible provided you have a direct link to it. Clicking a message link older than fifty messages will display it along with the following fifty messages.

#### Add sender domain and subject to ILS payload (#15234) -
When predicting the leasing attribution source of an email message, we will now take into account the email address and subject line.


## 🐛 Bug Fixes

#### Remove unused code (#15242, #15212) -
We've removed some unused code from the Lisa codebase. 



# 12/20/21

## 🚀 Features

#### Add `ILS_PROSPECT` label (#15204) -
We have added an `ILS_PROSPECT` label to Galaxy Brain in order to ensure the ILS parser is only attempting to parse messages that need to be attributed to an ILS.


## 🐛 Bug Fixes

#### Increase accept or counteroffer threshold (#15230) -
We have increased the negotiator's threshold of accepting showings to make sure we are not accepting showings prospects don't want.

#### Only use leasing source available on a task (#15228) -
Our ILS parser previously was able to attribute a lead to a source that didn't exist in the task's leasing attribution sources. Now, the ILS parser will only attribute a lead to a source if it exists for the task. 

#### Remove unused Twilio code (#15209) -
We have removed some outdated code from Lisa.



# 12/13/21

## 🚀 Features

#### Switch to weekend senior staffing on Christmas Day and New Year's Day (#15187) -
We have adjusted the staffing for the upcoming winter holidays. We have also introduced functionality that this adjustment will occur automatically for future holidays. 

#### Improved Twilio Message Service provisioning (#15149) -
We are currently in the process of switching our phone numbers for client marketing into "Messaging Services" to avoid fees and message filtering from telcos. This process involves a fair amount of manual labor, so we have introduced automation to make this process less cumbersome. 

#### Introduce email adapter (#14976) -
In an effort to maintain our codebase, we have adjusted some of our Gmail integrations. 

#### Add a new admin job (#15184) -
By introducing the `CREATE_LOCALHOST_FIRM` admin job, we have reduced the delay in communicating between the APM codebase and the Lisa codebase. This will significantly increase our developer velocity. 


## 🐛 Bug Fixes

#### Handle zero prospect for `Reject Showing` QA (#15193) -
We've fixed a bug that stopped us from using the `Reject Showing` QA in cases where all prospects on a showing were `NO_SHOWS`.

#### Add EMAIL messages to prospect feedback (#15192) -
We experienced a bug where we were only sending feedback reports for SMS messages. Now we generate feedback for Email and SMS. 

#### Fix Slack Alerts bug (#15151) -
We have resolved an issue where engineering alerts were sent to incorrect Slack channels.

#### Improved date formatter for message volume prediction (#15183) -
We now use year-of-era instead of week-based-year in our volume predictions. This fixes a bug where the prediction model would return an empty response because it misunderstood the date.

#### Always add the current task to tasksInterested when loading CrossSell (#15176) -
Previously, we had a bug where cross selling would work incorrectly on threads created more than 90 days ago. This has since been fixed.



# 12/06/21

## 🚀 Features

#### Fix time copy when cross selling to existing thread (#15111) -
Previously, we would specify the date we last spoke to a prospect when cross selling to an existing thread. In examples where we had spoken to the prospect the same day as the cross sell, this would cause some conversational awkwardness. 

Now, in instances where we cross sell a prospect and our last contact with them was the same day, we will just say `today`.

#### Add new leasing attribution sources (#15173, #15172) -
We have added two new leasing attribution sources for `YELLOW_PAGES_COM` and `SOCIALSERVE`. This ensures accurate data regarding our inbound.


## 🐛 Bug Fixes

#### Improved cross sell based on preferred unit type (#15137) -
We will no longer automatically cross sell to a prospect if we've not yet set their preferred unit type after inquiring about it. This will help avoid cases where a prospect is interested in a 1br, but Lisa tries to cross sell to a nearby property with only 3br units available. 

#### Resolve cross sell inconsistency (#15142) -
We have made some back end changes to ensure results are consistent among the various ways we cross sell.

#### Eliminate duplicate `ILS PROSPECT` messages on agent thread #15121 -
In instances of a self scheduled showing, we would sometimes send duplicate information about a prospect to agents. This has since been fixed.



# 11/29/21

## 🚀 Features

#### Custom floor plan description (#15124, #15134) -
In instances where a floor plan name or description is too long, we'll now use `{# bedrooms} bd {# bathrooms} ba {# sqft} sqft` as the description. This description will appear on both the task page as well as in the `floor plan` quick action. 

![Floor plan rent QA](https://user-images.githubusercontent.com/60522124/144457553-ce212523-80c8-4f29-8464-6cbc94d8205d.png)


## 🐛 Bug Fixes

#### Fix Lobby page date (#15109) -
We've made some adjustments to make sure the lobby always displays the correct date. This fix will ensure that dates within a new draft will not display before the full draft officially opens.

#### Showing negotiator refinement (#15136) -
The showing negotiator will now prioritize the most recent showing time request from a prospect.

#### Ignore voice calls from Lisa (#15130) -
If we receive a voice call from a phone number associated with Lisa, we will now ignore it.



# 11/22/21

## 🚀 Features

### Cross Sell Improvements

#### Ask for unit type before cross selling (#15072) -
When a prospect messages in about an unavailable unit, we'll first ask for their unit type preference before cross selling. 

Example:

![Cross selling change](https://user-images.githubusercontent.com/62905507/141842174-2d08b9ce-c265-41fa-89a3-10dda117a3fb.png)

#### Add year to some cross sell messages (#15039) -
When cross selling to an existing thread, we include the date of last contact. We will always specify the year of our last messages in cases when that last contact was in the previous calendar year.

### Additional Improvements

#### Use agent email address when sending offboard message (#15107) -
When we offboard a property, we will now only provide agent email addresses to prospects with a previously scheduled appointment. This avoids situations where we may inadvertently give out an agent's personal phone number.

#### Adjust Senior Operator Staffing for Fall Holidays (#15082) -
We now treat Thanksgiving and Black Friday as if they are Saturday and Sunday for the purposes of Senior Operator staffing. 


## 🐛 Bug Fixes

#### Improve “send Operator” messages (#15074) -
We had a bug where some Lisa messages intended for Operators did not send as expected. This has been fixed so that all messages, such as the Operator tardy message, are delivered successfully.

#### Do not load agent schedule if bot disabled (#15075) -
We no longer attempt to load an agent’s schedule in CC if they are only assigned to a property that has the bot disabled.



# 11/15/21

## 🚀 Features

#### Remove duplicate QAs from combined QAs (#15015) - 
Our combination QA feature will no longer combine two of the same QAs together. 

Previously, we would allow for two of the same QA to be combined, resulting in the repetition of text in the outbound message. Now, we will not serve combinations that include two of the same QA.

Before:

![Old QA Combo](https://user-images.githubusercontent.com/21322866/140543401-111514a7-9681-4ac2-97ee-9011167702d5.png)

After:

![New QA Combo](https://user-images.githubusercontent.com/21322866/140543409-4a3aec86-3af7-42f8-999a-37e84a77c6f7.png)

If for any reason an Operator accidentally includes two of the same QA in a combination, the text of that QA will only be sent once. 

#### Cross Sell Update (#14991, #15041) -
Our experimentation with various cross sell models is ongoing. We have made adjustments to the recommender that should provide better cross sell results.

#### Add `SNOOZE_THREAD` and `THANKS_FOR_TIME` to all threads (#15030) -
`SNOOZE_THREAD` and `THANKS_FOR_TIME` are now available on any prospect thread. 

#### More "bot only" threshold refinements (#15051) -
We have made further adjustments to the application of the `BOT_ONLY` label. 

#### Add “pre-join” UI for virtual tour room (#15040) 
We’ve enabled the “pre-join” UI for the prospect side of a virtual tour. This allows prospects to “wait in a lobby” and check their microphone/camera settings before jumping into the call.

#### Milo Slackbot (#14996, #15061, #15058) -
In order to maintain the quality of our notes in CC, we have introduced Milo to Slack. Milo will facilitate the matching of unattributed notes to specific messages. 

#### Guest Card Update for new APM API (#14895, #14954) -
We have updated how we save guest cards to accommodate changes to APM's new API. For context, API refers to the connection between computers or computer programs. These updates will allow us to maintain accurate guest card information.


## 🐛 Bug Fixes

#### @help fix for Senior Operators (#15052) -
We have resolved an issue where Senior Operators using @help within a thread resulted in a new Slack thread being created in the #lisa-operators channel. Moving forward, all @help pings should remain threaded as expected.

#### Send evening prospect confirm message (#15021) -
Previously, prospects were not receiving confirmation messages when an agent confirmed a showing after 6pm the night before. We will now make sure to send the confirmation message in that situation.

#### Fix `realtor` QA for tasks with no office phone (#15024) -
Previously, the `realtor/broker` QA would not work if a task was missing an office phone. Now, in situations where there is no office phone for a task, the QA will fall back to the firm's office phone.



# 11/08/21

## 🚀 Features

#### Cross sell message when prospect already has a thread (#14989) -
We have refined the way Lisa will handle cross sells where the prospect has an existing thread at the cross sold property.

Previously, trying to cross sell a prospect when they already had a thread resulted in a QA error. The bot was also able to initiate the cross sell, but would not follow up on the other existing thread. Now, the bot and the `cross sell` QA will acknowledge the prospect’s already existing thread and message them on it. 

#### Translate call request quote (#14925) - 
When we send prospect messages to an agent in a call request, we now translate that text into English when necessary.

#### Refine “bot only” threshold (#14982) -
As we continue to refine our automation, we've narrowed the conditions under which we apply the `BOT_ONLY` label. This will allow Operators to verify the action Lisa takes in situations where our confidence is not as high and continue to train the bot.


## 🐛 Bug Fixes

#### Improved 'send cancel show’ message (#15007) -
We have resolved a bug where a prospect's appointment would not be cancelled if an agent confirmed the showing within three hours of its start time, but then rejected it before the start time.

#### No availability check for inactive agents (#15008) - 
We will no longer toggle the showing confirmation status for agents without active tasks because inactive tasks have no showings by definition.

#### Showing reminder fix (#14997) -
We have resolved a bug where we sent showing confirmation messages to prospects who accepted the showing within five hours of its start time. 

#### Improved ‘acknowledge accept’ message (#14986) -
We've refined our response when acknowledging an accepted appointment for prospects at some off-site properties. This update will more accurately reflect when a prospect can expect to receive agent contact info and their virtual tour link (when applicable).

#### Skip thread activity validation in cross sell threads (#14987) -
Because cross sells only happen when we have been contacted by a prospect, we will no longer perform the `ThreadActivityValidation` check on threads created by cross selling. This eliminates a bug where Operators were not able to cross sell due to an "inactive thread."

#### Cross sell recommender refinements (#14979, #15001, #15005) -
We have made several adjustments to our new cross sell `recommender`, including adjusting the similarity threshold. The `recommender` will now provide a wider range of cross sell options.



# 11/01/21

## 🚀 Features

#### `Offer` QA gives 'Can't create showing' when enough approved applications (#14939) -
The `Offer` QA will now read, `I'll let you know as soon as I have an appointment available for %s.` in situations where the number of approved applications is greater than showable units. This change helps Operators continue the flow of conversation for tasks with many applications.

#### Translate email subject (#14918) -
Our ML translations will now also translate email subject lines.

#### Send application score to APM (#14762) -
We now send the results of our lead scoring algorithm to APM.

#### Add new cancellation reason (#14922) -
For cleaner data collection, we now indicate in our database when we cancel on a prospect because a property is hidden. 


## 🐛 Bug Fixes

#### Fix missing QAs (#14968) -
We have resolved a bug where several QAs, including `can't call`, `outside scheduling`, and `realtor` were unavailable for Operator use in prospect threads.

#### Do not unarchive hidden APM property thread (#14938) -
Previously, messages on hidden tasks might be unarchived if Lisa is not able to figure out how to respond. Now, no threads on hidden tasks will be unarchived into Operator inboxes. 

#### Lead scoring bug fixes (#14941, #14945, #14946) -
We now only calculate a lead score in cases where a prospect has sent more than six inbound messages. We have also streamlined our testing framework for assessing lead score. 
 
#### Change `TENTATIVE` color from lemon to mint (#14924) -
The color of an agent’s`TENTATIVE` status indicator on a showing was initially the same color as `ACCEPTED`. We have changed the `TENTATIVE` color to mint so that it matches the `CONFIRMED` status. This color change is intended to reduce confusion because `TENTATIVE` is functionally the same as `CONFIRMED` in CC. 



# 10/25/21

## 🚀 Features

### Cross Sell Improvements

#### Cross sell conversion experiment (#14888) -
We will be experimenting with different methods for ranking cross sell options. By introducing an additional machine learning element to how we order our cross sells, we hope to increase cross sell conversion.

#### Integrate task recommendation service (#14829) -
We have introduced a ML component to cross selling through the task recommendation service or cross sell recommender as an alternative to our heuristics based sorting method.

#### Use interaction history for cross sell recommendation (#14872, #14890) -
Our cross sell ML recommender will take into account all available interactions, looking at all of a given prospect's threads, in ranking cross sell options.

### Additional Improvements

#### Do not offer showings if we have enough approved applications (#14811) -
We will no longer be able to offer showings for APM properties that have as many `APPROVED` applications as available units. 

Here's an example of the message we send to cancel appointments:

![Cancelling appointments if too many approved applications](https://user-images.githubusercontent.com/62905507/137184308-2a079449-a951-4ef2-a8a6-e06e10e2b87b.png)

And here's an example of Operators unable to schedule new appointments:

![Can't offer showing because of approved applications](https://user-images.githubusercontent.com/59895301/139271824-9e8005a2-25d1-4b06-ba10-53876aae0e93.png)

#### Hidden task threads skip the inbox (#14901) -
New messages for a hidden task will no longer enter Operator inboxes.

#### Add `ConversationContext` for EMAIL_REACTIVATION (#14824) -
We've added `ConversationContext` to email threads where we have reactivated a prospect. This will allow Operators to use the `still interested` QA in these threads.

#### Remove create task button (#14865) -
Due to disuse, we have removed the "create task" button.

#### Allow bulk update to clear fields (#14862) -
Command Center admins can now empty fields of text by leaving a field blank when bulk updating.


## 🐛 Bug Fixes

#### Improve `SEND_DELAY_SHOW` usage (#14868) -
We have resolved an issue where `SEND_DELAY_SHOW` might be used on a prospect thread where the prospect already has an `ACCEPTED` appointment.

#### Fix to Showings section of prospect threads (#14861) -
We have resolved an issue where upcoming appointments for SFRs would not appear on the right side on prospect threads where the prospect threads have a desired unit type selected.

#### Make sure contact isn't null (#14859) -
We will no longer create a contact in cases where we receive inbound from invalid phone numbers.



# 10/18/21

## 🚀 Features

### New QAs

#### Reminder QA (#14806) -
We now have a [`Reminder` QA](https://appfolio.slack.com/archives/C02CMG7H96J/p1634153327043700) that can help prospects when they lose track of where they might have booked a showing. If a prospect already has an upcoming accepted or confirmed showing and asks to schedule a showing, we want to have a QA that says `You already have an appointment at ~`.

This QA will work in one of two ways.

*If an upcoming showing does not exist*:

![Reminder QA with no upcoming showing](https://user-images.githubusercontent.com/21322866/137167837-1d5515dc-2816-45a1-9550-16fdeb54d9f1.gif)

*If an upcoming showing exists*:

![Reminder QA with an upcoming showing](https://user-images.githubusercontent.com/21322866/137167852-75e4ccad-d1aa-4b86-a6d0-74091c27c5cc.gif)

This QA should alleviate manual work for Operators and provide a better experience to prospects. 

#### Realtor/Broker Policy + QA (#14798) -
We've added a space for `Realtor/Broker` to our policy fields at both the task and firm level. We also offer a [`Realtor/Broker` QA](https://appfolio.slack.com/archives/C02CMG7H96J/p1634081022032100) which will serve that information from the policy fields to inquiring prospects or real estate professionals.

If the field is empty, the QA will deliver this response:

![Realtor/Broker Generic Response](https://user-images.githubusercontent.com/62905507/136868036-b1338378-245b-4abf-9211-7a401038a2d7.png)

### QA Improvements

#### Copy Edit Resident QA (#14795)-
We have added a `.` to the `Resident` QA.

Before:

![Unedited Resident QA](https://user-images.githubusercontent.com/7215139/136848020-f1ab590a-9bdb-48a9-b46b-938bf0b386a6.png)

After:

![Edited Resident QA](https://user-images.githubusercontent.com/7215139/136847907-e5249203-34b5-49d0-ac03-9d99ba6741dc.png)

### Additional Improvements

#### Note Message Evaluator (#14802) -
In order to update older notes saved in CC, we will be evaluating them for similarity with inbound messages from agents and bosses. As we further refine this feature, our Ops leaders will be able to correctly attribute old notes to the agent or boss who has sent in a similar message. This will allow for better data integrity and more visibility for clients.

#### Add `Migrate tasks to new PM` admin job (#14779) -
CC admins can now migrate tasks from one kind of PM software to another. 

#### Adjusting botOnlyThreshold (#14791) -
We use a measure of the `BOT_ONLY` value of recent inbound messages to decide if they will skip the inbox. If our confidence is high enough that the message does not need any Operator attention, the message skips the inbox. We want to make sure our `BOT_ONLY` threshold is correct, so we will be experimenting with the impact of assigning the `BOT_ONLY` value in different ways. This will help us determine the optimal criteria for filtering messages from the inbox.


## 🐛 Bug Fixes

#### Blocked contacts skip inbox (#14813) -
Inbound on threads where the contact has been blocked will now skip the inbox.

#### Improved agent virtual tour link setting (#14827) -
We have fixed an issue where agents were not properly assigned virtual tour links. Now, once an agent's confirmation status is set to `OFFERED` we will set a virtual tour link for them.

#### Get the name of the only attending prospect in Reject QA (#14822) -
Previously, we experienced a bug where prospects who are not marked as `ACCEPTED/CONFIRMED` would be referenced in the `REJECT` QA. Now, when we use the `REJECT` QA on an agent’s behalf, we'll only reference the prospects marked as `ACCEPTED/CONFIRMED`. 

#### Improved email ILS parser (#14787) -
We have resolved a bug where our ILS parser might accidentally identify the task email as the prospect's email. 

#### Nullify parsed contact name if too long (#14835) -
Previously, our ILS parser experienced issues if prospects wrote more than their name in the name field of an ILS. Now, we'll record prospect names as `null` if there are too many characters in the name field.



# 10/11/21

## 🚀 Features

### New QA

#### Implement `any other questions` QA (#14770) -
Operators can now [deploy a QA](https://appfolio.slack.com/archives/C02CMG7H96J/p1633639547024900) that asks a prospect if there is anything else we can help them with.

Example:

![Any Other Questions QA](https://user-images.githubusercontent.com/7515702/136446126-d3d236c4-ed28-4196-b042-3230348b701c.gif)

### QA Improvements

#### `Unparsed` QA without arguments (#14733) -
The [`unparsed` QA now works](https://appfolio.slack.com/archives/C02CMG7H96J/p1633450864020800) without including an email address or phone number. In those instances, it will trigger our ML ILS parser.

Example:

![Unparsed lead with no address](https://user-images.githubusercontent.com/21322866/135888734-eb8e8f0d-fa9d-49e9-bebc-64cc222c7640.png)

### Additional Improvements

#### Create Property Management System in Firm Admin page (#14723) -
The Property Management System a firm uses is now stored in a separate part of the Firm Admin page. This section of the page also includes the credentials for the PM System so Lisa can write guest cards.

Example:

![PM System Section on Firm Admin Page](https://user-images.githubusercontent.com/52615870/135562255-b3ba2f05-bad2-4e6e-995c-2f203a9e1ea6.png)

#### Introduce encryptedSecrets and remove FirmSecrets (#14759, #14772) -
We are now saving sensitive information about a firm, such as email log-in info, to an encrypted field in our database. If Lisa needs to access that information, she now reads from the field `firm.encryptedSecrets`.

We have also removed the `firm.info.secrets` field from our database. This field has been made redundant by our new method of saving encrypted information about a firm.


## 🐛 Bug Fixes

#### Lisa marks prospects as `DID_SHOW` (#14780) -
Previously, we had an error where prospects were marked as `DID_SHOW` after being updated to `NO_SHOW` per an agent’s request. `DID_SHOW` status should be reserved for prospects first marked as a no-show, but ultimately arrived for their scheduled showing. This designation will now function as intended. 

#### Allow `List units` QA to return multiple results (#14750) -
The `list units` QA can now return results for multiple properties. This fixes an issue that made the QA unusable if a firm had multiple properties with the same prefix.

Example:

![Fixed list units QA](https://user-images.githubusercontent.com/21322866/136048215-5d4da925-8d16-4915-89f7-731bf8cbbca6.png)

#### Fix incorrect message IDs in thread (#14740) -
We have fixed a problem with several message IDs not being updated when we use the `unparsed` QA. Now all threads will have the correct `lastMessageId`, `lastInboundMessageId`, and `firstMessageId`.



# 10/04/21

## 🚀 Features

### QA Improvements

#### Copy edit for `Unit unavailable` QA (#14730) -
Because the original copy for the `Unit unavailable` QA sometimes led to unclear, single-word responses from prospects, Operators found it challenging to cross sell after using the QA. This copy change will more clearly prompt prospects and help ease the process of cross selling for Operators.

Before:

> Sorry, this {property / unit} is currently leased. Would you be interested in hearing about our other available {properties / units}? {link}

After:

> Sorry, this {property / unit} is currently leased. You’re welcome to take a look at our listings! Do any of our other {properties / units} look like a good fit for you? {link}

#### Add unparsed lead pattern (#14684) -
We have expanded the ways the `unparsed` QA can be invoked to better match Operator intuition and usage. Now, "unparsed..." and "unparsed lead..." will invoke the `unparsed` QA.

Example:

![Using "Unparsed Lead" to evoke the unparsed QA](https://user-images.githubusercontent.com/21322866/135162948-1c667718-835f-43dd-8f6f-fd6e7b18a143.png)

#### Adds a bot command to mark the original thread to OTHER (#14676) -
The `unparsed` QA will now automatically mark the thread in which it is used as OTHER.

#### Hide parsed message (#14707) -
Using the `unparsed` QA previously caused the initial unparsed message to be deleted after it is parsed. This resulted in invalid message IDs, causing problems in our database. We now hide the initial unparsed message rather than delete it to avoid problems in the database. 

### Additional Improvements

#### Slack: Migrate to AppFolio workspace (#14511) -
We are now happy members of the larger AppFolio workspace! Behind the scenes, our Engineers have worked hard to make sure all the functionality of Lisa and Slack are working as intended.

#### Always skip inbox if Dynasty or Dynasty Support (#14678) - 
Messages from Dynasty Support or tasks associated with Dynasty will no longer hit Operator inboxes.

#### Remove Daily.co announce from Morning Report (#14695) -
We have removed the announcement regarding our new virtual tour provider from the Morning Report.

#### Improved verbiage and styling for OAuth instructions (#14721) -
The “Email and Calendar System” section on each firm’s admin configuration page has been improved. The tabs within this section have been re-ordered, have tighter wording on instructions for each system, and have improved visual UI.


## 🐛 Bug Fixes

#### Fix availability discrepancy (#14670) -
We recently experienced some discrepancies in availability data between CC and PM softwares. This was because of some bad square footage data leading to failed PmSyncs. The failed syncs have been corrected so that Lisa/CC have accurate unit availability information.

#### Move message email associated with message using `unparsed` QA (#14662) -
Previously, the `unparsed` QA only moved the message itself, not the email associated with the message for unparsed leads. This has since been fixed.

#### Make sure calendar time zones are accurate (#14712) - 
We previously saw the calendar view on an Agent thread might display an old timezone when switching to a different thread. This has since been fixed so that the calendar view shows the correct date and time zone.

#### Removes spaces in a phone number (#14702) -
The QA bar now recognizes phone numbers in `(xxx) xxx-xxxx` format. 



# 9/27/21

## 🚀 Features

### New QAs

#### Unparsed QA for Leasing Tasks (#14573) -
Because unparsed leads make up a significant proportion of our bugs, we’ve added a new QA! Operators can use the [`unparsed` QA](https://dynastycom.slack.com/archives/CB757SQ4S/p1632172200042700) to help Lisa learn how to parse previously unparsed leads. We designed the QA initially to work in `LEASING_PROPERTY` threads and it can be used in `PROSPECT` and `OTHER` thread types.

Example:

![Unparsed Lead QA in LEASING_PROPERTY Thread](https://user-images.githubusercontent.com/21322866/133679960-2c3da9d1-dfc0-4e53-8d62-80347a51feee.png)

The QA will:
- Create a contact with given phone and/or email if the contact doesn't exist
- Create a thread between the task and contact if the thread doesn't exist
- Copy the last inbound message to the new thread with the contact information
- Delete the last inbound message from current thread 

#### Unparsed QA for Firm Tasks (#14613) - 
We've also taken steps to make the `unparsed` QA available in `LEASING_FIRM` threads. Because we sometimes receive unparsed leads in these threads, we wanted to make a version of the QA that works in these threads and allows Operators to identify the property being inquired about. This version of the QA requires that Operators include the phone or email of a prospect along with the property name. 

Example:

![Unparsed Lead QA in LEASING_FIRM Thread](https://user-images.githubusercontent.com/21322866/134195300-724015fd-b99c-4c56-8702-460336e5aec0.png)


#### Add `Not A Bot` Agent QA (#14601) -
We now have a QA to deploy when agents indicate to prospects that Lisa is a bot.

Example:

![Agent QA - Not a bot](https://user-images.githubusercontent.com/60522124/135317070-342e5773-d9c6-43b8-ad7d-4d51f290d29a.gif)

### Additional Improvements

#### Add senior shift for 6am PT (#14631) -
We have added a new Senior Operator shift at 6am PT. The overlapping shift time will help manage the large amount of `AGENT`/`BOSS` volume beginning at 6am PT, which previously fell to the lone Senior Operator on the 4am PT shift to handle.

#### Use ILS ML parser (#14557) -
We will now use our ML parser to support our regex parser for inbound from ILS. This should continue to reduce the number of unparsed leads.

#### Edit copy for cancelling too many times agent message (#14600) -
We have new options for copy when messaging agents about a prospect who has been cancelled on multiple times. The new copy more directly requests that the agent connect with the prospect for further assistance.

#### Add leasing attribution source `RADIO` (#14628) -
We now have a leasing attribution source for `RADIO`, accounting for inbound from clients' radio advertisements.

#### Create `EMAIL AND CALENDAR SYSTEM` section in firm admin page (#14592) -
We have created a tabbed system in the firm admin page which houses all of the credentials for a firm in one place. This includes email and calendar authentication information.

#### Firm Secrets Updates (#14569, #14566, #14550, #14515) -
We've made a wide array of updates to how we store firm secrets, including taking steps to eliminate any sensitive information stored in plain text and deleting outdated credentials.


## 🐛 Bug Fixes

#### Handle zero sqft (#14636) -
We no longer send messages to prospects indicating that a property's square footage is zero. Instead, if a floor plan has zero square feet, we treat it as if the information is missing.

#### Check contact name not null (#14617) -
We have resolved a bug where we did not check if a contact name is `NULL` before using it.

#### Parse Arden (#14616) -
We can now parse leads from The Arden Apartments.

#### Fix prospects csv message created time out of range (#14630) -
We have resolved a bug where our CSV reports of prospects who have contacted Lisa within the last 28 days might also include prospects who do not meet that criteria.



# 9/20/21

## 🚀 Features

#### Edit copy in `note` QA to be more generic (#14567) -
To avoid any awkwardness or confusion, we have revised the copy of the `note` QA to make sense in response to all the ways an agent might give Lisa information.

Before:
`note` QA begins with ...

```
OK, sure thing, I can do that. I'll add a note from you in my memory for …
```

After:
`note` QA begins with ...

```
Thanks for letting me know! I'll add a note from you in my memory for …
```

#### Add application status to GalaxyItem (#14542) -
We now predict if a prospect has applied based on our conversation with us. If they indicate they have submitted an application, their status is updated to `APPLIED`.

#### Add `likelyToApplyProbability` in `message.info` (#14564) -
As we explore the viability of introducing new features for our clients, we will now be storing data regarding a prospect's likelihood to apply in the `message.info`. This probability will be calculated based on the conversation with the prospect.
 
#### Remove Empty Email Check (#14544) -
Previously, we had a check in the backend to see if an email was empty or not. In theory, this would ensure we did not take any action on an email with no contents. However, because we never receive empty email, we have removed this check.


## 🐛 Bug Fixes

#### Prefer last accepted showing when disqualifying (#14535) -
Because of prospect confusion, Lisa will no longer send `SHOWING_HOURS` in the same message as `ACK_ACCEPT_SHOW`. We will also make sure to both update and cancel the most recently accepted showing, rather than the most recently offered, when a prospect is disqualified. 

#### Change evening report logic to treat showing feedback grouped by contact (#14570) -
In the evening report, feedback from prospects is grouped by both the showing and the prospect.



# 9/13/21

## 🚀 Features

#### Add email sync flag (#14528) -
Within CC, we now have the ability to turn on/off email sync at the firm level. This feature will help us better manage and troubleshoot email sync bugs that may only be impacting single firms.

#### Improved `Reach out` QA (#14501) -
Previously, the `reach out` QA could only see tasks assigned to the current agent/boss. Now, `reach out` will look at all firm tasks, even if the agent/boss isn't assigned. 


## 🐛 Bug Fixes

#### Fix: unparsed leads from 30lines.com (#14497) - 
We can now correctly parse leads from 30lines.com. 

#### Update ML ILS parser (#14522) -
We deployed a new version of the ILS parser that is trained on more data, has improved pre- and post-processing, and returns additional fields. This will further help make the ILS parser more robust!



# 9/06/21

## 🚀 Features

### QA Improvements

#### Fix `Soonest` QA copy (#14458) -
Added "That is our next available appointment" to the end of the `soonest` QA to enhance clarity for prospects.

#### Make `cross sell search` filters more clear (#14385) -
If a `cross sell search` returns no showable floor plans, the QA dropdown will now indicate by what criteria available floor plans are being excluded.

For instance, if there is not a floor plan within 45 miles, the `cross sell search` QA will say so:

![Cross sell search returns no showable floor plans](https://user-images.githubusercontent.com/212360/130843333-d4fbbb02-97da-44b7-96d1-f5d795f89ef1.png)

### Additional Improvements

#### Update firm's secrets when updating the firm (#14476) -
If we update firm information, we now re-encrypt the firm secrets with every update.

 
## 🐛 Bug Fixes

#### Allow adding note to any task on the firm (#14436) -
Senior Operators and other CC Admins can now add notes to any property using the `Notes` QA. The QA is no longer limited to leasing tasks assigned to the agent/boss of the current thread.

#### Fix unparsed leads (#14473, #14454, #14453) -
We have improved lead parsing for homeiscitysend, Rivergate Apartments, and rentcafe.

#### Prevent multiple joins in virtual tours (#14478) -
Agents recently reported “seeing themselves” and some “echoing audio” during virtual tours. This happened because the “join” action was registering twice for the same person, causing a single person to have multiple videos and audio.

The bug has since been fixed!



# 8/30/21

## 🚀 Features

#### During draft mode, allow an Operator to drop a shift even if it's not full (#14386) -
In an effort to make sure our shifts are fully staffed and messages are being equitably distributed among Operators on shift, we made some changes to when [Operators can drop shifts](https://dynastycom.slack.com/archives/CB757SQ4S/p1629321951057800) they have drafted.

However, we have adjusted those changes during Draft Mode (Thursday to Sunday Pacific). Operators can once again drop shifts with open slots for the coming week during Draft Mode.

Example:

![Operator dropping shift while in draft mode](https://user-images.githubusercontent.com/7515702/130838656-c7b04646-dcb6-4715-b6ef-2d66877826d3.gif)

#### Add shortcut to toggle unit types (#14390) -
Operators can now change a thread's unit type using [keyboard shortcuts in CC](https://dynastycom.slack.com/archives/CB757SQ4S/p1629929407077100). You can see the shortcuts below:

![Unit type keyboard shortcuts](https://user-images.githubusercontent.com/7515702/130851502-10267211-2110-4585-a7d0-39c709affe08.png)

#### Remove context filter for `additional occupants` QA (#14357) -
Previously, if Lisa has not yet asked the `ADDITIONAL_OCCUPANTS` question yet, the `additional occupants` QA would be unavailable for Operators. We have since removed the context filter that prevents the QA from being used.

Now, the `add’l occupants` QA will always be available. The QA can be triggered by typing:
- “add {#} occupants”
- “add {#} o”
- “a {#} o”
![Add'l Occupants QA"](https://user-images.githubusercontent.com/60522124/131521691-ffb21465-ef00-4c60-a679-9d81a0b64306.png)

#### Better UI for virtual tour widget (#14362) -
We have made changes to the UI when agents visit a virtual tour link more than an hour before it begins. Previously, the page we showed did not clearly tell agents what went wrong.

Before:

![Old Virtual Tour Too Early](https://user-images.githubusercontent.com/21322866/130999352-c896d6b0-eb86-4fd1-b565-757b5920e16f.png)

Now, we show more information about the upcoming virtual showing including the scheduled time and prospects attending.

After:

![New Virtual Tour Prospects Attending](https://user-images.githubusercontent.com/21322866/131014708-7a8b28f8-b119-46a1-8bd1-8cc83e0df1f0.png)

![New Virtual Tour No Prospects Attending](https://user-images.githubusercontent.com/21322866/131015251-c9518efa-a9a3-45a4-aee1-d5cb48e51753.png)

#### Copy edit to `SEND_PROSPECT_OUTSIDE_SCHEDULING` (#14361) -
We have made a change to the text of `SEND_PROSPECT_OUTSIDE_SCHEDULING`.

Before:

_Our agent {name} just let me know that you guys connected already..._

After:

_Our agent {name} just let me know that you connected already..._

#### Toggle view Firm Secrets in Admin (#14310) -
CC Admins will now be able to view obfuscated firm information in CC for the purposes of Ops debugging. After a user is authenticated, their access to a `FirmSecret` is logged.


## 🐛 Bug Fixes

#### Improved understanding of rejected appointment  (#14347) -
If a prospect tries to accept a previously rejected showing and the property has become leased in the interim, we send `cancelShowingIfLeased` to let the prospect know the property is no longer available.

This message would previously check against a `mightAttend` status, but not a `Rejected` status, occasionally leading to a backend error. Now, `cancelShowingIfLeased` checks both statuses to avoid errors.

#### Fix task `JOBS` section in CC (#14333) -
The `JOBS` section on task pages in CC will now always show sync results, even when multiple vhosts are present.



# 8/23/21

## 🚀 Features

### Counteroffer and Showing Parser Improvements

#### Add length of month showing negotiator (#14231) -
We have added the length of the month to the showing negotiator model to eliminate confusion regarding whether "tomorrow" refers to the 31st or 1st when appointments are booked at the end of a month.

#### Slightly lower confidence threshold (#14330) -
We have slightly adjusted the showing negotiator confidence threshold such that more `HUMAN_FALLBACK` classifications will be shifted to `COUNTEROFFER`.

### Cross Sell Improvements

#### Cross sell filtering refinements (#14319, #14344) -
We have removed a preliminary backend filter when displaying results of the `cross sell` QA. The cross sell QA will still return results in order from nearest to furthest.

### Additional Improvements

#### Single soonest showing QA across multiple unit types (#14334) -
We have updated the `Soonest Showing` QA such that it will offer a single soonest showing slot available across all of a prospect's desired unit types. We have also eliminated the `Two Soonest Showings` QA due to low usage.

#### Make notes QA be the only way to create notes (#14215) -
New notes can now only be added through the `notes` QA. This QA is only accessible to Senior Operators and other Command Center admins. All notes will now be directly attributed to an Agent or Boss.

#### Remove station tables (#14282) -
Following our elimination of code regarding Operator stations, we have also deleted station information from our database.


## 🐛 Bug Fixes

#### Add missing line break in email report (#14343) -
We've added a missing line break in the evening report.

Before:

![No line break in evening report](https://user-images.githubusercontent.com/21322866/129974456-102a53ef-fc49-42fc-8ead-5803882e50ac.png)

After:

![Line break added in evening report](https://user-images.githubusercontent.com/21322866/129974402-f9d13e02-95ba-46e8-bdb5-259ca20f69af.png)

#### Non-ASCII characters are converted to ASCII equivalents for translated message (#14341) -
For outbound messages translated into languages other than English, we no longer convert non-ASCII characters to ASCII characters. This change helps us accommodate languages that use non-ASCII characters such as `Здравствуйте`.

#### Clear QA from text box when changing unit or tour type (#14294) -
We now clear the text box of any QAs when the unit or tour type is changed. This will eliminate outbound QAs that are no longer relevant to a prospect's desired unit or tour type.

#### Cross sell bathroom error fix (#14308) -
We will now ignore undefined bathroom counts when cross selling.



# 08/16/21

## 🚀 Features

### Cross Sell Improvements

#### Visible unit types when cross selling (#14259, #14304) -
In continuing to [refine the `cross sell` QA](https://dynastycom.slack.com/archives/CB757SQ4S/p1628788247039000), we have made available unit types for a property visible when cross selling. We have also adjusted how we return cross sell results. They are no longer dependent on the prospect's desired unit type.

The cross sell results will show all available unit types at a cross sellable property. If a unit is not showable at the other property, we receive a helpful error to send to the prospect.

Example:

![Cross sell Oakwood](https://user-images.githubusercontent.com/60522124/130138034-89cf3d52-5f29-4b71-88a0-f39aa2321b29.gif)

![Cross sell 1305 Parker St](https://user-images.githubusercontent.com/60522124/130138089-fd4c6fe5-25ad-4462-b899-d6631488511e.gif)


### Additional Improvements

#### Polish `note` QA display and parsing of last inbound msg (#14296) -
We have applied additional refinements to the UI of the `note` QA as well as made it more functional.

The QA will now remove quoted messages, greetings, and goodbyes when generating a new note. It will also appear differently when evoked to prompt the user to choose whether the note is for a firm, task, or contact.


## 🐛 Bug Fixes

#### Cross sell search distance (#14298) -
Previously, certain firms would return too many results when using `cross sell` QAs which would cause CC to freeze.

To alleviate this, we have reduced the radius of properties returned by the `cross sell` QA. We will also only return the `cross sell search` QA when an Operator types 'search' or 'css' when cross selling.

#### Remove station code completely (#14214) -
We have removed unused code for the old station system from the code base in order to eliminate errors in our data collection.

#### Do not show thread count for non operators (#14254) -
There will no longer be a thread count in the Lobby for non-Operator users when logged into CC.



# 08/09/21

## 🚀 Features

### New QAs

#### `Note` QA (#14178) -
Senior Operators and CC admins can now add notes using the [`note` QA](https://dynastycom.slack.com/archives/CB757SQ4S/p1628708157032300).

This QA can be used with the firm name, name or address of a property task, or a task agent/boss name. The QA will send a message to the agent in addition to adding a note to the database.

Example:

![Evoking Note QA](https://user-images.githubusercontent.com/52615870/126857854-0fbfc1ae-fedc-4728-9473-a1d103d9f510.gif)

![Added note](https://user-images.githubusercontent.com/62905507/126856807-7af698de-2a5c-438b-86c9-795d01641513.png)

![Note QA Detail](https://user-images.githubusercontent.com/52615870/128225756-64409c57-dbe2-4e26-ad5e-f953a2d7ec0a.png)

#### 'Move in {+numDays}' QA (#14229) -
We now have a QA that helps Operators add an estimated move-in date when a prospect says something like, "We're planning on moving in 2 weeks."

Using `move {days}` or `move in {days}`, we can set the move-in date to X number of days from today.

### Additional Improvements

#### Update Notes UI (#14219) -
Notes with attributions to a specific Agent or Boss now appear differently in Command Center. The notes now include the name of the agent/boss who noted it and a link to the message from agent/boss that prompted us to add the note.

Before:

![Old Note UI](https://user-images.githubusercontent.com/62905507/127609426-4f394ed2-e35b-425e-a37a-8bccfc32e522.png)

After:

![New Note UI](https://user-images.githubusercontent.com/62905507/127609174-467237d7-c450-4c60-9d51-fc9081f163c3.png)

#### Validation for 'null' in message body (#14242) -
We now have validations in place to avoid sending a message to prospects that includes the word "null" if there’s missing information.


## 🐛 Bug Fixes

#### Do not offer appointments to `AGENT` threads (#14187) -
Previously, if an `AGENT` messaged a property to ask for an appointment, we could offer them one. However, it would break our bot logic when trying to find another `AGENT` to offer the appointment to.

Now, if an `AGENT` messages a property where they also work, we'll send them a message that acknowledges Lisa is working but won't continue to reply to them as if they are a `PROSPECT`.

Example:

![Offer Show To Agent](https://user-images.githubusercontent.com/10438328/127063808-4b4e9846-56bc-488c-ad70-81182827a7d5.png)

#### Do not translate spam messages (#14226) -
We'll no longer perform translations on spam messages and threads marked `OTHER`.

#### Avoid using “null” instead of agent name (#14233) -
We had a bug where Lisa might use the word “null” in place of an agent name if the name was not parsed correctly. Now, we have a check in place and if the result is null, Lisa will simply say:
_“Our leasing agent will be there to show you the property.”_



# 8/02/21

## 🚀 Features

### Counteroffer and Showing Parser Improvements

#### Add timestamps to negotiator payload (#14130) -
Our backend data for the showing negotiator now includes more accurate timestamps.

### Translation and DeepL Improvements

#### Add Galaxy labels for additional occupants (#14189) -
We will now use ML (Machine Learning) to parse the correct number of additional occupants from `RECEIVE_ADDITIONAL_OCCUPANTS` messages. The previous parser was not very robust and Operators would notice mistakes.

#### Use translated message for non-English inbounds (#14202) -
We will now only be feeding English language text to Galaxy for ML interpretation. This change will improve bot performance because our Galaxy model is trained using English text and English-based ML models.

### Additional Improvements

#### Only retry reactivation if no appointment is scheduled (#14186) -
If a prospect already has an appointment scheduled via their SMS thread, we will no longer try to reactivate via email.

We recently saw instances where a scheduled prospect was snoozed, so they received a reactivation message before their appointment. The prospect did not respond to that reactivation message, so Lisa reached out via email to check if they were still interested. To avoid this confusion, we simply do not retry the reactivation if an appointment is already scheduled.

#### Remove unused code from the code base (#14199, #14160, #14179) -
We've removed various pieces of unused code from our code base.


## 🐛 Bug Fixes

#### Remove other unit types from the `Cross Sell` QA (#14210) -
We've reverted a recent change to the cross sell QA and will now only allow cross selling between properties that match the prospect's preferred unit type.

#### Return when no available cross sells (#14212) -
We have fixed a bug that might cause CC to freeze if there are no cross sell matches.

#### Add Guide email parser (#14223) -
We can now parse ILS lead emails from `@lwh.dv`.

#### Fix unparsed leads from Greystone Heights Apartments (#14222) -
We can now parse more leads from Greystone Heights Apartments.

#### Do not create guest cards for blocked numbers (#14213) -
We will no longer create guest cards for anonymous phone numbers.

#### Do not assume `scheduledShowing` exists in DB (#14221) -
We've eliminated errors resulting from changes in the `COUNTEROFFER` logic that create scheduled showings with an id of '0'.



# 7/26/21

## 🚀 Features

### New QAs

#### Add `Agent running late` QA (#14045) -
We now have an ['Agent late' QA](https://dynastycom.slack.com/archives/CB757SQ4S/p1626890278011700) for agent threads. In cases where an agent messages in letting us know they are running late, we can reply with this QA. It will send a quick message to agents that lets them know we've sent messages out to all prospects on a particular appointment.

The QA is available for appointments that recently occurred (within one hour) as well as future appointments on the same day. If no time is specified for the QA, it will message prospects on the soonest appointment the agent is scheduled to attend.

#### Add QA for replying to "Why Photo ID" (#14149) -
We have a new QA for Operators to use on prospect threads. In cases where a prospect asks why their photo ID is necessary, Operators can reply with the ['Why Photo ID' QA](https://dynastycom.slack.com/archives/CB757SQ4S/p1627003803019800):

![Why Photo ID QA](https://user-images.githubusercontent.com/8827318/126721613-a7b88b6e-d594-4209-8745-b6be1a96d63c.png)

#### Add `Move in date` QA (#14129) -
We now have a QA that updates a prospect's move-in date. Typing "move" or "move in" invokes the `Move in date` QA. Typing no date, "now," or "ASAP" will set the prospect's move-in date to the current date and the UI will show the move-in date as "ASAP." Entering a date in the correct format gives the corresponding QA:

![Move-in date QA](https://user-images.githubusercontent.com/62905507/126541079-e649d347-76c6-4602-bd64-797f3bb56e72.png)

Clicking on the QA result will send an acknowledgement to the prospect:

![move-in date updated acknowledgement](https://user-images.githubusercontent.com/62905507/126541191-514b49a2-56b6-434b-9977-4465a441bcfc.png)

And updates the thread's move-in date in the database and UI:

![move-in date updated](https://user-images.githubusercontent.com/62905507/126541306-fa2d6e37-9597-49b3-a631-1403b34ba8f6.png)

### Counteroffer and Showing Parser Improvements

#### Don't classify as CO (counteroffer) if already accepted appointment (#14155, #14168) -
In cases where a prospect with an already accepted appointment thought they may want to change their appointment, our `counteroffer` classification would cancel their existing appointment. Now, we will not use the `counteroffer` classification for prospects that have appointments scheduled.

#### Counter vague counteroffers (#14044) -
The showing negotiator will more actively interpret messages that might indicate a prospect's accepting a showing or offering another showing time.

#### Fallback to vague CO slots if we don't find any for the preferred slot (#14117, #14153) -
After a prospect gives us their time preference for an appointment, we try to give them an appointment close to that slot. However, if we fail to find an appointment within those parameters, the vague counteroffer logic will begin offering appointment times to try and schedule the prospect.

#### Finetune vague CO thresholds (#14097) -
We've increased the confidence score required to mark a message as `counteroffer`.

#### Adjust auto accept thresholds (#14158) -
In the showing negotiator logic, we have increased the auto accept threshold requiring a higher confidence score when we automatically schedule prospects for appointments.

#### Give weekday preference if we are confident (#14152) -
If a prospect says they are unable to attend appointments on a certain day of the week, the vague counteroffer logic will exclude that day of the week from future appointment offers.

#### Counteroffer can accept previously rejected appointments (#14135) -
The `counteroffer` logic will now search the list of rejected appointments to see if any explicitly meet the prospect's preferences for appointment time.

When we offer multiple appointments to a prospect, and the prospect selects one of them, the rest are updated to `REJECTED`. However, a prospect may have a change of heart and want to accept one of the rejected appointments. Lisa will now be able to successfully recognize the counteroffer and accept the desired appointment.

#### Don’t set a confirmation message to accept or counteroffer (#14068) -
We have refined the showing negotiator such that it will not be confused by a prospect messaging to 're-confirm' an appointment they have already accepted and confirmed.

### Translation and DeepL improvements

#### All languages translation wide release (#14105) -
We now [translate inbound and outbound](https://dynastycom.slack.com/archives/CB757SQ4S/p1626830225003100) messages for all firms. If a firm has English conversation only set up, we don't translate any outbound messages.

#### Polish translation UI features in CC (#14112) -
We've made some updates to the translation UI, including:
- Lowering the contrast of the `translated by DeepL` tag
- Providing the “English only” warning before clicking send on a message if the firm is set to ‘English conversation only’

#### Polish translated transcripts (#14165) -
Translated transcripts have been updated in the styling and the translations formality. There is now:
- more space between the translated message and the message itself
- a fade-in color change of the translated text when hovering over it
- the formality parameter has been reduced so that we sound more natural

![Translated transcripts improved](https://user-images.githubusercontent.com/60522124/127543062-764c561d-2f82-4026-b4db-44f89413f20b.gif)

#### Add logop for translation (#14071) -
We recently noticed a higher than expected translation cost through DeepL. To maintain visibility every time we call the translate function, we now have logging to better track the function. This will provide insights into the number of characters translated per day.

### Additional Improvements

#### `Cross sell` includes all of a firm's tasks and is also searchable by address (#14042) -
`Cross sell` and `Cross sell search` now [include all property tasks](https://dynastycom.slack.com/archives/CB757SQ4S/p1626833741005200) within a given firm. Operators will no longer need to cross sell “manually” by creating new threads. Properties are also now searchable by the address as well as the property name.

#### Only offer `Undo outside scheduling` if `Outside scheduling` was previously used (#14077) -
The `Undo outside scheduling` QA will now only be available where Operators have already used `Outside scheduling`. This removes confusion and clutter when checking outside scheduling cases.

#### Pluralize “time” to “times” (#14083) -
Thanks to an Operator’s feedback, we have updated the copy to the `Soonest showing` QA.

Before:
_“Sorry, we don’t have any earlier appointment time to offer.”_

After:
_“Sorry, we don’t have any earlier appointment times to offer.”_

#### Draft forecasting stats (#14055) -
To provide more visibility into weekly shift data, Ops leaders now have a forecast for the upcoming draft week. This data shows the current number of Operators, planned average hours per Operator, and how many shifts we’ve scheduled.

#### Removes showing parser from Lisa repo (#14050) -
We've removed the showing parser, which we no longer use, from Lisa's code.

#### Move Bulk Merge LeasingTasks from Admin page to Firm Bulk Update page (#14131) -
We've moved last week's bulk merge update from the admin page to the firm bulk update page.

#### Copy passwords into secrets (#14100) -
In an effort to keep firm passwords more secure, we’ve created a designated location to store “secrets.”

#### Extract secrets from Exchange, GSuit, and PmInfo (#14090) -
Firm PM software and email passwords were previously visible on the firm’s configuration page in Command Center. Now, we hide that information in CC.

Example:

![Hidden client content](https://user-images.githubusercontent.com/21322866/126350641-72a7aa57-f36c-4ced-83c0-3c0f57eec48f.png)


## 🐛 Bug Fixes

#### Don't automatically cross sell far away properties (#14093) -
We now exclude all properties that are too far away or that have a different property type (e.g. SFR vs MF) when Lisa is automatically cross selling. Operators can still use the `Cross sell` or `Cross sell search` QAs to browse all the firm's properties.

#### Fix cross sell: increase showability constraint to 90 days (#14142) -
We were unable to pull up certain properties when trying to cross sell. The reason was that the properties weren't showable within the next 7 days. Now, as long as a unit is available within 90 days we are able to cross sell to it.

#### Specify source language when making DeepL request (#14124) -
To obtain as accurate of a translation as possible, we now tell DeepL what the source language is.

We previously thought that their language detection was good enough, but found that a Spanish message was classified as Italian. The misclassification caused the translation to fail, so we’ll now provide the source language to avoid that error.

#### Don't make DeepL request if no texts provided (#14103) -
We had a bug where DeepL translation requests would return an error if no text was provided. Now, we first check if there is text that needs to be translated. If not, we simply return a result without attempting to translate.

#### Allow `SEND_RETRY_REACTIVATION` on inactive threads (#14163) -
We recently had a bug where we could send `SEND_REACTIVATION` to an inactive thread that we've snoozed for more than 30 days, but we could not send a follow-up `SEND_RETRY_REACTIVATION`. Now, as long as we've sent a `SEND_REACTIVATION` in the past ~24 hours, we can also send a `SEND_RETRY_REACTIVATION`.

#### Parser for Wufoo.com property website forms (#14089) -
Lisa can now support the Eenhoorn-implemented wufoo.com form.



# 7/19/21

## 🚀 Features

#### Allow an admin to remove an Operator from a shift (#14063) -
Command Center admins are now able to remove Operators from a shift when necessary. This will help in emergency situations when a reassign is not possible or when a shift is picked up by mistake.

#### Always unarchive non-preferred threads with inbound (#14048) -
When a prospect sends an inbound message on a given thread, the thread will enter the inbox regardless of the prospect's preferred form of contact (SMS or email). Lisa will not reply on a non-preferred thread, but an Operator can now jump in to handle those instances.

#### Show translation of messages in transcript (#14040) -
We've updated transcripts such that both the message body and translation of the message will appear where applicable.

Example:

![Translation recorded in transcript](https://user-images.githubusercontent.com/62905507/125694333-f98cf34a-ee04-4717-9ae2-d3895b87acc6.png)

#### Showing negotiator updates (#14012, #14015, #14028, #14030, # 14033, #14036, #14038) -
We have made a number of adjustments to the showing negotiator, including:
- Increasing the range of times we offer in a counteroffer
- Making sure we pass the auto-accept threshold before marking a prospect as accepted
- Lowering the confidence threshold for auto-accepting a prospect
- Better handling for out-of-context accepts or counter offers

#### Exclude `RECEIVE_PHOTO_ID` from `COUNTEROFFER` reclassification (#14021) -
The `RECEIVE_PHOTO_ID` message from prospect will no longer be reclassified as a `COUNTEROFFER`.

#### Bulk Update - Merge existing tasks (#14027, #14065) -
Because we have repeatedly received requests from firms to combine tasks, we have created functionality to automate the process.

#### Send standalone CC support requests to support@dynasty.com (#14007) -
Support requests for standalone customers are now directed to the support@dynasty.com email. Similar to the update for standalone NPS detractor cases to support in #13697.


## 🐛 Bug Fixes

#### Disable reassign button fix (#14060) -
We have resolved a bug where the `Reassign` button was always enabled on the 'Change Shift?' modal for Operator shifts.

#### Fix bugs in Soonest QA (#14019) -
Several bugs with the 'Soonest' QA have been squashed.



# 7/12/21

## 🚀 Features

#### Add 'Soonest' QA to give prospect earliest showing slot (#13886) -
We’ve added a conditional QA that allows the Operator to offer the [two soonest appointments](https://dynastycom.slack.com/archives/CB757SQ4S/p1625861278263600) rather than the suggested appointments. This QA is only available after the Operator has offered the suggested appointments, otherwise it will prompt the Operator to use the original 'Offer show' QA.

#### Correct the QA display name for the 'Speaks {language}' QA (#13942) -
Previously, we used ‘Update contact language’ as the QA display name for the 'speaks {language}' QA. This differed from the trigger for the QA, so we have changed the QA display name to 'Speaks' in order to better match the way we evoke it.

Before:

![Previous 'speaks language QA' display name](https://user-images.githubusercontent.com/212360/124332238-df450180-db45-11eb-9fcb-6ddbf1a68ca3.png)

After:

![Current 'speaks language QA' display name](https://user-images.githubusercontent.com/59895301/125689944-6b533d3e-95b0-4507-a0bf-de8955db0a28.png)

#### Add validation for using naughty words (#13958) -
We have made some changes to our [naughty word filtering](https://dynastycom.slack.com/archives/CB757SQ4S/p1625694454245300), including providing a failure message in Command Center along with the ability for Team Leads or Engineers to manually overwrite the filter and send flagged messages where necessary.

Example:

![Naught Words Failure Message](https://user-images.githubusercontent.com/62905507/124672989-e719e400-de6c-11eb-9fb4-5ca895f8e220.png)

#### Add days of week to 'Showing hours' QA (#14004) -
To enhance the functionality of the showing negotiator, the 'Showing hours' QA will now include the day of the week along with the month, date, and time. This helps avoid any confusion on the part of the negotiator, especially when the dates are far into the future.

Before:

![Showing Hours QA Before](https://user-images.githubusercontent.com/7515702/125107999-4b6bbc00-e096-11eb-9eb1-76468ef0e4ac.png)

After:

![Showing Hours QA After](https://user-images.githubusercontent.com/7515702/125110319-46f4d280-e099-11eb-86fb-43656a0d7915.png)

#### Use `ACCEPT_OR_COUNTEROFFER` label (#13948, #13949) -
We have added a new message label, `ACCEPT_OR_COUNTEROFFER`, to deal with situations where the showing negotiator may have otherwise not been engaged because the message would be classified as `HUMAN_FALLBACK`.

This new label entails a number of changes, including no longer classifying boss and agent messages as `COUNTEROFFER` and no longer overwriting the `CONFIRM_SHOW` classification when a prospect confirms their appointment.

#### Try to counter counteroffer (#13862) -
As we continue to iterate on the appointment negotiator, now Lisa will [attempt to counteroffer](https://dynastycom.slack.com/archives/CB757SQ4S/p1625773067249000) a time that is within one day of the time proposed by the prospect after our initial offer. The negotiator will also take into account whether they prefer an earlier or later time compared to our initial counteroffer.

Example:

![Counter counteroffer example](https://user-images.githubusercontent.com/6893229/124034531-9e40d600-d9fb-11eb-80d8-74a809bb79f0.png)

#### Remove 2nd copy for `ReactivateEmailFromSms` (#13952) -
We have removed a copy option for our `SMS to email` reactivation message to prospects. The following copy variant simply didn’t get as many replies:

_"This is Lisa, I sent you a few texts the other day about (property). Just following up to see if you’d still like to rent with us. Let me know!"_

#### Use different copy for 'Stop Showings' QA when showing policy is `ALWAYS` (#13913) -
The 'Stop Showings' QA now utilizes different copy when the showing policy is `ALWAYS` to convey accurate information to our agent partners.

#### Remove language tagging, translate inbound based on contact language (#13987) -
For our real-time language translation, we now use the contact's preferred language instead of language tagging to determine whether or not we translate. If we change a contact's language to something other than English, the ten most recent messages they've sent us get translated in all the threads they have.

#### Wide release of reactivation via new Email threads (#13997) -
We've released our new reactivation workflow to all firms. Now, cold SMS leads will be reactivated with emails if we have the prospect's email address.

#### Remove the function that handles accepting out of context showings (#13993) -
Because Galaxy can now apply the `ACCEPT_OR_COUNTEROFFER` label, Lisa can handle out-of-context showings implicitly. There is no need for distinctive functionality to recognize and respond to showing acceptances that are outside of the normal conversation flow.


## 🐛 Bug Fixes

#### Don't change classification of `STILL_INTERESTED` when `ACCEPT_OR_COUNTEROFFER` is active (#13998) -
The new `ACCEPT_OR_COUNTEROFFER` classification previously overwrote some other classifications, specifically `STILL_INTERESTED`. This has since been fixed.

#### Do not always prioritize immediate non-msg actions (#13995) -
We no longer prioritize the immediate non-message action on a thread. This resolves a bug where sending the immediate non-message action that updates a thread's `preferredTourType` would block other important message actions to prospects.

Now, if the earliest action is a non-message action, we replace it with the earliest message action if it needs to be sent within the next fifteen minutes.

#### Change Handicapped units to ADA units (#13962) -
We now refer to our ADA compliant units as `ADA units` more consistently in CC.



# 7/05/21

## 🚀 Features

#### Translate outbound Spanish (#13923) -
[Outbound Spanish translation](https://dynastycom.slack.com/archives/CB757SQ4S/p1625265505207100) is live! For firms that have the 'TRANSLATE_SPANISH_OUTBOUND' feature flag enabled, we automatically translate all outbound for contacts that have their language set to Spanish. If we change the prospect's language back to English, the outbound is sent untranslated.
![Outbound translation into Spanish](https://user-images.githubusercontent.com/62905507/124188843-a5f09100-da74-11eb-9215-5fafb7057945.png)

#### Add ‘Speaks Language’ QA (#13931) -
To go along with our new outbound translation features, we now have a QA that will set the language for the thread. Typing 'speaks {language}' or 'language {language}' into the QA bar will invoke it.

#### New language field on Contact (#13911) -
We now store information about prospect's preference for spoken language. If their preference is Spanish, we can use our new auto-translating functionality. This information is visible in the top right of a thread and can be adjusted by Operators using our 'Speaks Language' QA.

![Example](https://user-images.githubusercontent.com/62905507/124031635-69f1f900-d9ac-11eb-94da-f25b5c7d1bfb.png)

#### Add prospect QA to undo outside scheduling (#13929) -
Operators can now use ‘undo outside scheduling’ from a prospect thread. Previously, undoing outside scheduling was only an agent QA that would also send an automated message. However, sometimes confusion would arise in certain circumstances when the QA was deployed from the agent thread. Moving forward, this QA will help reduce that confusion and the awkward bot message.

#### Add ‘List holidays’ QA (#13837, #13921) -
Our new 'List holidays' QA will send a message on an agent or boss thread that lists all the firm holidays.

#### Add a shortcut to move to message with least time left (#13895)
The [keyboard shortcut 'g'](https://dynastycom.slack.com/archives/CB757SQ4S/p1624999145182400) when used in the inbox will navigate CC users to the thread with the shortest timer.

Currently available keyboard shortcuts:
![Currently available keyboard shortcuts](https://user-images.githubusercontent.com/7515702/123847245-39865e00-d8cb-11eb-81db-3c963fbc234f.png)

#### Subtract cost for later showings so that they are preferred (#13938) -
Analysis from our product team has shown that the rate of appointment no shows drops at each hour of the day. We are now weighing the appointments Lisa will offer differently with a preference for appointments later in the day.

#### Removes the mention of auto assigned station name from lobby title (#13932) -
CC no longer displays the name of an Operator's assigned station to match our new navigation functionality. As a reminder, you can access an Operator's current inbox simply by clicking their name in the lobby, making the alphanumerical station name unnecessary.

Before:
![Lobby page with old station name](https://user-images.githubusercontent.com/7515702/124201355-ac8b0280-da8c-11eb-8622-87c47786f31e.png)

After:
![Lobby page without old station name](https://user-images.githubusercontent.com/7515702/124201364-b0b72000-da8c-11eb-9aac-08c0432a2dd9.png)

#### Update Multi Offer Show variation with a period (#13864) -
We have updated the 'SEND_MULTIPLE_OFFER_SHOW' copy to add a missing period.

Before:

![Old 'SEND_MULTIPLE_OFFER_SHOW' copy](https://user-images.githubusercontent.com/60522124/122925623-02e19e00-d335-11eb-80ee-19c0192fd26b.png)

After:
Similar 'SEND_MULTIPLE_OFFER_SHOW' messages will now read:
>What about today at 3pm? We could also do tomorrow at 11am.

#### Remove “coronavirus” from Lisa copy (#13924) -
We have removed references to 'coronavirus' from various messages, including Lisa's explanation of why a firm is offering virtual tours.

#### Remove “showable on” from standalone ‘Stop showings’ QA (#13900) -
The 'Stop Showings' QA no longer includes the “showable on” date for standalone tasks. This removal provides cleaner data and helps to reduce confusion.

#### Skip inbox for outbounds that don't need Operator interaction (#13874) -
Lisa's outbound messages that do not require an Operator to take any action will no longer appear in the inbox.

#### Give up if we can't parse a valid date (#13865) -
Our appointment negotiator will no longer try to parse prospect responses if it cannot determine a valid date. We will actively be looking for good fall back methods of determining a prospect's appointment date preference.


## 🐛 Bug Fixes

#### Improved counteroffer (#13876) -
We have refined the appointment negotiator backend to more accurately offer valid counteroffers.

#### Enhancements to preferred tour type (#13808) -
We have made several changes to the backend 'preferredTourType' logic. We now re-check the last 90 days of prospect threads when we change a task's available tour types to update their 'preferredTourType.' We also only use the 'null' value in cases where a task supports multiple tour types.

#### Only reject appointments if accepting a future appointment (#13906) -
Previously, marking a prospect as having attended an appointment would cause all other appointments to be rejected. This would cause unintended behavior if the appointment was in the past and the prospect had already been scheduled for another, future appointment. Now, we will only reject appointments if the one we're confirming hasn't happened yet.

#### Fix setting preferred tour type at thread creation (#13877) -
Previously, threads on single tour type properties did not have their preferred tour type set. In those cases, if only one tour type is offered, that should be the preferred tour type for the prospect thread. This has since been fixed.

#### Show correct text color for offboard date (#13934) -
The text color for the offboard dates that are not in the future should appear in coral in CC. The offboard dates will now appear in the correct color.

Example:
![Offboard date in coral](https://user-images.githubusercontent.com/7215139/124202680-7a839b80-daa0-11eb-8e0c-180f57a80673.png)

#### Validation to prevent us from sending evaluation skipped message (#13927) -
We recently encountered a bug where the 'SEND_AGENT_CAL_REMINDER' message for agents did not send the desired text and included "evaluation skipped until send time." We've added validation to prevent messages with this text from being sent by Lisa.

#### Zumper move-in date parsing fix (#13925) -
Zumper ILS messages will now have their move-in dates correctly parsed.



# 6/28/21

## 🚀 Features

#### 'SHOWING_HOURS' QA no longer cancels accepted showings (#13810) -
The 'Showing hours' tag and QA used to cancel all previously offered appointments at off-site properties. To avoid unintended cancellations, we've removed the cancellation behavior for 'Showing hours' if the prospect has an appointment scheduled:

| Condition | Behavior |
| --- | --- |
| Prospect already has a future appointment scheduled and asks about showing hours | We provide a list of 5 appointments without cancelling the scheduled one and add: "I see that you are already scheduled for {appt}. Would you like me to cancel it?" |
| Prospect does not already have a future appointment scheduled and asks about showing hours | We provide a list of 5 appointments and cancel any previously offered appointments |

#### Add a shortcut for editing messages (#13818) -
We've added a [keyboard shortcut](https://dynastycom.slack.com/archives/CB757SQ4S/p1624553469129100), 'e', to be able to edit a queued message in the inbox.

Message shortcuts:

![Message shortcuts](https://user-images.githubusercontent.com/7515702/123173309-46223680-d433-11eb-8dde-ed102765f5af.png)

Here’s the shortcut in action:

![Gif of the shortcut being used](https://user-images.githubusercontent.com/7515702/123173388-618d4180-d433-11eb-9197-ec56204e4cfa.gif)

#### Lisa recognizes prospect counter offers with showing negotiator (#13735) -
Lisa's new [appointment negotiator model](https://dynastycom.slack.com/archives/CB757SQ4S/p1624560921140000) can determine concrete and vague counteroffers and time preferences. If we detect a concrete counteroffer (day of the week, day of the month, hour, or minute) we will attempt to silently offer, and accept an appointment.
![Example thread of Lisa accepting a prospect counteroffer](https://user-images.githubusercontent.com/6893229/122989905-a4073d80-d3a3-11eb-8735-fea5be23388c.png)


## 🐛 Bug Fixes

#### Do not remove message classification when Operators use shortcut 'e' (#13833) -
Using the 'e' keyboard shortcut would fail to send the correct message classification. Now, using 'e' behaves the same as clicking the text box when editing an already queued message.

#### Cancel non self-guided showings if no more task agent for the task (#13803) -
We previously had a bug where the 'Decline all' QA would not fully cancel an appointment if the property had no assigned agents. Moving forward, if all agents are removed from a property, we will automatically cancel all scheduled in-person and virtual tours. Self-guided tours, however, will remain scheduled because they do not require an agent present.

#### Counter Offer fine tuning (#13840) -
As we develop the counter offer functionality, we want to make sure we are correctly identifying messages where a prospect provides a desired appointment date. To that end, we are excluding messages where the dates requested are not self consistent (i.e. they request a numerical date and a day of the week that do not match).

#### Correctly transfer prospect info from SMS to email thread when reactivating (#13838) -
In reactivating cold SMS threads via a newly-created email thread, we failed to transfer information about the prospect from one thread to the other. Such information included market source, preferred unit type, and desired move-in date. This has been fixed so that all the information about a prospect will appear when we reactivate an SMS thread in a newly-made email thread.

#### APM: Assign a unit when creating a new appointment (#13703, #13793, #13813) -
We now tell APM which unit to assign for new appointments if all units are occupied at a property with the 'ALWAYS' showing policy. We also add a note on guest cards when we assign an occupied unit to an appointment.

Tasks set to the 'ALWAYS' showing policy were experiencing a bug where appointments would not appear on AppFolio calendars or the prospect’s guest card. When we create an appointment in APM, the system will automatically assign a unit to the appointment. However, there was an error when all the units were occupied. If the showing policy is 'ALWAYS', we still want to create an appointment even if all the units are occupied.

This has since been fixed, and all functionality for APM clients with the ‘ALWAYS’ showing policy will work as intended.

#### Realpage: Only re-open guest cards when we receive an inbound on the corresponding thread (#13787) -
Pursuing a fix for our customers, we resolved a bug where prospect inbound on a single thread would reactivate guest cards for every property where they had an existing thread. This has since been fixed.

#### Fix Calendar Sync spamming agent with updates (#13681) -
We have adjusted our backend checks to reduce the number of messages agents receive when a calendar event is updated.



# 6/21/21

## 🚀 Features

#### Return Agent threads when Senior Operators return from breaks (#13731) -
Agent inbound that comes in while a Senior Operator is on break will now be redirected back into their inbox when they return from the break.

#### Move 'LANGUAGE_…' tags to their own CC tagger section (#13751) -
Language tags now have their own separate section in the CC tagger.

Before:
![before](https://user-images.githubusercontent.com/212360/122302576-2335d680-ceb7-11eb-9b63-06bc36b34cec.png)

After:
![after](https://user-images.githubusercontent.com/212360/122302514-14e7ba80-ceb7-11eb-9a65-b61b04c3f702.png)

#### Balance thread continuity and workload equity (#13694) -
To maintain an equitable workload for all Operators, we have adjusted the way messages are sorted into inboxes. If an Operator has worked with a thread previously, they will still receive priority on getting that thread back to help with continuity. If an Operator’s inbox is already busy, however, any returning threads will now go back into the round robin pool to be redistributed.

#### Add 'JUNETEENTH' and 'JUNETEENTH_OBSERVED' as possible firm holidays (#13773, #13777) -
Agents can now ask Lisa to block showings for Juneteenth, which will block showings on June 19 of the current year.

We also added a feature to facilitate calendar blocking for 'JUNETEENTH_OBSERVED', which will block Lisa from offering showings on Friday June 18 or Monday June 20 if and only if June 19 falls on a Saturday or Sunday in that year.

#### Add additional occupants to APM (#13631) -
We now update the number of additional occupants directly in APM during a guest card sync.

#### Classify language again using DeepL after Lingua (#13689) -
Previously, we used Lingua to classify an inbound message's language. Now, we'll use DeepL to classify the language after Lingua detects that the message is not written in English.

#### Do not translate empty messages (#13716) -
Our DeepL translator will no longer translate empty messages.

#### Send standalone NPS detractor cases to support (#13697) -
Up until now, standalone clients who submitted detractor NPS surveys would only be sent to our standalone account manager. Moving forward, these cases will be sent to a support role based email. This gives all support members access to those alerts.


## 🐛 Bug Fixes

#### Fix bugs that unarchived threads (#13670) -
We experienced a bug where old Lisa threads were unarchived after a long period. This could also occur when a prospect with an existing thread messages about another property. Now, old Lisa threads should no longer be unarchived arbitrarily.

#### Use correct DeepL API domain (#13746) -
We experienced some errors with DeepL due to using the incorrect API. This has now been fixed.

#### Add parser for 'STREET_EASY' emails (#13696) -
Lisa can now parse emails from 'streeteasy.com', provided they are forwarded to the property-specific email marketing source.



# 6/14/21

## 🚀 Features

#### Follow up on unresponsive SMS leads via EMAIL (#13569) -
We've introduced a feature to follow up with cold SMS leads via email to increase the chances of Lisa getting a reactivation response.

Here's an 'SMS' thread, about 24 hours after we sent the 'SEND_REACTIVATION' message and the prospect hasn't responded yet:

![Send Reactivation SMS](https://user-images.githubusercontent.com/62905507/120723171-1613f300-c486-11eb-8d37-7984cf3e6109.png)

This prospect has an email address, but not an 'EMAIL' thread.
When the queued action is executed, this new 'EMAIL' thread is created and the following message is sent on it:

![Send Reactivation EMAIL](https://user-images.githubusercontent.com/62905507/120723280-4b204580-c486-11eb-9148-b70f7ceb0072.png)

#### Acknowledge prospect’s response to number of occupants and add QA (#13177) -
We have added a Lisa message that responds affirmatively to prospects when they respond to our question about additional occupants. We have also added a QA to make it easy for Operators to manually adjust occupant information.

Before:

There's a ''+0 occupants' 'editBox' in the right 'ContextSidebar', but you have to fill values manually to save them to the 'thread.info.additionalOccupants' field. There's no way to use QAs to capture the data or reclassify the last inbound as 'RECEIVE_ADDITIONAL_OCCUPANTS'.

![Occupants edit box in context sidebar](https://user-images.githubusercontent.com/212360/121245197-9a82bf00-c854-11eb-9075-cccd5daeb520.png)

After:

Now, Lisa can acknowledge receiving the additional occupants. Users can also add occupants using the new ‘Add’l Occupants’ QA by typing “add {number}”.

![Add Occupants in QA bar](https://user-images.githubusercontent.com/62905507/117706305-fee12e80-b181-11eb-839f-25dbef8bb924.png)
![Add'l Occupants QA](https://user-images.githubusercontent.com/60522124/123007069-998a7b00-d386-11eb-9c8b-de3e7ff42a9f.png)


#### Improve guess time by appending AM or PM if missing (#13610) -
Previously, if Operators used the ‘Decline {time}’ QA without including AM or PM, it would not parse. Now, for hours larger than or equal to 9 but less than 12, Lisa interprets it as AM. For all other hours, Lisa interprets it as PM.

#### New copy for 'SEND_SHOWING_UNAVAILABLE_RESCHEDULE' (#13597) -
Because of confusion among prospects, we have changed the copy for 'SEND_SHOWING_UNAVAILABLE_RESCHEDULE'.

Before:

_Sorry, I just saw that that time is no longer available._

After:

_I'm sorry, your appointment time at {property name} is no longer available._

#### Improved prospect search with RealPage integration (#13616) -
Previously, when a prospect messaged Lisa through a widget click, we would search the Realpage API by their phone number, their email address, or both. Because we only queried the Realpage API one way, this could result in duplicate guest cards in a client's system.

Now, we will query by phone number first and then email address to avoid creating duplicate guest cards.


## 🐛 Bug Fixes

#### Add available slots to the Operator Shift Editor (#13593) -
When the Operator draft previously matched shifts for the week, but some shifts weren’t covered, it was not possible to see how many open shifts remained. Hovering over a shift with open slots will now show how many slots are still available.

![Available Shift Slots](https://user-images.githubusercontent.com/60522124/123001655-f84bf680-d37e-11eb-81d3-55ee53ad2fe8.gif)

#### Clear credits on Operator clock out (#13603) -
Previously, an Operator might experience an unusually small amount of inbound by accumulating 'credits' from previous shifts. We've resolved the issue by clearing credits when a user clocks in and out.

#### Better agent matching with the 'Outside scheduling' QA (#13391) -
We've made several improvements to the 'Outside scheduling' QA. Now, Operators can message all agents assigned to a task if there is no exact match when using the QA. Operators can also choose which agent to message in the case of multiple matches.

- **Case - no exact match:**

Before:

![No exact match for agent before our update](https://user-images.githubusercontent.com/65361670/121091924-9ba5e480-c79f-11eb-9594-153a21e3618f.png)

After:

![No exact match for agent after update](https://user-images.githubusercontent.com/65361670/121092763-ee33d080-c7a0-11eb-8584-45354dfb442f.png)

- **Case - there are multiple matches:**

Before (matches 2 agents but returns 1 agent as QA):

![Multiple matches for agent before update](https://user-images.githubusercontent.com/65361670/121094175-10c6e900-c7a3-11eb-9e97-696f65272b96.png)

After (matches 3 agents, returns 3 separate QAs for each agent):

![Multiple matches for agent after update](https://user-images.githubusercontent.com/65361670/121094049-d3faf200-c7a2-11eb-92fe-ced77c187cf6.png)

#### Fix APM assigning unit with wrong unit type (#13626) -
Previously, when syncing appointments to APM, we would ask APM to assign any studio or 1BR unit to an appointment if a prospect was interested in either. This resulted in the wrong unit type sometimes being assigned to appointments.

Now, when we ask APM to create an appointment, we ask them to assign a 'showing.unitType' unit. This results in the correct unit type being reflected in APM's record of the showing.

#### Say 'hi/hello there' if contact firstName does not exist (#13641) -
Lisa's greetings could previously encounter some issues if the prospect had not yet provided their first name. Now, we'll replace an empty first name with 'there', using 'Hi there' when the contact first name does not exist.

#### Do not annotate empty messages (#13570) -
We have removed tags assigned to empty inbound messages. This will prevent the bot from tagging and queueing up basic property info QAs when the prospect did not request said info.

#### Fix ‘List units’ sync time bug (#13601) -
We recently saw that the QA displayed “11:11pm” no matter the entered date. The ‘List units’ QA will now display the correct date and time from when the latest sync job finished.

#### Fix unparsed leads from Zumper (#13655) -
As we continually strive to minimize unparsed leads, we've added capacity to parse more Zumper leads.



# 6/7/21

## 🚀 Features

#### Add tags for ‘SQFT’, ‘FUTURE_AVAILABILITY’, and ‘LISTINGS’ (#13508) -
Thanks to Operator feedback, we now have tags for ‘SQFT’, ‘FUTURE_AVAILABILITY’, and ‘LISTINGS’ so that Lisa can queue up those QAs.

#### Parsing the occupants count response (#13151) -
Lisa is better able to parse a prospect’s response after asking about additional occupants. The ‘Occupants’ field in the top right of their thread will also update with the appropriate number once parsed.

![Occupant Count](https://user-images.githubusercontent.com/60522124/122955523-2fee7a80-d34e-11eb-9acc-86f16a03464c.png)

#### Generate "Unsubscribe" email URL for follow up (#13494) -
When we send an email follow up to prospects that have not recently responded on their SMS thread, we want to give them the option to unsubscribe from further Lisa emails. When an email is classified as ‘SEND_FOLLOW_UP_FROM_EMAIL_THREAD,’ an “Unsubscribe” hyperlink is placed at the bottom of the sent email.

#### Add validation for sending messages to unsubscribed threads (#13516) -
If a prospect decides to unsubscribe from receiving Lisa messages, outbound messages will now be blocked by our validation checker. Clicking on the little red triangle will reveal the “Unsubscribed thread validation failure” modal:

![Unsubscribe Validation](![Unsubscribe Validation](https://user-images.githubusercontent.com/60522124/122955705-53192a00-d34e-11eb-9fd0-935c9e9abb69.png)

#### Use the full month names (#13524) -
Lisa will now use full month names instead of 3 character abbreviations. Prospects would get confused between "Jun" and "Jul", so this change will help avoid future confusion.

#### CSV: Replace line breaks with a single space (#13470) -
We previously had a problem where our clients couldn't parse our CSV report file when there was a new line in prospect name columns. Moving forward, we replace every new line character with a space when writing a CSV report. This will help the report parse correctly and allow the names to appear as expected.

#### Remove a couple of feature flags (#13519) -
‘QUALIFY_MESSAGE_FOLLOWUP’ has been deprecated because we want it to be turned on for all firms. ‘RETRY_REACTIVATION’ has been removed as a feature flag without deprecating because we may re-use this feature flag.

#### Add a parser for Trails of Sanger (#13551) -
An email parser has been added for Trails of Sanger.


## 🐛 Bug Fixes

#### Do not send lockbox code too early (#13443) -
We recently saw that Lisa tried to send a lockbox code one week early for a scheduled self-guided appointment. This happened because the prospect decided to reschedule their appointment, but Lisa thought they still needed a code for their previous appointment.

Moving forward, Lisa will make sure that the showing id for the currently scheduled appointment matches the showing id from the most recent ‘SEND_CONFIRM_SHOW’ message. This will allow the lockbox code to be sent at the correct time.

#### Prevent ‘Outside scheduling’ QA from sending messages to non-agents (#13490) -
The ‘Outside scheduling’ QA recently sent some confirmation messages to bosses assigned to a property in CC. Non-agents, such as bosses and investors, do not regularly assist with tours, so they should not be included. Moving forward, we filter out non-agents as recipients for outside scheduling messages.

#### Ignore case when getting original sender (#13584) -
We recently had a repeat of #12117, where new messages from a prospect would come in on new threads instead of a singular thread. The recent instance slipped through the previous fix due to a case sensitivity problem. That has been addressed, so Lisa will update the prospect’s primary thread in CC as expected.

#### Fix self scheduled showing infinite loop (#13553) -
We had a problem where the same ILS message for a self scheduled appointment continued to come into a prospect thread. These messages were only showing on the inbound side, so they didn't spam the prospect, but should have only appeared once.

This has since been fixed by checking against both email and SMS threads for a prospect. When the ILS message is present on one, we will not try to re-insert it on the other.

#### DeepL: Prevent overriding translation during message reannotation (#13521) -
We recently found that annotating a new Spanish message would override a message translation. Now, annotating a new message that should be translated with DeepL will no longer interrupt the translation process.

#### Fix issue with Twilio calls API (#13542) -
Continuing to investigate the large number of missed voice calls being recorded, we found some strange behavior with Twilio API. Some date ranges were getting ignored due to a syntax distinction. This has been fixed to include the correct syntax so that we can properly receive requested date ranges.



# 6/1/21

## 🚀 Features

#### Translate Spanish inbound with DeepL (#13423) -
Lisa can now [translate Spanish inbounds](https://dynastycom.slack.com/archives/CB757SQ4S/p1622245822032600) if the feature flag ‘TRANSLATE_SPANISH’ is enabled! Inbound messages that have been translated will have the phrase “Translated by DeepL” below the message itself. Clicking on the message will expand it to reveal the original message in Spanish as well as the translation.

![DeepL- translated by](https://user-images.githubusercontent.com/60522124/122607183-31672c80-d048-11eb-990b-26b2abce93c5.png)

![DeepL- translation](https://user-images.githubusercontent.com/60522124/122607218-3f1cb200-d048-11eb-831c-dee68c3304ae.png)

#### Round robin when multiple Senior Operators are on the same shift (#13411) -
CC now allows more than one Senior Operator to be on the same shift! When we have multiple Senior Operators on the same shift, agent threads will round robin between them.

#### Add custom greeting for RPM Express voice calls (#13471) -
RPM Express now has a custom greeting for the general phone tree.

#### Add an additional "you" to ‘Define self-guided tour’ QA (#13466) -
The second sentence for one of the ‘Define self-guided tour’ QA responses was missing an additional “you.” This made the sentence feel a bit awkward, but has been updated.

Before:

_A self-guided tour is an in-person tour. I will give you a code to unlock the door and can then let yourself in and out._

After:

_A self-guided tour is an in-person tour. I will give you a code to unlock the door and you can then let yourself in and out._

#### Treat investor threads like agent and boss threads (#13401) -
Investor threads were recently getting routed to Operator inboxes with the CVS Router changes. This has been updated so that investor threads are routed to Senior Operators when present, similar to agent and boss threads.

#### Add admin job for running the Operator Draft (#13468) -
CC now has an admin job called ‘Operator Draft Shift Matching Round.’ This allows an admin to push a round of shift matching for the Operator Draft.

As a recap, the draft allows Operators to pick shifts and set their schedules. We recently experienced a bug where the Operator Draft did not match shifts after the first night. The new admin job will help in cases where shifts were not matched to Operators as expected.

#### New admin job, ‘Backfill Guest Cards for all Firms’ (#13396, #13405) -
We have a new admin job that allows a user to retry syncing failed guest cards for all firms.

#### PreferredContactType when ‘Ingesting Agents Onboarding Spreadsheet’ (#13429) -
Most agents prefer email, but SMS was automatically set as their preferred contact when a phone number was present during onboarding ingestion. Moving forward, when an agent/new contact is created or updated, the ingestion process will default to email as their preferred contact.

#### Update Endpoints for firm during ‘Onboarding Ingestion’ (#13428) -
When the property phone number was pulled onto a task using the ‘Ingest Properties’ admin job, some sources did not recognize it. This led to a caller being forwarded directly to Lisa's voicemail rather than the appropriate phone tree.

Now marketing source phone numbers are successfully being updated if they have changed.

#### Improve OnboardingService ingestion errors for users (#13467) -
When validation failed on spreadsheet ingestion, there was no explicit indication of whether the entire process failed or if a single section was skipped due to an error. We now have response JSON Errors that begin with a statement indicating if data has been updated or not.

#### Send alerts for endpoints-without-twilioSid metric events (#13383) -
We now have an alert set up to ping #lisa-eng-alerts in Slack if there is an endpoint not in Twilio.


## 🐛 Bug Fixes

#### Bypass cancellation check when using ‘Showing hours’ QA (#13340) -
After multiple cancellations the ‘Showing hours’ QA previously showed that there were no available appointments at MFR properties. However the ‘Offer’ QA returned several available times.

This has been fixed so that the ‘Showing hours’ QA will return available appointments even when the prospect has been cancelled on multiple times.

Before:

![Showing Hours QA- no appts](https://user-images.githubusercontent.com/60522124/122607322-607d9e00-d048-11eb-8a84-5471843b3426.png)

After:

![Showing Hours QA- with appts](https://user-images.githubusercontent.com/60522124/122607341-683d4280-d048-11eb-94b7-8c62d2c77f1f.png)

#### Fix for ‘Fire agent’ QA (#13412) -
The ‘Fire agent’ QA would sometimes not update the fired agent status to “cancelled” on all appointments they were connected to. This has been fixed to account for appointments with multiple agents. Now the status of the removed agent will be updated accordingly.

#### Setting agent specific virtual tour link  (#13372) -
We found that Lisa was adding a virtual tour link to prospect guest cards in AppFolio. However, this link was not the agent specific link that is also included on agent calendar invitations. If the agent joined the virtual tour using the link in AppFolio, Lisa would recognize the agent as a prospect and send a reminder to the agent to join the tour.

This has since been corrected so that the agent specific virtual tour link is included in prospect guest cards.

#### Fix guest card sync (#13394) -
We recently saw that some guest cards were not syncing automatically, but would succeed when run manually. This has been fixed so that guest cards automatically sync when there is an update to the contact or the appointment status.

#### Fix unparsed lead (#13482, #13469) -
Lisa can now parse leads from RentVision and Sawyer Living LA.



# 5/24/21

## 🚀 Features

#### Remove station picker (#13373) -
To streamline the CC Lobby’s functionality and align CC’s design with our new station balancer, we have [removed the station picker](https://dynastycom.slack.com/archives/CB757SQ4S/p1622743805053500).

#### Allow Operators to view another’s inbox (#13268) -
By clicking an Operator’s name in the Lobby of CC, a user can view their inbox.

#### Firms can opt-in to have Lisa close guest cards for cold leads (#13367) -
If a firm enables it, we will now close guest cards for prospect if 'SEND_BEST_OF_LUCK' or 'SEND_THANKS_FOR_TIME' is recently used in the conversation.

#### Add property name to agent-facing 'Outside scheduling' message (#13351) -
When an 'outside scheduling' message went out to an agent, we asked them about a prospect by name, phone number, and email address. Now, we’ll also identify the property the prospect is interested in to alleviate any confusion for agents who work at multiple properties.

#### Increase the automation of our ‘thank you’ messages using Galaxy labels (#13291) -
We’ve added labels to the Galaxy logic to help Lisa use 'SEND_BEST_OF_LUCK', 'SEND_THANKS_FOR_TIME', and 'SEND_ACK_GRATITUDE' more frequently and reliably.

#### Prevent 'task.buildingName' from duplicating 'address.street' (#13337) -
In making changes to the 'Address' QA, we found some number of cases where the property name and property address are the same. Now, attempts to add property names that are the same as the property address will be blocked to avoid repetition.

#### Update 'RequestOccupantsCount' copy (#13303) -
To help Lisa feel more colloquial, two phrases that ask about the number of occupants have been updated.

Before:

_And how many additional occupants will be living here?_

_And how many additional people will be living here?_

After:

_And how many additional occupants will be living with you?_

_And how many additional people will be living with you here?_

#### Enable new call UI with some customers (#13099, #13304) -
Some clients reported that it took way too many clicks to switch camera views. Now you can do it in just one click! Users can simply tap the camera icon at the top left of their virtual tour video call instead of going through settings.

This feature is initially only enabled for Trion (APM), JC Hart, Continental, and Eenhoorn.


## 🐛 Bug Fixes

#### Reopen guest cards for cold leads (#13378) -
Previously, if a cold lead messaged us again we would not reopen their guest card. Now, a new message from a cold lead will reopen guest cards as intended.

#### Don’t close ‘Cross sell search’ modal unexpectedly (#13380) -
The pop up for the ‘Cross sell search’ QA would occasionally disappear unexpectedly when the user tried to interact with it. This has since been fixed so that the modal will not close before the user intends.

#### Fix ‘Reassign all’ QA (#13315) -
The ‘Reassign all’ QA would sometimes not work: it didn’t work on a particular agent, only worked if a last name was used, etc. Turns out ‘Reassign all’ did not make all of the same checks as the ‘Reassign’ QA and would have some trouble. This has since been fixed so that both QAs work as expected.

#### Fix: Lisa offers an appointment with no longer supported tour type (#13320) -
We used to create showing slots based on the thread's tour type. However, we never checked if this tour type is actually supported by the thread. Now, when we create showing slots, we use thread's tour type by default. If that tour type is not supported by the task, then we choose the first tour type supported by the task.

#### Adjusted “thank you” timing (#13335) -
We expect that when the bot replies to prospects, it does so within a reasonable (~24 hour or less) window. Unfortunately we had a bug where Lisa would send an automatic follow up about 2 days late.

Moving forward, we limit bot “thank you” messages to only send when the last interaction was less than 1 hour prior.

#### Do not require CVS turned on to see CVS assigned threads (#13336) -
Users who had CVS auto stations turned off were not able to view any threads in CC that were assigned to another user. This has been updated so that users can view CC threads whether they have CVS stations on or off.



# 5/17/21

## 🚀 Features

#### CVS Router Boomerang (#13204) -
The CVS Router will now prioritize sending a prospect thread to the last Operator that worked with them when the Operator is still on shift. This allows for better continuity, faster response times, and generally leads to fewer mistakes.

#### Copy edit for ‘Rent’ with student housing properties (#13183) -
Operator feedback pointed out that the ‘Rent’ QA for student housing caused confusion. This has been updated to be more clear.

Before:

_… Note that the price is per bed._

After:

_… Note that the price is per bedroom._


#### Add ‘Showing duration’ QA (#13237) -
A new ‘Showing duration’ QA has been added to CC that quickly pulls the appointment duration for a given property/firm.

![Showing duration QA](https://user-images.githubusercontent.com/60522124/121432526-c8493000-c948-11eb-8ecc-61323dd8e214.png)

#### Change the language with ‘Unit unavailable’ QA (#13264) -
The ‘Unit unavailable’ QA used to always say “Sorry, those units are currently leased.” This would sound a little awkward when a prospect was discussing a house instead of an apartment unit. Moving forward, the QA will take the property type into account.

MF:

_Sorry, those units are currently leased. Would you be interested in hearing about our other available units?_

SFR:

_Sorry, this property is currently leased. Would you be interested in hearing about our other available properties?_

#### Prevent bot from offering new appointments late at night (#13253) -
Lisa was recently offering new appointments to prospects late at night in cases where the ‘delay show’ action was used. This was understandably upsetting for prospects.

Moving forward, Lisa will not automatically offer appointments during off hours (hours outside of 8am-9pm of the task's local time).

#### Only create showing room when accepted (#13181) -
We will not create a new Daily.co room until an appointment has been accepted - instead of creating a new room upon offering an appointment.

This decision came about because we were reaching our limit of open Daily.co virtual tour rooms. Essentially, we were creating rooms at a faster rate than we are expiring/deleting them.

#### Update responses when showing policy is ALWAYS (#13208) -
The ‘Rent’ and ‘Availability’ QAs previously did not consider whether or not the property’s showing policy was set to ALWAYS. Lisa now takes the showing policy into consideration and will encourage the prospect to discuss availability with a leasing agent if we do not see current availability.

![Rent, Availability with ALWAYS showing policy](https://user-images.githubusercontent.com/60522124/121432573-d8610f80-c948-11eb-9387-fe3270d0e9e6.png)

#### Copy edit for ‘Prospect No Show’ QA (#13191) -
A small copy change has been made to the ‘Prospect No Show’ QA to capitalize the ‘f’ in AppFolio. That line will now read:
_“And just so you know, you can also cancel and reschedule prospects directly from AppFolio without asking me! Here's how: link”_

#### Validate onboarding agent emails exist in APM (#13144) -
Lisa can now validate if an onboarding agent email address exists in APM. If the email does not exist in APM, a new agent contact will not be automatically created in CC.

#### Use alt. phone number when needed for rent cafe (#13254) -
Some leads from RentCafe have empty phone fields and a filled alt. phone field. We were not capturing the alt. phone previously, which led to some prospects getting split across two threads in CC. Moving forward, we will now use the phone alt. phone if the main phone field is empty.

## 🐛 Bug Fixes

#### Fix Agent Calendar visual glitch (#13239) -
Elements of the Agent Calendar would wiggle around or move when interacting with specific event modals. This has since been fixed so that event modals can be opened and closed without underlying elements of the calendar moving.

#### Fix missed call reconciler (#13205) -
We were recently seeing a large number of missed calls. The reconciler only imports SMS for now, so that we don’t miss any messages related to the leasing flow.

#### Remove deprecated feature flags from admin UI (#13241) -
We don't want to see deprecated feature flags in the feature flag dropdown or in the list of "on" feature flags for a firm on the CC Admin page. Both ‘Virtual Tour Reminder for Agents’ and ‘Request Move-In Date’ have been removed from feature flags.

#### Fix unparsed lead (#13287, #13180) -
Lisa can now parse leads from audreyapts and thresholdagency.



# 5/10/21

## 🚀 Features

#### CVS router logic: Routing agent/boss threads (#13150) -
With the new CVS router, Senior Operators receive all agent and boss threads while they are clocked into CC. When a Senior Op went on break, the CVS router would initially distribute agent and boss threads in a round robin fashion to all Operators.

Now, when a Senior Operator is not clocked in, agent and boss threads will go to the next most senior Operator on shift. This will help prevent sitting threads because most tenured Operators are more comfortable handling agent threads.

#### Add ‘Define {tour type}’ QA (#13121) -
We have [added three new QAs](https://dynastycom.slack.com/archives/CB757SQ4S/p1620248464255100) to help define the various tour types that Lisa is able to schedule with prospects. This will help when prospects ask questions like “what do you mean by virtual tour?”

![Define {tour type} QA](https://user-images.githubusercontent.com/60522124/119831613-737ad380-becb-11eb-83ad-850c9e7aea6c.png)

![Define {tour type} prompts](https://user-images.githubusercontent.com/60522124/119831672-81305900-becb-11eb-909c-b2eee2202073.png)

#### Remove “tours” as prefix to match ‘Define {tour type}’ QA (#13176) -
Operators use the word “tours” to pull up the ‘Tour instructions’ QA, but the new ‘Definite {tour type}’ QAs were also populating the QA bar. To avoid QA collision and maintain efficient Operator workflow, “tours” is no longer a trigger word for the ‘Define {tour type}’ QAs.

#### Allow ‘Agent late’ QA up to one hour after appointment ends (#13161) -
Sometimes, when Lisa asks how a scheduled appointment went, the prospect reports an agent no-show. Previously, Operators could not use the ‘Agent late’ QA because the QA was not available after the appointment end time.

Moving forward, the ‘Agent late’ QA is available on prospect threads until one hour after the appointment has ended. This will help provide firms with accurate agent no-show data and gives Operators a quick answer for prospects.

#### Remove underperforming copies (#13137) -
We had experimental variations on copy and found some to be underperforming. To optimize prospect response rates, we have removed those variations.

The removed copy include:

MTT preference check
- We are offering in-person appointments with an agent. If you'd rather see the place by yourself, we can do self-guided tours. Which do you prefer?
- We are offering virtual and self-guided appointments, which would you prefer?
- Would you like to do a virtual or self-guided tour?
- We are offering in-person and virtual appointments with an agent. But if you want to see the apartment by yourself, we can also do self-guided tours. Which do you prefer?

Move-in date reply
- Thank you very much!

#### Avoid duplicated holidays (#13136) -
It used to be possible for a firm list of holidays to have a duplicate entry. This caused the ‘Holiday office hours’ QA to show the same holiday more than once.

![Holiday QA- duplicate holidays](https://user-images.githubusercontent.com/60522124/119837332-788e5180-bed0-11eb-93de-807056d0864c.png)

This has been fixed and Lisa will automatically remove any duplicate holidays if present.

#### Use last sync of day in ‘Lisa Units’ QA (#12758) -
The ‘Lisa Units’ QA previously only showed one unit sync that happened around 7am local time each day. This proved troublesome because agents will often update availability after that time, ask Lisa what the availability currently looks like, and the QA would no longer be accurate.

Now, the ‘List Units’ QA will pull the most recent sync so that Lisa can pass along the most up to date information.

#### Ask prospect about the number of occupants (#13116) -
We now have a feature flag that allows Lisa to ask how many occupants are planning on living at the property. Many clients have mentioned they would like this information.

When the feature flag is turned on for a firm, Lisa will reach out to a scheduled prospect after checking in on their preferred move-in date. If a preferred move-in date is already present while the flag is on, Lisa will still ask about occupancy. Those questions include:
- And how many people will be living here, not including you?
- And how many people will be living here, excluding you?
- And how many additional occupants will be living with you?
- And how many additional people will be living with you here?

#### Add gif link to ‘No show’ agent QA (#13111) -
‘No show’ from agent threads is a very popular QA. Because AppFolio clients are also able to mark no show directly from guest cards, we now include an informational gif with the ‘No show’ QA. This will help teach agents about the tools available for them and can save Operators a lot of time.

![Mark No Show with gif](https://user-images.githubusercontent.com/60522124/119834279-cfdef280-becd-11eb-80cd-ac538e34de5f.png)

#### Validate Lisa Onboarding Spreadsheet during ingestion (#13068, #13123) -
Spreadsheet data ingestion relies on a perfect Spreadsheet. Swapped columns or invalid inputs could result in incorrect database updates. The ‘Ingest Onboarding Spreadsheet’ admin function in CC will now:
- Validate Spreadsheet shape - check that headers match/are in the correct order
- Validate that all tasks/properties belong to the same firm before making any changes
- Create a list of 'errors' to send back to the user to inform the user of which fields were skipped

#### Update NPS onboarding survey instructions (#13131) -
The NPS survey instructions sent to clients regarding their onboarding experience was previously unclear. To avoid confusion, the survey instructions have been updated from:
On a scale from one to ten, how would you rate your onboarding experience so far?
To:
On a scale from one to ten, would you rate your Lisa onboarding experience so far?

![NPS Onboarding Survey](https://user-images.githubusercontent.com/60522124/119834225-c35a9a00-becd-11eb-9501-c7b9562b134b.png)

## 🐛 Bug Fixes

#### Don’t send calendar updates for old appointments (#13096) -
We recently saw Lisa send calendar update messages to agents for appointments that were multiple days old. This has since been fixed.

#### Fix Dash typo (#13173) -
Dash (our sprint Slackbot) was posting “@undefined” to Slack with some posts because it didn’t know its own Slack username. Moving forward, Dash will post it’s own name correctly as “@dash” when the bot posts about changes it made automatically.

#### Fix unparsed leads (#13171, #13088) -
Lisa can now parse leads from homeisspark and leaselabs.



# 5/3/2021

## 🚀 Features

#### Send a follow-up message if prospect hasn't responded to qualifications (#12942) -
We want to get a response for qualifications from as many scheduled prospects as possible. If a prospect doesn’t respond to the first qualifications message, [Lisa will now follow up](https://dynastycom.slack.com/archives/CB757SQ4S/p1620064912245000) 1 hour later to double check.

![SEND_QUALIFY_FOLLOWUP- Julissa Hernandez](https://user-images.githubusercontent.com/60522124/118875740-6b62d880-b8ba-11eb-997b-54689df567e9.png)

#### Add CC shortcut to navigate to the bottom most thread in inbox (#13057) -
CC now has a [new shortcut](https://dynastycom.slack.com/archives/CB757SQ4S/p1619727131214400). Pressing “b” enables the user to navigate to the bottom-most thread in their current inbox.

#### Fix 'Can’t call' QA language based on the message type (#13052, #13055) -
The [‘Can’t call’ QA](https://dynastycom.slack.com/archives/CB757SQ4S/p1619736010225600) has been updated to account for email threads in CC. The QA previously mentioned “this line,” which felt awkward when a prospect requested a call from an email address. The ‘Can’t call’ QA will now display:

_I can't make or receive calls on my work line, but I'm happy to help via text or email._

#### Allow basic QAs on OTHER threads again (#12973) -
Many QAs were briefly blocked by the Allowed Action Validator on threads set to OTHER in CC. These QAs have been re-allowed because Operators will sometimes still need them. The returning QAs include:
- ‘Request voice call’
- ‘Office phone’
- basic property info QAs like ‘Pets’ or ‘Laundry’

#### Neutral messages for move in acknowledgement (#13004) -
Thanks to [Operator feedback](https://dynastycom.slack.com/archives/CNF5MQ820/p1619447261446600), some changes have been made to how Lisa responds to prospect’s preferred move in dates. The previous responses could be considered insensitive when a prospect shares personal information (such as a divorce or currently being homeless). Lisa’s responses now include:
- Thanks, I've put this in my notes for the leasing team.
- Thank you very much!
- Thank you, I appreciate it!

#### Add new post-shift Operator survey question (#13076) -
We’ve added a question to the post-shift survey when Operators clock-out to help gauge stress levels.

![Post-shift Survey-- stress level](https://user-images.githubusercontent.com/60522124/118875853-8df4f180-b8ba-11eb-99f5-046172670bce.png)


## 🐛 Bug Fixes

#### More accurate ‘Next Showings’ section (#12997) -
The ‘Next Showings’ section on the right hand side of prospect threads in CC was not always showing recently scheduled appointments. Operators could do a manual refresh to load the info, but this slowed their workflow.

Moving forward, Lisa will fetch the appointment data each time a thread is unarchived. This allows the ‘Next Showings’ section to always be up to date.

#### Fix issue of Lisa accepting old appointments (#13058) -
Lisa was recently accepting prospect appointments that had already been offered and rejected by the prospect. This has since been fixed! Lisa will now:
- double check the time of the acceptance message
- ensure it’s not older than the most recent offer message
- verify that the time picked is one of the most recently offered appointment times

#### Faster ‘History’ QA (#12982) -
The ‘History’ QA is now much faster and will no longer time-out when called on an Agent or Boss thread in CC.

#### Do not cancel same-day self-guided appointment (#13053) -
Lisa will no longer automatically cancel same-day self-guided appointments if the prospect hasn’t confirmed.

#### Fix “expired” prospect virtual tour link (#12992) -
The prospect specific virtual tour link recently failed, preventing a scheduled prospect from attending their virtual tour. This has since been fixed so that all generated virtual tour links will connect to the appropriate virtual tour room.

#### Limit the number of messages added to transcript (#13049) -
We now only add the most recent 1,000 messages to each transcript to avoid errors. We previously added all messages from a thread to the transcript. This would cause an error when a thread with a large number of messages exceeded the maximum allowed data size.

#### Improved Agent calendar update (#13060) -
Lisa will no longer miss when an Agent accepts an appointment invite if they accept right away.

We recently saw that Lisa would ignore the accept if it happened at the same time as an event update. This caused the appointment to be offered to the next available agent in line, even though the first agent was planning to attend. Lisa now waits 5 minutes before updating the calendar event to avoid missing those speedy accept actions.

#### Avoid overlapping appointments for agent schedules (#12993) -
We recently had a case where an agent was scheduled for two appointments that overlapped. This has since been fixed so that Lisa will check against both future appointments and those already scheduled within an hour of the check.

#### Only show month and day in the ‘Showing History’ section (#13046) -
There was previously some visual overlap in the ‘Showing History’ section on the right hand side of CC. This primarily occurred around the turn of the year to show dates that are not in the current year. That overlap has been fixed by removing the year display.

Before:

![Date overlap in Showing History](https://user-images.githubusercontent.com/60522124/118879366-a36c1a80-b8be-11eb-9d73-b4c0ce52734f.png)

After:

![Date overlap removed](https://user-images.githubusercontent.com/60522124/118884039-f5636f00-b8c3-11eb-9ce6-626b595e2139.png)

#### Message greeting accounts for empty AGENT/BOSS name field (#13019) -
New messages to agents and bosses in CC will auto-populate a greeting and sign-off. However, Lisa had trouble when there was no name present. Lisa will now auto populate the greeting “Hi there,” when starting a new message on an AGENT/BOSS thread that does not have a name.

#### Fix to CC user creation (#13017) -
Ops Managers were briefly blocked from adding new users to CC. The new CVS router option was set as a required field, but has since been updated to an optional field. This allows new users to be added to CC without an error.

#### Update task address phone number (#13010) -
Each property task page in CC has a field for ‘addressPhone’ which matches one of the property’s tracking numbers for marketing. The old ‘addressPhone’ will now be removed when the corresponding tracking number is updated. This will help avoid having bad phone numbers being associated with CC tasks.

#### Remove unique index on appointment (#13015) -
On rare occasions, a prospect accepting two appointments at the same time will lead to multiple ‘showingContacts’. Because APM removes duplicate appointments based on the appointment start time, we will assign to the same APM appointment to avoid errors.

#### Backfill missing Twilio SIDs and add an alert for them (#12968) -
We want all of our unreleased endpoints to have a Twilio SID (Security Identifier) associated with them in the database. If any of them are missing the SID field, when we try to reconfigure them, the new configuration doesn't actually happen in Twilio. This leads to inconsistencies and possibly missing voice calls and messages to Lisa.

We now have a migration that will backfill all the missing Twilio SIDs. We also have a new alert that will warn us when a recently-created endpoint doesn't have a Twilio SID.

#### Fix  unparsed lead for the Rex Building (#12995) -
Lisa can now parse leads from @olyrex.



# 4/26/21

### 🚀 Features

#### Better trigger for ‘Stop showings’ QA (#12962) -
The ‘Stop showings’ QA would fail if someone fully typed out “stop showings,” but would succeed with “stop showing.” Now the QA will succeed with or without the ending ‘s’ in “showings.”

Before:
![Stop showings- failed](https://user-images.githubusercontent.com/60522124/116486276-36cfa400-a85b-11eb-9376-66a87ba33cf7.gif)

After:
![Stop showings- success](https://user-images.githubusercontent.com/60522124/116486292-418a3900-a85b-11eb-99b3-36f111a34f10.png)

#### Add description to ADP section in UserDetail (#12948) -
The final step to making CC the source of truth has been completed! The ADP section on the admin page now:
- has a description that explains ADP
- explains the nightly sync with ADP
- warns the user about ‘[dead naming](https://en.wikipedia.org/wiki/Deadnaming#:~:text=Deadnaming%20is%20the%20use%20of,binary%20person%20without%20their%20consent.)’
- hides this section when it’s irrelevant

#### Add prospect and agent VT join times to ‘showings.csv’ (#12894) -
The ‘showings.csv’ file in Lisa’s API Docs now includes 2 new columns for virtual tours. These columns include when a prospect first joins and when the agent first joins a virtual tour. This data will help generate deeper insights about how agents and prospects are interacting with the virtual tour process.

### 🐛 Bug Fixes

#### ‘Rent’ QA handle SFR threads with unit type preferences (#12921)
The ‘Rent’ QA recently failed in some instances, even though a rent amount was present for the given property. This might happen when there was a discrepancy between the prospect’s preferred unit type and the actual unit types at the property. Moving forward, the ‘Rent’ QA will be able to successfully pull the rent at single family residences (SFR) no matter the preferred unit type.

#### Improved accept show for self-guided tours (#12933, #12939) -
Moving forward, Lisa will use the appointment from the initial accept message that asks for an ID in the self-guided tour scheduling flow.

When receiving a photo ID for a self-guided tour, there might have been multiple offered appointments. The previous fallback was to check the prospect’s accepted inbound for a showing ID, but in cases of ‘Human Fallback’ that ID isn’t present. We now use the showing ID in the ‘SEND_REQUEST_PHOTO_ID’ message instead.

#### Faster ‘Agent calendar’ QA (#12950) -
We discovered that the ‘Agent calendar’ QA had a very slow load, but we could improve that. By removing the counts in the copy, the QA is roughly 20x faster!

Before:
![Agent Calendar- Before](https://user-images.githubusercontent.com/60522124/116485604-9f1d8600-a859-11eb-8d1a-4fe627ea7944.png)

After:
![Agent Calendar- After](https://user-images.githubusercontent.com/60522124/116485685-cc6a3400-a859-11eb-8e10-fa077a2a6c91.png)

#### Increase speed for Boss/Agent QAs (#12929) -
Some Boss/Agents QAs like ‘Reassign’ have been very slow to load in CC. This was because Lisa was looking at appointment information from the previous 60 days plus all future information. The QA is now faster because Lisa will only be checking the previous 24 hours plus all future information.

#### Fix unparsed lead Respage Chatbot (#12924) -
Lisa can now parse leads from Respage Chatbot.


# 4/19/21

### 🚀 Features

#### ‘Rent’ QA uses unit type preference (#12815) -
The [‘Rent’ QA](https://dynastycom.slack.com/archives/CB757SQ4S/p1618262258028800) now checks both the prospect’s last inbound message and their unit type preference when quoting rent. The QA also looks 60 days out, instead of just 30 days, when pulling rent amounts.

#### Add ‘Spam’ QA (#12905) -
We now have a [‘Spam’ QA](https://dynastycom.slack.com/archives/CB757SQ4S/p1618619119137200) that allows Operators to mark PROSPECT threads as OTHER. This will help improve Operator flow and provide more points of data to the spam detection machine learning processes.

#### Add ‘Get Agent virtual tour link’ QA (#12875) -
We've added a new QA that pulls up an [agent's virtual tour link](https://dynastycom.slack.com/archives/CB757SQ4S/p1618526556107800). When an agent loses track of a given link or asks Lisa to resend their link, we can use this new QA. Because Agent virtual tour links differ from Prospect virtual tour links, this new QA will help keep everything in order.

![Get Agent's Virtual Tour Link QA](https://user-images.githubusercontent.com/60522124/116486332-5cf54400-a85b-11eb-932d-7b3018f93326.gif)

#### Unique Prospect virtual tour link and appointment reminder (#12805) -
On the quest to further decrease prospect no-show rates, each prospect now receives a unique virtual tour link so that we can tell when they’ve joined. Lisa will also send a reminder 3 minutes after the appointment start time if they are not in the virtual tour, but the agent is.

![Send virtual tour reminder](https://user-images.githubusercontent.com/60522124/116486371-78604f00-a85b-11eb-848c-7044546570c0.png)

#### Use the correct agent name and phone number with reminder (#12824) -
We recently began sending reminders to prospects three hours prior to their appointments. However, these showing reminders included individual agent phone numbers rather than the office number or their custom number. Now, Lisa once again recognizes the custom name and phone number field, so the correct contact information is passed on.

#### OTHER-ize spammy threads at any point (#12857) -
We have recently added a new label to Galaxy called ‘MARK_CONVO_TYPE_OTHER’. This will allow Lisa to [mark threads as ‘OTHER’](https://dynastycom.slack.com/archives/CB757SQ4S/p1618619119137200) at any point in the conversation, instead of just on the first inbound.

#### Add property name, where available, to ‘Address’ QA and bot replies (#12814) -
The ‘Address’ QA will now include the property name when one is present. This will help streamline Operator workflow, reduce QA edits, and informs prospects of both the property name and address.

![Address Name with Address QA](https://user-images.githubusercontent.com/60522124/116486400-857d3e00-a85b-11eb-945d-2c9b3a97c428.png)

#### Remove phrase from Lisa’s ‘Ask for move-in date’ question (#12860) -
We’ve found that one of the phrases Lisa uses to ask for a move-in date has a noticeably lower response rate and update rate. As a result, the below phrase has been removed from the rotation:

"Before the appt, we’d love to learn a bit more, when are you looking to move in?"

#### Better explanation for CC showing hour syntax (#12886) -
The showing hour syntax within CC can be confusing when first learning how our system expects to see the hours entered. We now have an improved tooltip message to help when entering showing hours for Lisa.

![Showing hours syntax tooltip](https://user-images.githubusercontent.com/60522124/116486360-6da5ba00-a85b-11eb-95de-a635a5af7ffb.png)

#### APM agents can mark prospect as ‘Did Show’ (#12849) -
APM agents and bosses are now able to update a prospect that was marked ‘No Show’ to ‘Did Show.’ This will also update the prospect appointment status directly in CC from ‘No Show’ to ‘Confirmed’ to ensure consistent data.

#### Assign an agent to Yardi showings during guest card sync (#12831) -
Lisa will now assign an agent to a given showing directly in Yardi during the guest card sync. Previously, “Lisa Dynasty” was the assigned agent in Yardi. This will hopefully be a huge improvement to the agent’s day to day work.

### 🐛 Bug Fixes

#### Don't try to unarchive if it's already unarchived (#12876, #12889) -
We recently had some [phantom timers](https://dynastycom.slack.com/archives/CB757SQ4S/p1618524031095900) that were unarchiving threads, even though the thread was already unarchived. This has since been corrected.

#### No more unexpected reactivation (#12883) -
We experienced a bad combination of feature effects that caused unexpected reactivations. The bot mistakenly thought we were waiting for a qualification, which snoozed the thread and reactivated with a generic message. This has been fixed!

#### Do not wake up old threads to request additional info (#12841) -
Lisa was briefly waking old threads to ask them for their preferred move in date. This has been fixed so that Lisa will only wake a thread when it’s appropriate in the scheduling flow.

#### Fix ‘Rent’ QA to skip floor plans with missing bed counts (#12871) -
Sometimes clients have floorplans in APM with missing bed counts, which appear as “-/-” instead for “1/1”. This used to cause the ‘Rent’ QA to fail because it was expecting to see a number in both fields. Now, the ‘Rent’ QA looks past units with missing bed counts and successfully provides a rent amount when present.

#### Higher rate-limit and “Too many requests” error (#12854) -
The error "Unexpected token I in JSON at position 4" is no more! It was happening because of a rate limit put in place to protect CC crashes. Moving forward, CC users are no longer capped at 10 requests per second in CC. The rate-limit has been increased to 25 requests per second and we now show a helpful error message if that limit is hit.

![Too many request- error message](https://user-images.githubusercontent.com/60522124/116487069-1acd0200-a85d-11eb-93c5-be6ecf11508d.png)

#### Open shift blocks Senior Operators from reassigning (#12842) -
Senior Operators and admins are now able to reassign shifts that have an open slot without being forced to rank that open slot. There is now an “edit” button that opens the reassign modal.

![Reassign edit](https://user-images.githubusercontent.com/60522124/116487081-28828780-a85d-11eb-97fe-b6081d752991.gif)

#### Even better accept show logic (#12828) -
There was a discrepancy in how we identify an accepted appointment on different parts of the code. This caused Lisa to accept a previously offered appointment instead of the most recently offered appointment. This has been fixed by setting the accepted appointment directly in Galaxy logic for single appointments.

#### Improved accepts after receiving a photo ID (#12823) -
Lisa will no longer try to find the accepted appointment information from a ‘RECEIVE_PHOTO_ID’ message. This will help prevent the bot from accepting the wrong appointments with self-guided tours.

#### Fix unparsed lead LZ management (#12856, #12845) -
Lisa can now parse leads from LZ management and village at three fountains.



# 4/12/21

### 🚀 Features

#### Gentler copy for ‘ACK_MOVE_IN_DATE’ (#12762) -
Thanks to Operator feedback, we have updated the copy for when Lisa acknowledges a prospect’s preferred move-in date. We’ve removed the “Got it, thank you!” message and added the following:
- Cool, thanks!
- Awesome, thanks!
- Thank you, I appreciate it!
- Wonderful, thank you!
- Thank you very much!

#### Try new copy for ‘SEND_REQUEST_MOVE_IN_DATE’ (#12756, #12778) -
In an effort to increase response rate, we have a few new phrases for when Lisa asks for a preferred move-in date. We will also log which copy is used for analytics. Those new phrases include:

![New copy for 'SEND_REQUEST_MOVE_IN_DATE’](https://user-images.githubusercontent.com/60522124/114613404-49e64f80-9c71-11eb-86df-41aadadfbe13.png)

#### Add ‘Emergency’ QA as option for Resident threads (#12810) -
The ‘Emergency’ QA has been re-allowed for Resident threads. The QA was briefly blocked due to the recent ‘Allowed Action Validation’ check to avoid downstream alerts and data inconsistencies.

#### Send a reminder 3 hours before a showing (#12752, #12811) -
Lisa will now send a reminder message to confirmed prospects 3 hours before their scheduled appointment. If the prospect is not confirmed, Lisa will retry to confirm them 3 hours before the scheduled appointment. Appointments that are scheduled the same day will not receive this reminder.

#### Add ADP related fields to CC admin page (#12804) -
On the road to making CC the source of truth for user data, ADP related fields have been added to the admin user panel. This will help reduce Slackbot confusion when checking new users and streamline the Operator onboarding process for Ops Managers.

#### Add source + parser for SAS_CALL_CENTER (#12783) -
Lisa now has a new marketing source and can handle leads from notifications@sasdesk. One of our firms hired a 24/7 answering service last year and we built a custom parser for those leads. They are now switching to a new service, “Specialty Answering Service Call Center” (SAS), and would like Lisa to continue handling that traffic.

### 🐛 Bug Fixes

#### Copy edit for ‘ACK_MOVE_IN_DATE’ (#12765) -
Operators caught a small typo in the new, gentler copy for ‘ACK_MOVE_IN_DATE.’ The copy was updated from "hank you very much!" to "Thank you very much!"

#### Unparsed leads from uloop (#12774) -
Lisa can now parse leads from uloop!



# 4/5/21

### 🚀 Features

#### Don't tag ‘Receive Move In Date’ messages with ‘Available On’ (#12748) -
We will now remove the ‘Available On’ tag from any message that is classified with ‘RECEIVE_MOVE_IN_DATE.’ The bot previously tagged about 30% of the ‘RECEIVE_MOVE_IN_DATE’ messages with ‘Available On.’ This is not desirable because in those moments the prospect was not asking about availability, they were responding to our question.

#### Thank the prospect when they tell us their move-in date (#12719) -
Lisa will now automatically send a thank you message to prospects after parsing their preferred move in date. When Lisa cannot parse a move in date, the bot will not respond so that an Operator can take a look and assist further.

#### Detect and mark OTHER on first inbound (#12634) -
Lisa is getting better at detecting spam messages and is now able to mark those threads as OTHER on first inbound. This will help reduce some Operator workload and prevent new guest cards from being opened unnecessarily.

#### Remove copy options from name collection (#12732) -
We previously had several different options for name collection requests. Moving forward, we will only use one phrase:

_“May I have your first and last name for our agents, please?”_

#### Fix “0 Prospects” calendar events (#12739, #12714) -
Calendar invite messages and calendar events that read “0 Prospects” have caused confusion and clutter for agents. Moving forward, we remove an appointment from an agent calendar if there are no prospects with the ‘Accepted’, ‘Confirm Sent’, or ‘Confirmed’ statuses.

#### QA to generate daily.co link (#12700) -
The ‘Create Virtual Link’ Agent/Boss QA will now generate a daily.co link directly. This process will be more efficient for clients because they no longer need to message a separate Lisa email address to generate that link.

![Create Virtual Tour Link](https://user-images.githubusercontent.com/60522124/114107129-59931c00-989e-11eb-9c1f-b694e5c9e655.png)

#### Dash notifies Slack threads when linked issues update (#12649) -
Dash, a Slack bot, is now able to update threads in public Slack channels that contain links to specific GitHub issues. This will help keep everyone informed on any issues they may be tracking.

![Dash update preview](https://user-images.githubusercontent.com/60522124/114107160-70397300-989e-11eb-8506-85b9fec93aef.png)

#### Deprecate ‘Request Move In Date’ feature flag (#12727) -
We now use the ‘SEND_REQUEST_MOVE_IN_DATE’ message with all firms, so the ‘Request Move In Date’ feature flag has been removed as an option.

### 🐛 Bug Fixes

#### Don’t reclassify message with null classification (#12741) -
We recently had a bug where a QA was used, but the action behind the QA did not execute. This has since been fixed.

#### Fix: unparsed leads from hillcrest heights apartments (#12677) -
Lisa can now parse leads from hillcrest heights apartments.

#### Fix: check for null parsed emails (#12707) -
Lisa will now check for emails that parsed as null and can handle accordingly.



# 3/29/21

### 🚀 Features

#### Revert firm name warning (#12551, #12633, #12637) -
In an effort to prevent mentioning the wrong firm name to a prospect, a warning was added to CC to catch when a firm name was used in the message body. Unfortunately this warning was disruptive to Operator workflow in its previous state. The firm name checker has been removed for the time being until we have a better solution.

#### Update to OperatorDraftService (#12631) -
As CC volume increases, we’ve [added additional slots](https://dynastycom.slack.com/archives/CB757SQ4S/p1616618684003500) to some of the busiest Operator shifts. These additional slots are on the M-F 7am and 3pm, Th-F 2am, and Su 10am Lisa shifts (all Pacific time).

#### Update Confirmation Status if ‘No Show’ in APM (#12640, #12657) -
Lisa can now read the status field from an AppFolio guest card to see if a prospect was marked ‘no show’ by an agent. When marked ‘no show’ in AppFolio, CC will update the prospect status to ‘NO_SHOW’ and reach out to the prospect to reschedule. This will hopefully help reduce workflow steps for both agents and Senior Operators.

#### Remove agent calendar link from demerit message (#12650, #12654) -
The agent calendar link has been removed from the ‘Agent Cancellation Demerit’ message to avoid confusion. A line break was also added to help break up the message.
![Send Agent Cancellation Demerit- new line](https://user-images.githubusercontent.com/60522124/113041703-4084bf80-9168-11eb-8d86-9f1407e55d61.png)

#### Feature Flag: Virtual Tour Reminders (#12613) -
Virtual Tour Reminders are now available to all clients. This feature flag can be toggled on if a client chooses and allows Lisa to send a reminder message to agents if a prospect is waiting on them in a virtual tour. The message will send a few minutes after the showing start time if the agent has not yet joined. We hope this reduces no shows for virtual tours.

### 🐛 Bug Fixes

#### Auto-confirm agent when needed (#12659) -
Lisa will now auto-confirm an agent when an appointment is reassigned to them within 3 hours of the given appointment start time. This will help prevent accidental cancellations when a reassign is needed on the day of an appointment.

#### Uninvite agents in ‘Reject’ QAs (#12655) -
Agents were previously getting OFFERED and CONFIRMED on appointments that had been rejected. Moving forward, Lisa will fully uninvite agents from rejected appointments. This will prevent the next calendar sync from updating the agent status back to OFFERED.

#### Don't spam bosses with ‘Outside scheduling’ (#12567) -
Lisa will no longer send the ‘Outside scheduling’ message to bosses or investors listed on a given task. The breakdown for the ‘Outside scheduling’ QA is now:
- When we specify an agent, we only want that message to go to the agent regardless of their work hours.
- When we don't specify an agent, we want that message to go to all of the ‘taskAgents’ that are currently working.
- No matter which QA is used, we don't want to message bosses and investors.

#### Add ‘Allowed Action Validation’ (#12619, #12645, #12653) -
An ‘Allowed Action Validation’ check was recently added to avoid downstream alerts and data inconsistencies. However, this check was blocking some QAs. This has since been fixed so that we can utilize QAs without error.

#### Fix unparsed leads from homeisuniongreen.com (#12609) -
Lisa can now parse leads from homeisuniongreen.



# 3/22/21

### 🚀 Features

#### Add "appointment" common misspellings (#12534) -
We have a ‘common misspellings’ validation in the message field on CC threads that prevents a list of known misspellings. The validator can now catch common misspellings of “appointment.” When present, an error will appear for the Operator to correct before sending.

#### ‘Cross sell search’ annotation (#12545) -
We now have better data tracking for the ‘Cross sell search’ QA. Before, we didn’t have metadata to search and did not distinguish ‘Cross sell search’ from regular ‘Cross sell’. Now we can see the unique QA name, which filters were used, and which tasks were selected.

#### Send agent-specific link in ‘Agent late’ QA (#12531) -
The reminder message an agent receives from the ‘Agent late’ QA will now include their agent-specific virtual tour link. The message previously contained the same virtual tour link that prospects receive. Using the agent-specific link helps us track if and when the agent joins the virtual tour.

### 🐛 Bug Fixes

#### QA note search improvement (#12527) -
The note search function in the QA bar is now case insensitive. This will allow for more accurate search results when checking the QA bar for information that is included in the task notes.

#### Retry saving action (#12354) -
We recently had an issue where some outbound offer messages were not saving all mentioned showing IDs. This prevented the bot and Operators from successfully accepting some appointments because no action was attached. Moving forward, we will retry to save the action so that everything can be executed as expected.

#### Agent calendar: keep showings where agent was once OFFERED (#12510) -
The agent calendar modal will now display showings that were previously OFFERED, even if the agent is no longer available.

![Agent Calendar- keep showings once OFFERED](https://user-images.githubusercontent.com/60522124/112226412-855aa480-8c04-11eb-904f-f04cbda738dd.png)

#### Agent calendar: fix label overlaps (#12555) -
The agent calendar previously had some overlapping labels, which have now been fixed! The work hour labels have been removed and if a FREE and BUSY label would overlap, we hide the FREE label.

Before:
![Agent Calendar with overlap](https://user-images.githubusercontent.com/60522124/112226447-92779380-8c04-11eb-8789-aa40545b3d9b.png)

After:
![Agent Calendar without overlap](https://user-images.githubusercontent.com/60522124/112226522-9d322880-8c04-11eb-8c4a-5e4abb8ad98f.png)

#### Fix parsing for ABODO leads from guestcards@appfolio (#12538) -
ABODO parsing has been restored for leads that come in via AppFolio guest cards. Testing for ILS leads has also been fixed, making the parser tests more robust.



# 3/15/21

### 🚀 Features

#### Allow all 3 tour types and shorten copy (#12503) -
Lisa can now support all 3 tour types for a single property task. The copy has also been adjusted to keep the tour type inquiry messages short.

_"We are offering in-person and virtual appointments with an agent. But if you'd rather view the place by yourself, we can do self-guided tours. Which do you prefer?"_

#### Filter out users from Fullstory recording to save sessions (#12483) -
CC is currently on a FullStory Free plan, which limits the number of sessions that can be recorded each month. For the time being, we will only record the following user types to save on those sessions: Operators, Senior Operators, Team Leads, and Ops Managers.

### 🐛 Bug Fixes

#### Do not ask for move-in date in the middle of confirmation flow (#12465) -
We recently had a problem where Lisa would ask for a preferred move in date at an awkward time. When REQUEST_MOVE_IN_DATE was sent shortly after SEND_CONFIRM_SHOW, prospects might not answer both questions. This led to some appointments being cancelled by mistake. Moving forward, we make sure the appointments are either ACCEPTED or CONFIRMED before asking for the move-in date.

#### Sort ‘Next Showings’ section by showing start time (#12463) -
The previous fix (#12384) that sorts showings by their start time was a backend fix that did not actually correct the view in CC. Moving forward, CC will display showings in the correct order based on their date and showing start time.

#### Save excluded units in PM property (#12501) -
If a unit is marked ‘excluded’ in a PM software, we will now save them as a non-rentable unit so that Lisa can still reference them. This will help Lisa more consistently provide the unit number or address when confirming self-guided tours.

#### Override skip inbox if the classification is HUMAN_FALLBACK (#12497) -
If a message is classified as HUMAN_FALLBACK, it will consistently be unarchived. This is a band-aid for now while more thorough diagnostics are in the works.

#### Set linkified text to stay visible once rendered (#12485) -
Linkified text recently caused CC inboxes to spasm when it scrolled in or out of view. That has been corrected so that the linkified text will stay rendered and not cause the inbox to spasm.

#### Updated demerit message calendar url (#12484) -
There was recently a period at the end of the calendar url we sent to agents in the demerit message. That caused an error when clicking the url link. Moving forward, the demerit message no longer has the period after the url and instead has a line break for easier reading.

#### Fix agent calendar widget problems (#12355) -
The agent calendar modal has been updated to be visually cleaner and will more consistently include address information.
- We used to show all appointments an agent was available for, even if they had not been offered the appointments, which led to some confusion. Now we only show appointments that they are available for and have been offered or accepted.
- We also used to pull Google Street View addresses in the address section of the agent calendar widget. This was incorrect because it could sometimes be just longitude and latitude numbers. Now we use the task address to make sure we show a proper address.

#### Fix unparsed leads from homeissomoflats (#12489) -
Lisa can now parse leads from “@homeissomoflats”.



# 3/8/21

### 🚀 Features

#### Enable Fullstory tracking for all CC users (#12447) -
In an effort to diagnose and triage issues, we have [launched FullStory](https://dynastycom.slack.com/archives/CB757SQ4S/p1614974859087100) for all users in CC. FullStory is a tool that will allow us to visualize recordings of actions in CC so that we can better understand how Operators interact with the UI. This will also help our Product and Engineering teams understand where they can focus their work to make the biggest impact.

#### FullStory permissions modal for clipboard access (#12388) -
Copy/paste tracking has been added to FullStory within CC to help provide further insights. The first use of copy, cut, or paste events when CC is open will cause a modal to pop up asking for permission.
![FullStory clipboard permission modal](https://user-images.githubusercontent.com/60522124/110534839-8a403400-80ed-11eb-8ce5-5b002ac3df09.gif)

#### Add ‘Schools nearby’ QA (#12441) -
[The ‘Schools nearby’ QA](https://dynastycom.slack.com/archives/CB757SQ4S/p1614977427091800) has been added so that Lisa and Operators can provide a consistent and helpful answer that doesn’t steer prospects. The QA will redirect “school” questions to the agent at their appointment. If there’s a task address, we provide a Google Maps link.

![Schools nearby QA](https://user-images.githubusercontent.com/60522124/110535331-21a58700-80ee-11eb-8ef7-080c3c62080c.gif)

#### Ask prospect for move in date (#12343) -
Lisa will now ask the prospect for their [move-in date](https://dynastycom.slack.com/archives/CB757SQ4S/p1614725517049700) after they've qualified (if she doesn't have it already). For now, we have turned this feature on for just a handful of clients and hope it will be a huge value add for agents.

#### Move-in date parsing version 1 (#12446) -
Lisa can now parse some of the [move-in date responses](https://dynastycom.slack.com/archives/CB757SQ4S/p1614986731107400?thread_ts=1614725517.049700&cid=CB757SQ4S) from prospects. Once parsed, Lisa will automatically update the prospect’s preferred move in date.

#### Shorten ‘Request Move In Date’ copy (#12416) -
The previous ‘Request Move In Date’ copy has been refined to be short and sweet.

Before:

"Before the appointment, our agents would love to learn a bit more about your rental needs so they can best help you. What date are you looking to move in by?"

After:

"Before the appointment, we’d love to learn a bit more about your needs. When are you looking to move in?"

#### Smartrent copy changes (#12435) -
Smartrent self-guided tours do not use lockboxes, so the copy has been updated to not mention “lockbox code” or “key” for Smartrent users.

#### Reminder to agents before self-guided showings (#12430) -
Some clients wanted to have their team know when self-guided showings will be happening on their properties. Lisa can now send a reminder before all self-guided tours to task agents if the feature is enabled for their firm.

![Agent reminder for self-guided](https://user-images.githubusercontent.com/60522124/110539005-b3af8e80-80f2-11eb-8344-c04ab9402757.png)

#### Assign agent to self-guided showing (#12418) -
We previously did not assign an agent to a self-guided tour, but this proved troublesome for at least one stand alone client. For RealPage clients, Lisa will now assign the first agent in the showing order to self-guided tours. This will allow agents to more easily follow up with scheduled prospects.

#### Agent choice for self-guided meeting (#12411) -
When assigning agents to self-guided tours, Lisa will pick the first agent in the showing order that is working at the time of the appointment. If no agent is working, Lisa will default to the first agent in the showing order.

#### Add "Leave Call" button for Daily.co tours (#12433) -
Both agents and prospects now have a “Leave Call” button when connected to a Daily.co virtual tour. Previously only the prospect’s virtual tour link had this button, but now the button is present for agents as well.

#### Copy edit to Self Scheduled showing reply (#12424) -
One of Lisa's automated replies to a Self Scheduled showing message was missing a grammatical element, making it feel clunky/robotic.

Before:

"Hi there! Showing is not available for the time you requested..."

After:

"Hi there! An appointment is not available for the time you requested...".

#### Close out "OTHER" guestcards in ResMan (#12313) -
We are working toward closing guest cards in PM software if their connected CC thread is marked OTHER. This feature is now live for RealPage, Yardi, and ResMan.

#### Store Daily.co tour join events (#12374) -
We now keep a record of when an agent joins a Daily.co virtual tour in an effort to reduce virtual tour no shows. This data will give clients a sense of when their agents actually joined the tour, how long the tours tend to run, and will help with future efforts as well.


### 🐛 Bug Fixes

#### Fix ‘Snooze’ QA (#12404) -
The ‘Snooze’ QA recently had some trouble and would not remove the current thread from the inbox when used. This has been fixed so that the QA can snooze the current thread and reactivate after the number of days entered.

#### Fix ‘Fire agent’ status (#12402) -
The ‘Fire agent’ QA had a bug where it would immediately re-offer a cancelled appointment back to the fired agent. This has been fixed so that fired agents remain removed from their previously scheduled appointments.



# 3/1/21

### 🚀 Features

#### Self-guided meeting directions field (#12332) -
We now have a separate field for Self-guided meeting directions! This will help properties that have different instructions based on the scheduled tour type.

![Self-guided Meeting Directions](https://user-images.githubusercontent.com/60522124/109727014-a12cd680-7b81-11eb-9eaf-06b4184b0f71.png)

#### Enable chat for Daily.co virtual tours (#12353) -
Daily.co virtual tours that Lisa generates now have text chat available if needed.

#### Start agent calendar on Sunday (#12327) -
Agent calendars pulled from the ‘Calendar’ QA used to start on Monday. Now Agent calendars start on Sunday.

Before:

![Agent calendar Mon](https://user-images.githubusercontent.com/60522124/109727086-bdc90e80-7b81-11eb-892e-d10c832af1ee.png)

After:

![Agent calendar Sun](https://user-images.githubusercontent.com/60522124/109727115-c9b4d080-7b81-11eb-9743-20d4594422cb.png)

#### CC lazy load notes, showing sections (#12326, #12328) -
To help make CC a bit faster, the notes and showings sections on task pages will not load until you scroll over them. This reduces the volume of data getting loaded until it is actually needed.


### 🐛 Bug Fixes

#### Show SEND_FAILED messages to users of any type (#12377) -
Not all Operators were seeing messages that failed to send. Moving forward, everyone will see the same messages in each thread.

#### Make CC showings sorted by timeStart (#12384) -
Sometimes offered showings would not show in [chronological order](https://dynastycom.slack.com/archives/CNF5MQ820/p1614364374080300) on the right side of a thread. Moving forward, showings should always be sorted by their start time.

#### Do not repeat prospect names (#12346) -
If a single prospect is cancelled on multiple times, their name will only be listed once in the agent calendar invite message.

Error:

![Do no repeat prospect name](https://user-images.githubusercontent.com/60522124/109729303-a7bd4d00-7b85-11eb-9938-aaf414dd2152.png)

Corrected:

![Tell agent about prospect cancellations](https://user-images.githubusercontent.com/60522124/109729194-80ff1680-7b85-11eb-8386-097ce19f8d4e.png)

#### Better bot behavior when cancelled on 2+ times (#12350) -
We no longer allow the bot to re-offer showings if the prospect has been cancelled on 2+ times. Operators can still offer any showing to the prospect other than the ones where they were cancelled on.

#### Set Agents to cancel by them when appropriate (#12302) -
We had a bug where an agent could hit “Decline” on a calendar invite from Lisa, but their status would not update in CC. This has since been fixed so that Lisa recognizes when an agent is no longer an attendee for a scheduled calendar event.

#### Fix sms/email icons in transcript (#12321) -
We recently had no SMS/Email icons in the "related threads" section of transcripts. This has been updated so that the visual icon is present:

![SMS:Email icon in transcript](https://user-images.githubusercontent.com/60522124/109729389-ca4f6600-7b85-11eb-8316-895cac634d3b.png)



# 2/22/21

### 🚀 Features

#### Improving multiple accept parsing (#12272) -
When the bot accepts a showing, sometimes the parsing fails to extract which showing the prospect wants. If the showing parser fails now, the message will be classified as Human_Fallback. This will allow an Operator to take a look without worry of the bot accepting the wrong showing.

#### New attempt to disable autocomplete in QA input (#12276) -
[Operators reported](https://dynastycom.slack.com/archives/CNF5MQ820/p1613521400234500) persistent Chrome autocomplete behavior in the QA input bar, which decreases speed. Moving forward, this should be fixed as autocomplete has been turned off in CC.

#### Add ‘Cancel prospect’ QA  (#12256) -
[The ‘Cancel {prospect}’ Agent QA](https://dynastycom.slack.com/archives/CB757SQ4S/p1613671651155400) has been added for Operators to cancel an appointment at an agent’s request. This QA will keep Lisa from rescheduling and will instruct the agent on how to make the same changes in AppFolio. The QA can be used with a prospect's name, phone number, or email address.

![Cancel {prospect} QA](https://user-images.githubusercontent.com/60522124/108905479-c2278180-75ed-11eb-87f3-f4a7795d5664.png)

The message to the prospect will also look like:

![Send_Cancel_Show](https://user-images.githubusercontent.com/60522124/108905533-cf447080-75ed-11eb-8efe-43ef1a1ed5ae.png)

#### Improvement to ‘Cancel prospect’ QA (#12298) -
We want to avoid cancelling a prospect's showings at unwanted properties. Now, the ‘Cancel prospect’ QA options also include a property name.

![Cancel prospect with property names](https://user-images.githubusercontent.com/60522124/108905611-eaaf7b80-75ed-11eb-91e2-4dc9a5eb3da5.png)

#### Tell agents which prospects have been cancelled on (#12303) -
We now alert agents if a prospect had a scheduled appointment that was cancelled within the last 5 days. This will be in the calendar invite message as an additional sentence. It will outline all the prospects on the showing that have been cancelled at least once already.

![Tell agent about recent cancellations](https://user-images.githubusercontent.com/60522124/108905669-fa2ec480-75ed-11eb-9b4d-eabd12a49a75.png)

#### ‘Outside scheduling’ only for specific property (#12296) -
To prevent weird side effects of using the ‘Outside scheduling’ QA, we now offer a selection of prospect+property pairs in the QA dropdown.

![Outside scheduling QA](https://user-images.githubusercontent.com/60522124/108905786-20ecfb00-75ee-11eb-982a-0cdc9f73030f.png)

Upon selection, only the showings scheduled at the specified property will be cancelled.

![Outside scheduling QA text](https://user-images.githubusercontent.com/60522124/108905820-2ba79000-75ee-11eb-81b2-a9da4194b766.png)

#### Close out "OTHER" guest cards in Yardi (#12243, #12297) -
We are working toward closing guest cards in PM software if their connected CC thread is marked OTHER. This will help reduce spam for both Operators and clients. So far, this feature is live for both RealPage and Yardi.

#### Save closed guest card info (#12277) -
We are now able to save the result of closing out an OTHER-ized guest card. This will allow us to measure the success and impact of this action for better analysis.

#### Copy edit within the ‘Office phone’ QA (#12288) -
The phrase “Give us a ring: phone number.” has been removed as a template option from the ‘Office phone’ QA. This decision was based on Feedback from our [#lisa-solving Slack channel.](https://dynastycom.slack.com/archives/CNF5MQ820/p1613587309241500)

#### Delete SmartRent code when necessary (#12264) -
AIR has expressed interest in a pilot for self-guided tours backed by SmartRent. SmartRent's locks generate new door codes automatically, which is different from the physical lockbox system used in our current self-guided tours.

Since each tour gets a fresh code, we need a way to clear codes when tours get cancelled.
Therefore, we will delete access codes when:
- a self-guided showing is cancelled for any reason (by agent, by prospect, etc.)
- the showing is over

#### Update auto-fill signature (#12279) -
The auto-fill greeting and signature feature has been a great hit! However, some of the randomized signature options did not always fit the same tone as the message itself. The randomized options have been reduced to include “Best”, “Sincerely”, and “Respectfully”.

#### Turn on Daily.co for some statewide properties (#12273) -
Daily.co has been turned on for 5 new properties from Statewide!

### 🐛 Bug Fixes

#### Fix for csv API timeout issue (#12290) -
Our CSV API feed was down for AIR recently because the API took too long. After fixing two major problems in the code, the csv API response time has been cut down tremendously and will not time out as easily.

#### Fix unparsed leads (#12301) -
Lisa can now parse leads from homeisverge.com.



# 2/16/21

### 🚀 Features

#### Add autofill for Boss and Agent threads (#12211, #12247) -
To help speed up typing messages to Agents/Bosses, those threads will now [auto fill a greeting and sign-off](https://dynastycom.slack.com/archives/CB757SQ4S/p1612916445110500). The cursor will also be placed in the middle of the message to allow for immediate typing.
![Agent:Boss auto-fill](https://user-images.githubusercontent.com/60522124/108123179-74ea6380-7073-11eb-87d1-fa1c2501b4bf.gif)

#### Change copy for Cancel by Agent action (#12237) -
The message that goes out when an APM agent cancels an appointment directly from APM has been updated to:
“Hi there, I was asked to cancel your upcoming showing at  property name  by a member of our leasing team. If this does not sound correct or if you have any questions, please call our office directly at  office phone.”

#### Decrease SMS timeouts (#12181) -
We had a problem where we would receive timeout errors from Twilio for handling SMS messages too long. Moving forward, we will reduce the time between receiving and handling of SMS messages. This will allow us to return an OK response to Twilio a lot faster and decrease timeouts.

#### Turn on MassCancellationValidation (#12225) -
The Mass Cancellation Validator is officially in place to help prevent unintended cancellations. Ops Managers and Team Leads are able to re-run cancellations if they were intentional.

#### Refactor DailyClient and turn on for test property (#12254, #12259) -
We will be testing Daily.co with at least one of our properties to ensure that it is a suitable alternative to Whereby.

### 🐛 Bug Fixes

#### Restore favicon (#12233) -
The favicon for CC was recently missing, but has been fixed!

Before:

![Dynasty favicon missing](https://user-images.githubusercontent.com/60522124/108124877-af550000-7075-11eb-954c-2446da8ada4e.png)

After:

![Dynasty favicon](https://user-images.githubusercontent.com/60522124/108123220-82075280-7073-11eb-86d5-2dcb6d639850.png)



# 2/8/21

### 🚀 Features

#### Alleviate showing blockage (#12188) -
SFRs and small MFs have a limit of 5 "active" showings. This caused some blockage when we allowed the [‘Showing Hours’ QA](https://dynastycom.slack.com/archives/CB757SQ4S/p1611950049075400) to offer 5 showings at once. To alleviate this blockage, we now sum up the expected number of accepts instead of counting any showing that was offered recently as "active".

#### Hide photo ID for any firm that has chosen this option (#12184) -
We can now hide the images from ‘RECEIVE_PHOTO_ID’ messages for certain firms. If the firm opts into hiding photos, the transcripts will show 'photo ID' for messages classified as ‘RECEIVE_PHOTO_ID’.
![TRANSCRIPT_HIDE_PHOTO_ID](https://user-images.githubusercontent.com/60522124/107310576-b15a0600-6a5a-11eb-8205-f85e24a59cb1.png)

#### Use shorter agent name in ‘Prospect no show’ message (#12185) -
The ‘Prospect no show’ message will now only use an agent’s first name and the first initial of their last name.
![Prospect No Show Message](https://user-images.githubusercontent.com/60522124/107310595-bd45c800-6a5a-11eb-8e40-d35ea39a18aa.png)

#### Use Daily.co to host virtual tours (#12195) -
We are now able to use Daily.co as an alternative to Whereby for virtual tours. There will be a slow rollout with client permission to ensure that everything works as expected.

#### Send calculated number of rentable units to APM (#12162) -
We now send the calculated number of rentable units to APM in a different way. This will help get the billing unit count in APM in a more timely and accurate manner for clients.

### 🐛 Bug Fixes

#### Safer Cross Sell search (#12143) -
The ‘Cross sell search’ QA recently sent an accidental message to the wrong thread in addition to the correct thread. Moving forward, if a modal is open and the thread changes, we cancel the modal to avoid accidental messages.

#### Remove timezone from agent schedules (#12178) -
We used to use the time zone from an agent’s calendar to calculate availability. This led to some confusion when trying to schedule one agent across multiple time zones. Now, we use the timezone of the task itself to calculate whether or not an agent is available for a showing.

#### Do not retry calendar sync more than is needed (#12135) -
We had an issue where Lisa would reoffer a showing after an agent had already accepted. This has since been fixed so that Lisa is able to properly fetch calendar data and act accordingly.

#### Unarchive threads if stale ‘not sent’ message (#12129) -
We had some instances where a confirmation message might fail to send, so the prospect would not be prompted to confirm a showing. Now, messages that don’t send because they are stale will unarchive for a human to jump in and assist.

#### Improved Agent reminder timing (#12139) -
When an agent confirmed a showing within 1 hour, the ‘SEND_AGENT_SHOW_REMINDER’ message failed to send. Moving forward, if an agent confirms an appointment within an hour of the start time, they will receive the showing reminder message immediately.

#### Fix Message Failed to send errors (#12204, #12205) -
Some Agent messages were failing to send recently due to some EWS ID translation errors. This has since been resolved so that outbound messages are delivered successfully.



# 2/1/21

### 🚀 Features

#### Login wave for CC (#12118) -
[The CC login page](https://dynastycom.slack.com/archives/CB757SQ4S/p1611859712068900) now has a new look.
![CC Login Wave](https://user-images.githubusercontent.com/60522124/107093362-1996b680-67d3-11eb-9902-60ffa1c4067f.gif)

#### Change copy for cancel by agent message (#12114) -
APM agents can now cancel Lisa scheduled appointments directly from APM. The copy was a bit blunt at first, but will now read:
“Unfortunately, we had to cancel your upcoming showing at Property. If you have any questions or would like to speak with our team, please call our office directly at Phone Number.”

#### "Dynasty" » "Lisa" logo (with Easter egg) (#12109) -
[The word “Dynasty”](https://dynastycom.slack.com/archives/CB757SQ4S/p1611772113051400) in the top left of CC has been updated to “Lisa” with the Lisa logo. Clicking the Lisa logo redirects to the CC Lobby/Home screen and animates the logo.
![Lisa Logo in CC](https://user-images.githubusercontent.com/60522124/107096556-0a1a6c00-67d9-11eb-82d8-8b825927b32e.gif)

#### Improved Send_Agent_Cancellation_Demerit (#12042) -
Lisa’s message to an agent regarding a prospect that has been cancelled on too many times has been improved. We now tell an agent when the last cancellation was and add a calendar link for reference.
![Improved Send_Agent_Cancellation_Demerit](https://user-images.githubusercontent.com/60522124/107096581-16062e00-67d9-11eb-9945-ae5ef9d7a5f1.png)

#### Self guided copy change (#12071) -
When we asked plainly for ID, we were getting non government issued IDs. Now, we will specifically ask for a government issued ID for self-guided tours.

![Send_Request_Photo_ID](https://user-images.githubusercontent.com/60522124/107096631-2e764880-67d9-11eb-9fc3-ba7a4b10796a.png)

### 🐛 Bug Fixes

#### Parse Appfolio original sender for received emails (#12117) -
We had a problem where new messages from some prospects would come in on new threads. We discovered that the APM email app generates a new random email address every time a prospect replies to our message. Lisa can now parse the original sender in those instances and update their primary thread in CC to keep the conversation in one place.

#### Allow ops to re-offer appointments again (#12107) -
A recent bug prevented Operators from re-offering a previously offered appointment to prospects. This has since been fixed so that operators have the ability to re-offer the same slot if the prospect changes their mind.

#### Show QA error for block calendar (#12120) -
There is now a QA error when trying to use the ‘Block calendar’ QA on an Agent thread that has no email address connected.
![No email address QA error](https://user-images.githubusercontent.com/60522124/107096875-af354480-67d9-11eb-9f67-e1e027c732cf.png)

#### Smoother Outside scheduling on agent threads (#12106) -
Using the ‘Outside scheduling’ QA from an agent thread will no longer bump to the next message in the inbox when sent.

#### Remove previously cancelled showings from offerable slots (#12065) -
When a prospect was cancelled on 2+ times, we used to only allow operators to offer them one of the cancelled showings. Now, we do the exact opposite. We allow operators to offer any showing to the prospect other than the ones where they were cancelled on.

#### QA error: this agent isn't on any tasks (#12080) -
We now have a QA error when trying to get the calendar link for an agent who is not listed on any tasks.
![Calendar QA Error Message](https://user-images.githubusercontent.com/60522124/107096914-bceaca00-67d9-11eb-972d-4974c6178da1.png)



# 1/25/21

### 🚀 Features

#### Don’t auto-select tooltip (#12056) -
The tooltip text when hovering over a property or firm name is no longer selectable. This will help reduce slowdown when copy/pasting property names for prospect and agent messages.

Before:

![Tooltip Before- Selectable](https://user-images.githubusercontent.com/60522124/107081282-750b7900-67c0-11eb-9b7a-68f5d7687fdd.png)

After:

![Tooltip After- Not Selectable](https://user-images.githubusercontent.com/60522124/107081298-79d02d00-67c0-11eb-8f6b-83200524b850.png)

#### Copy change when offering multiple tour types (#12052) -
We will now use 'or' instead of 'in addition' when offering multiple tour types. This will help reduce confusion around prospects thinking that they are expected to accept both types of showings.

#### Improve copy when tour type is uncertain (#12048) -
Lisa previously defaulted to in-person tour language when tour type preference was uncertain. Moving forward, we will use “during an appointment" if a preferred tour type has not yet been chosen.

#### Update mass cancellation message (#12015) -
The retry failed messages feature can also account for mass cancellations.
![Mass Cancellation Retry message](https://user-images.githubusercontent.com/60522124/107089985-8444f380-67cd-11eb-9df9-464eceb68523.png)

#### APM Sync alway take advertised rent (#12039) -
There are cases where a unit is set to not use Market Rent and their Advertised Rent is zero. In those cases, we will now use their zero Advertised Rent to help Lisa focus on units that are market ready.

### 🐛 Bug Fixes

#### Check only outbounds when reacting to cancellations (#12025) -
We had an issue where we wouldn't react to a cancellation loop when a prospect messaged in immediately after a cancellation message. The bot now checks if the most recent outbound was a cancellation message and will send an apology message when needed.

#### Fix unparsed lead from squarespace (#12020) -
Lisa can now automatically parse leads from squarespace.

#### Improved APM guest card parsing (#12037) -
Kading Properties recently had a firm sync error due to an issue with a guest card sync. This has since been resolved so that the firm data can sync as expected.



# 1/19/21

### 🚀 Features

#### Include Service Animals Policy with Pet Policy (#11988) -
Fair Housing laws require property owners to provide reasonable exceptions for service animals. The text of the ‘Service Animal’ QA will now be included with all ‘Pet Policy’ QAs to ensure all prospects receive this important information:

“We do accept reasonable requests for service animals: just mention it on your application. Our team approves requests that follow Fair Housing laws.”

#### Add “tomorrow” to confirm message (#11977) -
The word “tomorrow” has been added to confirmation messages now that we are sending those messages the night before for everyone.

#### Improved ‘Yes Qualify’ message (#12005) -
The phrase “thank you for confirming” has been removed from the ‘Yes Qualify’ message. This will remove confusion around ‘confirming’ before Lisa’s actual appointment confirmation messages.

#### Include unit number in lockbox message (#11969) -
When Lisa confirms an appointment for APM clients, she pulls in the property address and the unit information. We now add the unit number to the confirmation message on self-guided tours when the task is MFR.

#### Hide Kading address in confirm_show (#12004) -
Kading Properties has a separate address per unit, which caused some confusion around Self-Guided tours. Confirmation messages for Kading properties will now only contain the unit address. This will remove the confusion around double addresses.

#### Improve retry messages that fail validations clarity (#11970) -
The [Retry Failed Messages](https://dynastycom.slack.com/archives/CB757SQ4S/p1610595768012200) feature has been improved! It used to be unclear about why the message did not send, who could resend, and what the downstream actions might be. The modal now explains why the message failed, who can fix it, and what actions will be carried out when resent.
![demo-2-tcpa-retry](https://user-images.githubusercontent.com/60522124/107081004-08907a00-67c0-11eb-9437-f572ce02ccc1.png)

#### New marketing source (#11973) -
Lisa can now generate marketing phone numbers for ALN_APARTMENT_DATA.

### 🐛 Bug Fixes

#### Update prospect status when agent is late (#11987) -
The prospect confirmation status will now update to ‘Cancel_Us’ after using the ‘Agent Late’ QA. This means that Lisa will not automatically follow up to ask about an appointment that did not happen.

#### Aimco to AIR email change for calendar (#11986) -
AIR agent calendar events were not reliably updating to match their responses to appointment invites. This has since been fixed by allowing Lisa to recognize the agent’s Aimco and AIR email addresses.

#### Fix cross sell search failure (#11975) -
The ‘Cross sell search’ QA was throwing an error when used. This has since been fixed.



# 1/11/21

### 🚀 Features

#### Changes to prospect confirmation flow (#11868) -
[All confirmation messages](https://dynastycom.slack.com/archives/CB757SQ4S/p1609890949321800) will now be sent the night before to check in with scheduled prospects. We are hoping this will give prospects more time to see the text and confirm, leading to fewer cancellations and happier agents. If they schedule a showing the same day or the night before, then we will auto-confirm.

#### Cancelled too often time frame reduced (#11926) -
Lisa will now only look at the past two months to count for ‘cancelled too often’. This will help in cases where a prospect messages back in after many months, but their first interaction resulted in ‘cancelled too often’.

#### Improved message when asking an agent to save a showing (#11925) -
The message to agents about prospects who have been ‘cancelled too often’ now includes a date and time. This will be the date and time of the most recently declined showing to help agents identify calendar conflicts.
![SEND_AGENT_CANCELLATION_DEMERIT](https://user-images.githubusercontent.com/60522124/104373421-8ed1dd00-54ee-11eb-9840-f2c773ed2487.png)

#### Allow Ops leaders to reassign Operator shifts (#11963) -
Ops leaders (including Ops Managers, Team Leads, and Senior Operators) can now reassign shifts on behalf of Operators when needed. This will help prevent auto-clock out in cases of emergency coverage.

### 🐛 Bug Fixes

#### Fix to ‘Prospect Outside scheduling’ (#11946) -
‘Outside scheduling’ from a Prospect thread would sometimes fail to send. This would occur when an Agent had SMS as their preferred contact, but the ‘Outside scheduling’ QA was attempting to add an email subject. That has since been fixed, so that the QA works with both email and SMS preferred Agent threads.
![Prospect Outside Scheduling](https://user-images.githubusercontent.com/60522124/104373955-26373000-54ef-11eb-89a8-1f3af69da9db.gif)

#### Fix multiple cancellation apology randomly sending (#11944) -
We were sending an apology message to prospects when we shouldn’t have, but this has since been corrected. This occurred when a Prospect reached 2+ cancellations, but received a new offer instead of the apology message. Lisa will no longer react to the cancellation loop if the cancellation messages weren't recent.

#### Handle delay show plan (#11913) -
We have a new `HANDLE_DELAY_SHOW` plan that will make the Delay Show process more consistent. This plan makes sure that Lisa is able to offer an appointment when one comes available after a Prospect has been delayed. However, we won’t offer an appointment if the Prospect has been cancelled 2+ times.

#### Bring abodoapts parser back (#11919) -
Turns out we're still receiving ILS emails from both abodoapts and Abodo. Lisa can once again parse these sources.



# 1/4/21

## TLDR: Block Calendar Update, New Holidays Section, and Several Copy Edits

### 🚀 Features

#### ‘Block calendar’ QA for a date range (#11911) -
The [‘Block calendar’ QA](https://dynastycom.slack.com/archives/CB757SQ4S/p1609464347291700) can now block a range of dates on an Agent’s calendar.
![Block cal with range of dates](https://user-images.githubusercontent.com/60522124/103845864-f1e4ef00-506a-11eb-8a57-e47efca53052.png)

#### Improved offer show slots (#11839) -
Lisa can no longer automatically re-offer the same showing to a prospect if they mention they won’t attend. The ‘Offer show’ QA can still be used manually if the prospect changes their mind.

#### Add ‘Holidays’ section in property task page (#11881) -
There is now a section for Holidays on the right hand side of property task pages. This will help identify which Holidays Blocks are already in place at a given property.
![Holiday Section-Ashford Bayshore](https://user-images.githubusercontent.com/60522124/103844482-fa87f600-5067-11eb-9d9f-646f0bbad17c.png)

#### Copy edit to ‘Request Voice Call’ message (#11899) -
The ‘Request Voice Call’ message will now ask for a first and last name if the thread’s name field is not already filled in. The message previously only asked “may I have your name”.

#### Record and display Stop/Start Bot actions (#11910) -
There is now a display of when the Bot is Stopped or Started on a thread, similar to ‘Archive/Snooze display. This will help with troubleshooting and audit purposes in the future.
![Record of Stop:Start Bot](https://user-images.githubusercontent.com/60522124/103844529-0d9ac600-5068-11eb-9708-0f9c9dd046d9.png)

#### Copy edit to ‘Fix PM’ text (#11852) -
The ‘Fix PM… rent price’ QA for APM clients used to say “‘market rent’ date” instead of “data.” It has been updated to read:
“OK, got it. If we can update the ‘market rent’ in AppFolio, we’ll be all set.”

#### Copy edit to an Agent reminder message (#11905) -
The message for SEND_NO_TENTATIVE_RESPONSE_TO_SHOWING has been updated to be a bit softer. It will now look like:
![SEND_NOT_TENTATIVE_RESPONSE_TO_SHOWING](https://user-images.githubusercontent.com/60522124/103844386-c7ddfd80-5067-11eb-95bd-fe5c1c46f82f.png)

#### Admin job to rerun bot actions (#11873) -
CC now has an admin job that can rerun bot actions in bulk that failed validation.
![Rerun bot actions](https://user-images.githubusercontent.com/60522124/103844588-2c00c180-5068-11eb-9ad5-50a759c4d55c.png)

### 🐛 Bug Fixes

#### Temporarily hide background of login page (#11862) -
There were recently some odd/inappropriate images in the CC login page background. While this is getting looked into, background images have been temporarily removed.

#### Fix Prospect showing reminder send (#11906) -
The reminder message that states “Your virtual tour will be starting in 5 minutes!” will no longer send if the appointment start time has already passed.

#### Small permissions updates (#11854, #11878) -
Team leads are able to do bulk updates again and Senior Operators can now see task Action Logs.

#### Fix ‘Stop showing’ QA list (#11903) -
The ‘Stop showing’ QA has been fixed to consistently show a full list of available units.

#### Fix ‘Fire agent’ QA list (#11900) -
The ‘Fire Agent’ QA has been fixed to consistently list all tasks that an agent was fired from. The agent was removed from all tasks, but the list would be incomplete if the Boss didn't oversee all of the agent's tasks.

#### Do not include calendar link if agent is not listed on a task (#11894) -
If an Agent/Boss is not listed on a task, a calendar link will no longer be included in the Morning Report for them.

#### Force refresh token if the token is expiring in 10 days (#11893) -
Some Lisa emails failed to send due to an inactivity error, but this has since been fixed.

#### Fix unparsed leads (#11902) -
Lisa can now parse leads from Rentable, formerly Abodo.


# 12/21/20

## TLDR: Outside Scheduling QA for Prospects, User Type Updates, and Small Bug Fixes.

### 🚀 Features

#### Add ‘Outside Scheduling’ QA from Prospect thread (#11781) -
Prospect threads now have an [‘Outside Scheduling’ QA](https://dynastycom.slack.com/archives/CB757SQ4S/p1608169284253500) that will automatically check in with an Agent regarding outside scheduling.
![Prospect Outside Scheduling](https://user-images.githubusercontent.com/60522124/102944866-08dfea80-448a-11eb-9d6b-44acb1d5b8de.png)

#### Allow Operators to see floorplans and marketing info (#11846) -
Operators can now see the UNITS and MARKETING sections on task pages in CC.
![UNITS section of task page](https://user-images.githubusercontent.com/60522124/102944309-62471a00-4488-11eb-9d48-9cddf5d525f7.png)
![MARKETING section in CC](https://user-images.githubusercontent.com/60522124/102944812-e2ba4a80-4489-11eb-9216-280b07854dd3.png)

#### Add spacing between firms in Threads section (#11848) -
The list of a Prospect’s other threads now has a line break between firms to help break up the visual.

Before:

![Threads before line break](https://user-images.githubusercontent.com/60522124/102944432-c1a52a00-4488-11eb-97da-6bdeca7cd276.png)

After:

![Threads after line break](https://user-images.githubusercontent.com/60522124/102944435-c4a01a80-4488-11eb-8c59-f1a79201cea8.png)

#### Better punctuation (#11788) -
The colon in SEND_CANCEL_SHOW_DELAY_SHOW has been updated to a comma to be more grammatically correct. The message now reads:
"I'm so sorry, our agent just had something come up %s at %s. I'm looking now to find the next available time."

#### Fix Covid Lockdown admin job (#11815) -
The Covid Lockdown admin job needed a little extra TLC. It will now:
- Remove the In-person tour type from specified properties.
- Reject all previously scheduled in-person tours.
- If virtual or self-guided tours are not already enabled, the admin job will request one to be chosen.
- A new cancellation reason called “Tour type no longer supported” has been added and will explain why a given tour was cancelled:
![Tour type no longer supported copy](https://user-images.githubusercontent.com/60522124/102944611-4abc6100-4489-11eb-97e4-568e96172255.png)

#### Add gif to onboarding request property details message (#11835) -
When onboarding a new property, Lisa’s message now includes a gif showing how to get to the Lisa Settings page. This will help reduce confusion as well as the number of Support cases around onboarding settings.

#### Add functionality to rerun failed actions (#11763) -
There is now a UI element that will allow us to rerun a failed action if it did not pass the Message Validator. This applies to both the unsent message and all actions attached to that message.

![Rerun failed actions](https://user-images.githubusercontent.com/60522124/102944758-b7cff680-4489-11eb-858f-2ff12854f34e.png)

#### Usertype changes (#11837, #11793, #11797, #11698, #11807, #11808, #11854) -
We’ve added new user types to replace the vague admin user type and updated permissions. Those user types are ENGINEER, OPS_MANAGER, TEAM_LEAD, SUPPORT, and ANALYST. The admin screen has also been reorganized to have simpler categories for the new user types.

### 🐛 Bug Fixes

#### Improved ‘Edit Agent Hours’ QA (#11833) -
The ‘Edit Agent Hours or Precedence’ QA no longer errors out if we do not have a direct link.

#### Fix user update input (#11794) -
There is no longer an error when a user with admin permissions tries to update the UserType field in CC.

#### Unbreak NOTIFY_TASK_AGENTS threads (#11805) -
Threads with old Notify Task Agent commands will no longer break and cause a black screen.

#### Fix Christmas Operator draft shifts (#11787) -
When we last updated the Operator hours/shifts, special days (like holidays) were not updated. This has been corrected and measures have been put in place to help ensure the correct number of shifts are available.


# 12/14/20

## TLDR: Emergency QA, Improved Slackbot Alerts, and Small Bug Fixes.

### 🚀 Features

#### Add ‘Emergency’ QA (#11733) -
If someone sends a message indicating they are worried about their safety or the safety of their neighbors, we can use the [‘Emergency’ QA](https://dynastycom.slack.com/archives/CB757SQ4S/p1607624177229500) to provide the following text: "If you or someone else is in immediate danger, please call your local emergency services."

#### Remove ‘Application status’ QA (#11732) -
The ‘Application Status’ QA has been removed because it overlaps with the ‘Application’ QA. [Read more here.](https://dynastycom.slack.com/archives/CB757SQ4S/p1607547360201500)

#### Tag the Operator when clocking them out (#11764) -
The automatic clock out feature now tags the Operator that is getting clocked out. This will help make the clock out more visible and the Operator can take action if needed.

#### Add firm name to prospect search results (#11728) -
When searching for a Prospect thread in CC, only the property name would show in search results before. Now, the firm name appears before the property name in Prospect search results.

#### Relocate "sitting threads" alert (#11767) -
The “sitting threads” alert will now only post to #lisa-operators in Slack if a Senior Operator is not clocked in. When a Senior Operator is clocked in, the alerts will go to the #lisa-senior-operators Slack channel.

#### Add sorting on displayname for tasks (#11780) -
Task display names are now in alphabetical order in the Bulk Update Admin job.

#### Decorate Christmas station tree (#11715) -
The station picker in CC has been decorated to look like a Christmas tree for the holidays! ![Station Christmas Tree](https://user-images.githubusercontent.com/60522124/102294970-63bc9380-3f18-11eb-948e-306d7750e8bc.png)


#### Matchmaker improvements (#11617) -
We have a matchmaker bot that pairs Senior Operators and the engineering team. The matchmaker will now follow up to ask about the status of their pairing.
![Matchmaker follow up](https://user-images.githubusercontent.com/60522124/102295022-88187000-3f18-11eb-8d76-e17d8e476fb1.png)

#### Release Notes relocated (#11703) -
As you can see, the Release Notes found a new home in CC! We hope that this change will help the Release notes be more accessible and the new location allows for better tracking.

#### Update Release Notes to have more legible body text (#11759) -
The text of the Release Notes in CC now have more contrast from the background for easier reading.

### 🐛 Bug Fixes

#### Auto-clock out improvements (#11771) -
The auto-clock out feature would clock out an Operator right as their shift ended if they had not sent a message within the last 5 minutes. In most cases, the service will now wait 5 minutes after the shift to clock an Operator out if they have not already done so.

#### Fix "Operator is back from break" message (#11734) -
The automatic Slack message stating that an Operator is back from break was not sending recently. This has since been fixed.

#### Messages with photos are no longer EMPTY_MESSAGE (#11761) -
Lisa recently categorized messages with a photo and no text as EMPTY_MESSAGE and would not unarchive. This meant that Operators couldn't see and react to the received ID until the next unarchiving. That error has been fixed so that Lisa can recognize a photo and properly categorize it as RECEIVE_PHOTO_ID. The message will also unarchive for a human eye to double check.

#### Use preferred thread for the ‘Calendar’ QA (#11721) -
The ‘Calendar’ QA now finds the Agent’s preferred thread when sending. We previously searched for their email and would receive an error if email did not match their preferred thread.

#### Fix unparsed leads (#11743) -
Lisa can successfully parse leads from collegerentals.



# 12/7/20

## TLDR: Clock Out features to limit sitting threads, Message Classification Updates, and Improvements to Slackbot Alerts.

### 🚀 Features

#### Auto clock out inactive Operators (#11658) -
We now automatically clock out Operators that have been inactive for 5 minutes and aren’t assigned to a shift. This is to prevent operators from accidentally staying clocked in and letting messages pile up in the inbox.

#### Shorter auto-clock out message (#11688) -
The Operator auto-clock out message in Slack has been updated to be shorter and more succinct:

Before: *Automatically clocking out CJ Harlow because CJ is not currently assigned to a shift and had over 5 minutes of inactivity.*
After: *Automatically clocked out CJ Harlow five minutes after shift.*

#### Show availability date with ‘Rent’ QA (#11677) -
We used to only mention the rent prices of units that were available at the time of a prospect's move-in date. Now, the ‘Rent’ QA will show the rent amount for all showable units and let the prospect know when each one is available.

![Rent QA text shows prices based on availability date](https://user-images.githubusercontent.com/60522124/101560875-3dd04580-3992-11eb-9d66-2c8ea39d7f60.png)


#### Improvement to ‘Cross sell’ (#11667) -
When an Operator tried to
a property to a prospect who already had a thread with that property, the ‘Cross sell’ QA would not automatically send a message from the new task. Now, in this scenario, an error message will show in the QA dropdown saying: “Prospect already has a thread with this property.”

![Cross Sell Reminder Text](https://user-images.githubusercontent.com/60522124/101560982-740dc500-3992-11eb-948e-b30545d9ece7.png)


#### New "COVID lockdown" admin job (#11685) -
There is now an admin job in CC called [‘COVID lockdown’](https://dynastycom.slack.com/archives/CB757SQ4S/p1607020264178700) that will remove `In-person` as a tour type for the given task(s) and Reject all future `In-person` tours list at the specified task(s).

#### New limit for the `waitingForSlot` status (#11665) -
We don’t want Lisa to Offer an appointment that is too far in the future. There is now a 30 day limit for the “Waiting on showing” status, which will prevent Lisa from offering a showing that is more than 30 days out. This will allow Lisa to successfully follow up with the prospect before their thread becomes inactive if the prospect does not respond.

#### Reworking the Operator reclassification experience (#11615) -
With the removal of the Reclassification UI in CC, Lisa outbound messages could not be stopped without clicking Stop Bot. Operators provided feedback that they would rather have the ability to fix an intent and induce the bot to do the full action, rather than reconstructing the full correct action from scratch using QAs.

Now, the ‘Reclassify’ QA can be used to directly reclassify the last inbound message based on context!

![Reclassify Quick Action in Command Center](https://user-images.githubusercontent.com/60522124/101561155-c8b14000-3992-11eb-8695-2c60efe077ee.png)

#### Safeguard against possible mass showing cancellation (#11495) -
In an effort to prevent any bugs that mass-cancel showings, there is now a layer for stopping these cancellations and alerting engineers.

### 🐛 Bug Fixes

#### Hotfix: Do not Skip Inbox on Boss and Agent threads (#11696) -
A recent Boss message was not unarchived by the bot and skipped the inbox. This has since been fixed so that Boss and Agent threads will not skip the inbox.

#### Fix pre-shift auto clock outs (#11686) -
If an Operator clocked in before their shift started, they would be clocked back out. Now we give a 5 min period after any clock in before clocking people out.

#### Add slack alert to Operators for sitting threads (#11668) -
The previous Honeycomb alerts that pointed out threads that had been sitting idle were not easily understood and prone to error. Now only a slack message from Lisa will be sent to #lisa-operators if a thread has been sitting for more than 20 minutes.

![Slack alert for sitting threads](https://user-images.githubusercontent.com/60522124/101561412-4d03c300-3993-11eb-8b67-d315fa090326.png)


# 11/30/20

## TLDR: Big change to the `STOP SHOWING` QA, improvements to the agent calendar widget, and a number of tiny but helpful tweaks in CC

### 🚀 Features

#### Improve `Stop` QA (#11398) -
We've made some big changes to the `Stop Showing` QA that we send to agents and bosses! In the past, this QA would just send notice of the units that need to be marked as not showable. Now, we'll actually have Lisa stop showing units of that type.
Note that the behavior below only applies to Appfolio firms
(Not sure how to know what PM system a firm is on? Hover over the firm name in a prospect thread).

#### The QA will now:

- Move the showable on date to 120 days out for all units of that unit type that are currently showable so that we don't end up scheduling more showings for that unit type.
- Reject and mark all current showings for that unit type as CANCEL_BY_US so that those time slots can be allocated for appointments for other unit types.
- Let the agent or boss know that we've canceled all future showings for that unit type and also stopped others from being scheduled.

#### Show when the bot unarchives in CC (#11486) -
One of our projects this quarter is to improve Lisa's automation rate: this means that some messages don't make it into the inbox at all, because Lisa has a high enough probability to answer messages correctly that we feel comfortable with her handling the messages herself.
In order to make sure we understand what's happening in Command Center, [there is now a text line](https://www.google.com/url?q=https%3A%2F%2Fdynastycom.slack.com%2Farchives%2FCB757SQ4S%2Fp1605904829074200&sa=D&sntz=1&usg=AFQjCNGpefndVV16gYt4b78TtshxSblGsg) that tells us when the bot unarchives a thread. This will allow Operators to see when the bot unarchives and when Lisa decides to handle things on her own.

#### Clearer `Multiple Offer Show` (#11567) -
The `Multiple Offer Show` QA and bot function will only show the availability if there is a single interested unit type. This will help the outbound message feel less strange in situations where a prospect is interested in multiple unit types, but the offered appointment is not for the earliest available unit type.

#### Adjust policy as keyword for `Pet Policy` language (#11569) -
The word "policy" has been removed as a keyword for the `Pet policy` QA because it was pulling up too many results. The word "policy" will now only trigger the `Cosigner policy` QA, which will help prevent overlap of trigger words when searching for the appropriate QA.

#### Free/busy block modals (#11551) -
Our amazing new [agent calendar widget](https://www.google.com/url?q=https%3A%2F%2Fshare.getcloudapp.com%2FE0u42PvP&sa=D&sntz=1&usg=AFQjCNHBiFUbYI4r9DhZCZQXXYx60csoRg) now allows for easier debugging of free/busy blocks. Clicking on a block opens up an informational modal that provides further detail on the chosen block.

#### Add New Years Eve (#11572) -
A client recently asked Lisa to block the last day of the year (New Years Eve) as a firm-wide holiday. NYE is now a holiday that Lisa can block automatically and list in the 'Holiday' QA.

#### Bulk updates for Leasing Specials (#11594) -
Some of our largest customers will update their leasing specials every week and sometimes they'll send in roughly 60 updates at once. Moving forward, the bulk update admin tool can be used to save a single special to multiple tasks at once! [Read more here](https://dynastycom.slack.com/archives/CB757SQ4S/p1606249102102100).

#### Add office phone to Settings csv (#11537) -
The Settings.csv linked in each Friday funnel message provides a comprehensive view on most relevant Lisa settings. The office phone number is now listed there.

#### Better bot logic when "showable on" changes (#11433) -
A classic Lisa problem is when dates change out from under us. What should happen when an appointment is scheduled for tomorrow, but then the showable date jumps to late January?
Now, Lisa can better handle those appointments based on the property type. With single-family residences (SFR) Lisa will reschedule, and with multifamily residences (MFR) Lisa will only reschedule if the showing is rejected by an agent.

#### Improvement to 'Add Agent' QA (#11531) -
To reduce confusion about using the Add Agent form for APM, there is now additional information that gets sent. Lisa will say "Make sure that the agent has an account in AppFolio first. You have to use the email for that account when filling out this form."

#### Speed up `fetchOnboardingTasks` (#11306) -
When Lisa checked for new properties to be onboarded using a job called `fetchOnboardingTasks`, the process used to load all leasing tasks and slowed CC down. This process has been sped up to help prevent slowness with CC.

#### Copy change with `Calendar` QA (#11530) -
When sending the `Calendar` QA to an Agent, Lisa would include the agent's name at least twice. The copy has been updated to include the word "your" when sending an agent their own calendar with the `Calendar` QA.

### 🐛 Bug Fixes

#### New Cancellation behavior (#11542) -
Lisa recently ended up cancelling appointments that were in the past for SFR tasks after a new cancellation behavior was introduced. This has been fixed so that if the SFR is showable and a Prospect is scheduled for a showing, but then the showable on date is adjusted, only future showings are cancelled. These cancelled showings will either be rescheduled after the new showable on date or cross sold to a nearby property within the firm.

#### Fix `Prospect no show` QA (#11411) -
Previously, Lisa searched for prospect name/email/phone matches when using the 'Prospect No Show' QA, but only for the given agent thread that the QA was used from. Now 'Prospect No Show' can be used from any Agent or Boss thread connected to the given firm.

#### Fix `BOSS` or `AGENT` classification in NPS (#11570) -
The #lisa-surveys channel in Slack displays either BOSS or AGENT for each client name, but recently had some miscategorizations. Moving forward, the thread conversation type will directly determine if the contact is a BOSS or AGENT in NPS responses.

#### Improvement to message validator (#11520) -
We ran into a problem where a Prospect thread was considered inactive (no inbound within 30 days), but they had a scheduled appointment that was cancelled. This blocked Lisa's follow up messages that attempt to reschedule. Moving forward, Lisa will check for `SEND_OFFER_SHOW` and `SEND_APOLOGIZE_FOR_CANCELLATIONS` before blocking an outbound message with threads that may be inactive.

#### Fix Friday Funnel failure (#11516) -
We had multiple Friday Funnels failing to send repeatedly recently. This was happening for any agent who has had 0 total leads for the week, but has since been fixed so that the Friday Funnels can be delivered successfully.

#### Fix unparsed leads (#11538, #11603, #11602) -
Lisa can now parse leads from "homeistheline",  "andmarkproperties", and "@eenhorn.com".


# 11/30/20

### 🚀 Features

#### Operator survey will not block clock-out (#11440) -
The Operator Survey will no longer block Operators from clocking out if they are not able or opt out of responding to the survey. This will hopefully help reduce the number of failed clock out attempts when finishing a Lisa shift in CC.

#### Partial name match for 'Outside scheduling' QA (#11470) -
The ‘Outside Scheduling’ QA now works with a partial name match instead of requiring the full name.

#### Improved ‘Human Fallback’ QA (#11483) -
The ‘Human Fallback’ QA can now be used at any stage during the conversation with a prospect. This will allow Operators to better address situations, such as a prospect changing their mind at the last minute, without worrying about an incorrect message going out.

#### Rename preleasing to construction (#11481) -
The property type “preleasing” has been renamed to “construction” to help remove confusion.

#### Improved ACCEPT_MULTIPLE_SHOW (#11507, #11393) -
Lisa’s new galaxy brain is now able to more accurately understand which appointment to accept from a Multiple Show Offer. The galaxy logic is better able to parse these more complicated messages to choose the correct showing and accept.

#### Measure outbound actions (#11430) -
A log has been put together in an effort to track what actions take the most time and which actions are used the most in CC. It specifically tracks the time it takes between a thread being loaded in CC and an action taken such as sending a message, sending a QA, using ‘send now’, and archiving/snoozing a thread. The timer will reset after each action until archived/snoozed to also measure follow up actions.

### 🐛 Bug Fixes

#### Move all virtual tours to "group" mode (#11454) -
All Whereby virtual tours will be in group mode moving forward to prevent Agents or Prospects from getting “locked out” of virtual tour calls. The maximum number of participants in a call in group mode is 50, and of those 50, up to 12 can have their video feeds on at the same time.

#### Showing times for ‘Request Photo ID’ QA (#11465) -
The date and time can now be seen with the ‘Accept Show’ QA after the ‘Request Photo ID’ QA has been sent. The extra step of requesting a photo ID for self-guided tours previously led to the date and time not displaying in the QA drop down menu for ‘Accept Show’, but has since been fixed.

#### Better WaitingOnShowing (#11508) -
Previously sending an ‘Offer Show’ as “do without sending” in CC did not update the prospect thread to ‘Waiting on Showing’. Moving forward, ‘Send Ack Accept Show’ and ‘Send Ack Confirm Show’ messages will be checked to correctly update the ‘Waiting On Showing’ information to catch when OFFER or ACCEPT actions were used without sending the messages.

#### Fix: Send lockbox code after generating it (#11354) -
With self-guided tours, Lisa would occasionally tell a prospect that we do not have the lockbox key, even when we could successfully generate one. Now, Lisa will double check if a lockbox key failed to be provided and if so, will send a follow-up message 5 minutes afterwards saying: “Apologies, I was just able to get the lockbox code for the showing, here it is: *code*.”

#### Allow SEND_OFFER_SHOW after cancellation (#11479) -
The new thread activity validator recently prevented ‘Offer show’ from being sent right after a cancellation message. This has been fixed so that messages are ok if a prospect was just cancelled on and we are offering a new showing.

#### Fix broken lost showings section (#11435) -
The Evening Report has a "lost showings" section at the top that was recently broken when displaying multiple lost showings for a single agent. This has since been fixed to address the strange line breaks so that the Evening Report can read more easily.

#### Fix to sending Morning Report (#11464) -
The Morning Report recently did not send correctly and showed a null error. This happened because some background processes finished in the wrong order, but has since been fixed so that messages are sent successfully.

#### Fix unparsed leads (#11485, #11494, #11488) -
Lisa can now parse leads from ambscallcenter, broadstoneranch@eenhoorn, and homeispullmanpointe.


# 11/6/20

### 🚀 Features

#### Remove ‘Flag Problem Agent’ QAs (#11357) -
The ‘Flag Problem Agent’ QAs in CC has been removed. These QAs turned out to be tonally incorrect, not as useful as expected, and were supplanted by Q3 Client Friction experiments and the subsequent new Agent QAs.

#### Add Unit Type to ‘Offer Show’ QA (#11436) -
The ‘Offer showing’ QA didn’t previously show unit type, which occasionally led to some confusion when a prospect had multiple interested unit types. Now the unit type is shown in the QA dropdown so to distinguish which unit type a given appointment is for. An example would look like: “Offer 2BR 11/20 10:00am” instead of “Offer 11/20 10:00am.”

#### Thread activity validation (#11355, #11434) -
In an effort to prevent TCPA violations, Lisa now has checks in place to make sure that a thread is still considered active before sending a message. These checks only permit a message to be sent if one or more of the following are true:
- When it's a BOSS / AGENT / INVESTOR thread.
- When the lead has messaged in to that firm recently (within 30 days). (This permits both normal conversation and same-firm cross sell).
- When the lead has a showing at the thread's task recently (within 30 days or in the future).
- When we're trying to SEND_REACTIVATION and this thread ended a snooze within 1 day.
- When the outbound message is sent by an Operator.

#### Whitelist cross sell and agent reach out (#11438) -
Messages generated from the ‘Cross sell’ and ‘Agent reach out’ QAs have been whitelisted so that they are not flagged as TCPA violations.

#### Move gifs from CloudApp to AWS (#11428) -
There are a few .gif links we send to customers to explain how to work with Lisa features. We've had intermittent issues with CloudApp links being available, so when they failed again recently it was decided to move all .gifs to an S3 bucket in AWS for durable storage.

#### Increase retry count (#11420) -
As Exchange emails are being migrated to Microsoft Graph API, some messages have failed to send. The retry count for resending failed messages has been increased from 3 to 5 to help ensure that the messages are sent properly.

#### Switch intent classifier (#11419) -
The intent classifier takes cues from an inbound message to classify that message with the appropriate tags or follow up QAs. Using the new galaxy brain, a platform that can analyze large datasets, it was shown that human Operators preferred galaxy brain's classification 80% of the time. Continuing toward a more accurate and confident Lisa, the intent classifier has been switched over to Galaxy Brain.

#### Show Agent calendar link in Morning Report (#11400) -
The Agent calendar link is now included in the Morning Report. This will hopefully help reduce calendar support issues while also providing agents with a clear visual of their schedule.

#### Add parser for trionliving.com (#11397) -
Trion recently created a new company website, trionliving.com, so a new parser was added to capture new prospects.

### 🐛 Bug Fixes

#### Fix Agent Confirmation status (#11377) -
Agents recently reported some mysterious cancellations for appointments that they had Confirmed with Lisa and the ‘Accept Showing’ QA was used. Because these appointments were confirmed without a PM sync, they were not always recognized as fully confirmed like when an agent Accepts directly from their calendar. This has been fixed so that both methods of Accepting an appointment are registered as the agent being Confirmed.

#### Smaller date range with fetch calendar (#11410) -
The fetch calendar event can run into an error if there are too many dates in the result of it’s fetch. Moving forward, the fetch calendar event has additional requests to make sure we fetch 60 days of data. This will result in a request for a smaller date range if there are too many entries, preventing an error.

#### Fix intentTree sorting (#11418, #11404) -
The bot was incorrectly classifying affirmative inbound messages as CANCEL_SHOW and NOT_QUALIFY. This has since been fixed so that Lisa correctly classifies inbound messages.

#### Improved multiple-offer show (#11406) -
When using ‘Send Multiple Offer Show’, Lisa will pick two dates that are at least 3 hours apart. However, Lisa would sometimes offer an appointment that was more than a month away. Moving forward, Lisa will make sure the slots picked are never more than 8 days out. In the cases where we're not able to find enough showing time variation in the set of available slots, we will fall back to simply offering one showing.

#### Fix to leasing special action log (#11408) -
The Action Log in CC did not include the end date of leasing specials when those dates were updated. This caused confusion when troubleshooting information changes and has since been fixed to include the end date for more accurate information.

#### Better ‘Offer Showing’ (#11407) -
The ‘Offer Showings’ bot action and QA was not rejecting past offered showings with the same time, but different tour type. This led to confusion because an 11am in-person and 11am virtual tour on the same day could be offered in tandem, but only one tour type could be conducted. Moving forward, the ‘Offer Showings’ action will reject all past offered showings unless they are the same time and same tour type.

#### Fix parse for rentReady info (#11376) -
The Yardi XML that Lisa received for properties was not always uniform with the other MITS-compliant PM systems, leading to discrepancies with some availability details. Now Lisa can parse the received data to better  translate rent ready information.

#### Don’t reply to 404 error messages (#11382) -
A number of messages were recently failing to send and returning with a “404: Not Found” error. This has been fixed by generating a brand new message instead of replying to the previously failed message chain.

#### Parse rentcollegepads emails (#11426) -
Lisa can now parse messages from “rentcollegepads.com.”


# 10/30/20

## 🚀 Features

#### Do not archive/snooze on “Do without sending” (#11308, #11333) -
Operators are able to perform an action in CC as “Do without sending” to prevent awkward or repetitive messages with prospects. More often than not, a “Do without sending” QA requires a follow up action. As a result, the archiving/snoozing behavior has been removed in those cases so that the thread view is not automatically altered before each intended action is complete. [Read more here.](https://dynastycom.slack.com/archives/CB757SQ4S/p1603828052372600)

#### Add ‘Listings Link’ QA (#11350) -
Sometimes a prospect will ask for a link to all of a firm’s listings. The ‘Listings Link’ QA will now allow Operators to pass along that information without updating the prospect’s thread to “not waiting for a showing” prematurely. [Read more here.](https://dynastycom.slack.com/archives/CB757SQ4S/p1604097236409900)

#### Restore date preview in the ‘Calendar’ QA (#11342) -
The ‘Calendar’ QA in CC now provides a preview of the chosen date within the dropdown menu no matter how the date is entered. Both date formats will work, but previously the entered date would need to look like “2020-11-27” for the preview. Now simply typing “11/27” will also provide the same preview of “Send Calendar Link for *Agent name* Fri Nov 11”.

#### Show agent's decline time in showing modal (#11327) -
The showing modal for the agent calendar widget now includes the time when an appointment was declined for more accurate information representation.

#### Better showing modal (#11313) -
The showing modal in the agent calendar widget now includes a link to the prospect transcript when clicking their name. The prospect’s preferred point of contact will also be displayed moving forward, instead of defaulting to their phone number.

#### Change to send application reminder (#11300) -
The ‘send application reminder’ message will now be sent somewhere between 24-28 hours after a scheduled appointment. The previous threshold was actually between 24-72 hours, but has been reduced to minimize the message being sent at odd hours.

## 🐛 Bug Fixes

#### Better Accept Show (#11364) -
Some ILS and other inbound messages would interrupt Lisa when trying to Accept an appointment, preventing their appointment from being successfully booked. Moving forward, all inbound messages after an offered appointment will be double checked so that an Accept Show message will not be missed or interrupted.

#### Fix leasing source from Craigslist emails (#11335) -
When a Prospect emailed Lisa from the customer’s Craigslist listing, the source would incorrectly show as Post to Internet. This has since been fixed so that the inbound is parsed and lists the source as Craigslist as intended.

#### Fix to 'Still Interested' (#11321) -
When a prospect messaged in with a message classified as ‘Still Interested’, Lisa would sometimes not respond or continue the conversation. This often happened when the prospect did not provide an answer for their preferred unit type/tour type because Lisa has checks in place to avoid repeated questions. Moving forward, Lisa will look at the last inbound to check if they are still interested and follow up accordingly to keep the conversation moving toward booking an appointment.

#### Added leave button to whereby link for mobile (#11317) -
The Leave button was recently not present for mobile Whereby links when using our accident proof URLs. The Leave button is now available on mobile to be used as expected.

#### Fix unparsed leads (#11285, #11287, #11288) -
Lisa can now successfully parse leads from junesl, rentgrata, and 727west.


# 10/16/20

## 🚀 Features

#### Add ‘Outside scheduling’ and ‘Undo Outside scheduling’ QAs (#11070, #11005, #11023) -
Agents will occasionally message Lisa to mention that they had a conversation with a prospect on their own and want Lisa to stand down. In an effort to have a more consistent response, there are 2 new QAs to help Operators and Lisa follow up in these situations. The ‘Outside scheduling’ QA will:
- acknowledge the agent's request
- handle both cases: when they have a Lisa showing booked vs no future showing booked
- handle other threads: provide a list of other properties that the prospect has also contacted at the current firm
- give transcript links to the agent so they can see what Lisa said
- give office / agent phone number to the prospect, so they can contact the leasing office / agent
- add "outsideScheduling" tag to the prospect thread and prevent Lisa from offering a new appointment unless the ‘Undo Outside scheduling’ QA is used

#### Add ‘Decline all’ QA (#11146) -
The ‘Decline’ QA makes it easier for Operators to decline appointments on behalf of agents, but only applied to one appointment at a time. The new ‘Decline all’ QA allows Operators to decline all appointments on a single day or for a range of dates. [Read more here.](https://dynastycom.slack.com/archives/CB757SQ4S/p1602722052272900)

#### More messages skip inbox (#11184, #10939) -
The ‘Cross sell’ and ‘Agent reachout’ QA requests generate a new thread to message specified prospects and previously appeared in a CC inbox. Lisa is also able to send multiple showing offers in one message at certain stages of the scheduling process. Moving forward, those messages will simply skip the inbox as Lisa can auto-snooze once the desired message has been sent.

#### Improved ‘Cross Sell Search’ (#11105, #11222) -
When using the ‘Cross sell search’ QA in CC, Lisa will now include the available dates of the mentioned property or properties when filtering by availability and following up with the prospect. Information for Furnished, Parking, and Laundry have all been added to the cross sell search UI and a wider travel distance limit will be used to increase search results.

#### Agent calendar new labels (#11144) -
The agent calendar URL is making further progress and now has labels for working hours and calendar blocks for easier identification.

#### Improved ‘Reassign’ QA (#11102) -
The ‘Reassign’ QA in CC has been incredibly helpful, but would sometimes be difficult to know which option was the “correct” agent from the QA bar drop down. Sometimes the full name would be cut off or there would be several agents with the same first name. The QA will still act exactly the same, but now the drop down choices are more clear to choose from. [Read more here.](https://dynastycom.slack.com/archives/CB757SQ4S/p1602706155262600)

#### New table for responses (#11151) -
As Lisa is further learning how to best understand and respond to messages, an eventual goal is to have the ability to pre-fill the most likely QA. This will let the Operator 1-click validate the outbound. In an effort to get there, a new table was made to store responses so that it can be used in downstream tasks like bot logic and QA prediction.

## 🐛 Bug Fixes

#### Fix transcripts and other Lisa functions (#11174) -
Transcript links, Add Agent Forms, and Agent Calendar URLS were all down recently. This has since been fixed so that everything can be displayed and accessed as expected.

#### Fix to ‘Stop showings’ and ‘List Units’ QAs (#11111) -
Both the ‘Stop showings’ and ‘List Units’ QAs in CC have been a helpful tool for Senior Operators and Agents, but did not previously provide unit numbers when requested. Moving forward, the unit numbers will now be included to help Agents and Bosses more quickly identify what information should be checked and/or updated.

#### Agent calendar consolidated events (#11134) -
The blocked/free (red/green) sections of the same type previously overlapped in the agent calendar URL. Now, the events of the same type that are overlapping with each other will appear as one single event to avoid confusion.

#### SEND_MULTIPLE_OFFER_SHOW via QA (#11112) -
When the UI message reclassifier was removed, it was no longer possible to send a multiple offer show message as it was not a QA. Now it is possible to send a multiple offer show message using quick actions! This is important because offering multiple showings in a single message has a provably higher conversion rate than just one showing in a message.

#### Use ReplyAll instead of Reply for some messages (#11139) -
Some of Lisa’s messages, such as the Morning Report and Evening Feedback, were failing to send for some firms. This has been fixed by adjusting how Lisa sends the message and will be monitored as API changes occur.

#### Fix new accept showing logic for MFR (#11121) -
The fix previously mentioned in #10988 has made progress! If Lisa offers an appointment to the prospect and the prospect doesn't accept the showing immediately, availability for the unit can change. For MFR showings, Lisa will now re-look at availability once the showing is accepted to make sure the unit is still available before booking the showing!


# 10/9/20

## 🚀 Features

#### Cross Sell Search for all operators (#11093, #11029) -
The ‘Cross sell’ QA in CC has been hugely helpful for retaining leads by offering nearby availability within the same firm. There is now a new ‘Cross sell search’ QA that allows Operators to use a filter UI to find showable units that match the lead's needs. [Read more here.](https://dynastycom.slack.com/archives/CB757SQ4S/p1602015554112900)

#### Move status board errors (#11107) -
If Lisa runs into an error, such as a property missing an agent or trouble with a PM sync, it used to appear in the Status tab of CC. However, most status board errors for operations have been migrated to the Ops Sentry Board. Moving forward, the Errors section of the Status tab in CC will now link to Ops Sentry directly.

#### Cancel Entrata appointments when showing canceled (#11025) -
A few months ago with #10461, Lisa became able to remove appointments from Yardi PM calendars when an appointment was cancelled entirely. Now, Lisa can remove appointments from Entrata PM calendars as well. This will help prevent confusion around cancelled and rescheduled appointments.

#### Turn off message reclassification UI (#11096) -
The large UI change around removing message reclassification has launched for all Operators! Reclassification of inbound messages will happen automatically based on the QAs used in CC. This will continue to help Lisa more accurately understand messages and how to respond to them. [Read more here.](https://dynastycom.slack.com/archives/CB757SQ4S/p1602003746103500)

#### Convert CC fetchThread to a subscription (#11011) -
Before, the browser would refetch data periodically for a thread in CC, such as new messages and other thread info, even if no changes were made. Now, the server should notify the browser when something within the thread page has changed, and only then will the browser refetch data. The primary goal is to reduce the amount of time the server time is “wasted” getting thread data that hasn’t actually changed since the last poll.  [Read more here.](https://dynastycom.slack.com/archives/CB757SQ4S/p1602524246181700)

#### Add Javadoc rubric to README (#11082) -
A Javadoc rubric was previously written to outline “good” documentation of code changes for partial releases and other updates. This documentation is intended to summarize Lisa content for engineering, PM, and support in a way that does not require close familiarity with code. The Javadoc has been edited for clarity and added to README for easier access.

## 🐛 Bug Fixes

#### Fix to rejection logic (#11113) -
Lisa had a bug recently where multiple appointments were offered behind the scenes, but only one appointment was directly mentioned in Lisa’s outbound message. The rejection logic used to happen as a side effect when the ‘Offer’ QA was used after a previous appointment had been offered and would not always catch all previously offered appointments. Now, both the reject and new offer happen at once and is more reliable because the system does not need to know about the previous offers to do the rejections.

#### Check for showing availability when accepting a showing (#10988) -
When a unit is marked as ready to show but still occupied, Lisa will notify residents of scheduled appointments with at least 24 hours notice due to legal requirements. However, there was a bug where some of these notifications were sent with less than 24 hours notice. A fix is in the works and this first step will allow Lisa to double check if a previously offered appointment is still available or within the appropriate time window when a prospect Accepts it.

#### Don’t adjust APM showing duration if it’s 15 minutes (#11101) -
Lisa communicates to the current resident about upcoming showings in their unit and recently started including the appointment duration. However, it was found that Lisa occasionally mentioned the wrong duration for an appointment. For example, Lisa quoted 30 minutes when the appointment was actually 15 minutes. Moving forward, APM showing durations will adjust based on the showing duration set in APM.

#### Fix: Add Boss Onboarding Templates Back (#11089) -
The onboarding template was accidentally removed briefly, so Lisa was not able to send information for Marketing, OPS, or IT. These onboarding QAs have since been added back and are accessible again.

#### Add answer questions back (#11084) -
Certain Agent/Boss QAs that answered questions had accidentally been removed with a recent update. These QAs, such as ‘Feature request’ and ‘how to start a virtual tour’ have since been added back for use in CC.

#### Fix unparsed leads (#11110, #11091, #11090) -
Lisa can now parse leads from pro.zlead.co, AMBS Call Center, and mayn suites.


# 10/2/20

## 🚀 Features

#### Unify calendar block sync (#11052) -
Clients have reported some instances of their calendars being blocked for dates some time in the future, but Lisa was not able to see those blocks. It was discovered that the various platforms and services that are used to synchronize calendars were not always on the same page. This caused some events to not be seen, depending on exactly when they were viewed. Moving forward, Lisa will retrieve calendar information at midnight, can see events 60 days out, will share that information between necessary providers, and will no longer be limited by the number of calendar events that can be seen with each sync.

#### Replace old question tagger (#11062) -
The question tagger is one of the ways Lisa categorizes messages to better understand what questions or topics a prospect might be asking about and how to respond. Working toward a smarter and more accurate Lisa,  a code change has replaced the old question tagger with a new one that will retrain Lisa on the best responses to a given message. The new question tagger was proven in trials to have higher accuracy and will further help Lisa distinguish message content to confidently respond with less human fallback.

#### Better Combo QAs (#11044) -
The combo QA matching algorithm has been improved significantly! CC will now prioritize returning the best matched QAs entered as well as the same number of QAs requested. This means that typing “rent + pet” will no longer pull 3 or 4 combined QAs that might match, and will instead prioritize just those two QAs.

#### Allow QAs on Investor threads (#11058) -
When an Investor previously messaged Lisa with a request, a Senior Operator or Team Lead needed to run through a number of extra steps to fulfill that request. This was because Investor threads did not have access to QAs. Now, designated Agent/Boss QAs are also accessible on Investor threads to quickly help with requests around marketing, leasing specials, and the like.

#### Reduce variation in ‘Welcome’ QA (#11048) -
The ‘Welcome’ QA has been well received as a quick and easy way to acknowledge gratitude. However, Operators have reported some dissatisfaction with the possible variations and will manually change them. The variation has since been removed and the ‘Welcome’ QA will only pull “You’re welcome!” when used.

#### Send previous night confirm (#11049) -
We saw a decrease in early morning cancellations when we moved the confirmation to the night before. However, we still had plenty of unwanted cancellations on prospects who have afternoon appointments with no access to their phones in the hours leading up to their appointment. Now, Lisa will automatically confirm appointments by 8pm the night before appointments scheduled before 12pm at off-site properties.

#### Operator survey update (#11057) -
The Operator post shift survey has been updated to include a CSAT, 5 scale response. This change will help measure user happiness / comfort over time, and further allow the team to track how we're doing with communications.

## 🐛 Bug Fixes

#### Fix Add Agent form(#11042) -
The add agent form that can be sent to Agents and Bosses was not allowing users to choose the desired property or properties from the list. This has since been fixed so that Agents and Bosses can add new agents.

#### Reassign all of an agent's showings when firing (#11022) -
The ‘Fire’ QA was not reassigning or rescheduling appointments when removing the Agent. This has been fixed so that Lisa will double check the Agent’s tasks and scheduled appointments before removing them, and then reassign or reschedule accordingly.

#### Send "Stop Showings" to the right boss (#11043) -
The ‘Stop Showings’ QA recently had some bugs: it escalated to the wrong Boss, sometimes went to an Investor, sometimes did not include the list of units that needed to be updated, did not include the showable date, or did not have the showable date if that date was for a different year. These have all been fixed so that the messages are delivered to the appropriate parties and include the necessary information in a clear fashion.

#### Fix ‘Accept / Decline’ QA (#11037) -
The ‘Accept’ and ‘Decline’ Agent QAs previously did not work for appointments where the agent was offered the appointment, but had not yet responded to the invite. Moving forward, the QAs will work with OFFERED showings so that Lisa can adjust the invite response on the Agent’s behalf.

#### Improved Application Link (#11050, #11004) -
For AppFolio clients, Lisa references the floor-plan type associated with that unit, and pulls the application link that best fits that floor plan. However, Lisa was recently not able to pull the proper application link for some properties. Now, Lisa will now generate the link based on the following priority:
1. Posted campaign matching unit type
2. Rent quoted units matching unit type
3. Posted showable units matching unit type
4. Fallback to generic listings URL

#### Fix calendar sync error message (#11038) -
There have been a number of small, persistent bugs with Lisa’s Exchange integration. An error was found when turning on MS graph API for a new firm, but has since been fixed to allow Lisa to properly link with the necessary systems.

#### Always use advertised rent in APM sync (#11027) -
AppFolio has historically provided 2 fields that can be used for price quoting: the market rent and the advertised rent. However, a third price field for computed market rent was recently added, which takes the market rent for the unit plus the total value of all amenities on the unit. To prevent confusion or inaccurate information, Lisa will always use the computed market rent or advertised rent when communicating with prospects moving forward.


# 9/25/20

## 🚀 Features

#### Steps toward reclassification UI change (#10952) -
A large UI change is in the works to help Lisa more accurately understand messages and how to respond to them! With this change, Operators will use the QA bar in CC to reclassify inbound messages instead of using the tag or reclassify widgets. The UI change has been rolling out in stages, first to the Senior Operators and then to the Trainers, to allow time for feedback and adjustment. The announcement and full release will be coming soon.

#### Only use firm level threads for agent calendar URLs (#10933) -
Some clients might have multiple threads in CC that could be at the firm level or at the property level. Threads at the property level will not have access to Agent/Boss QAs and cause an error when trying to pull that agent’s calendar. The agent calendar URL will now only pull calendar information from a particular agent’s firm level thread in CC.

#### Agent calendar no overlap (#10920, #10999) -
The agent calendar URL previously experienced some overlap of calendar events when more than one event was scheduled for the same time. Moving forward, calendar events will appear side by side with no overlap if there is more than one event at the same time.

#### Remove "give me a few minutes" (#10989) -
When a scheduled appointment is cancelled, Lisa will often follow up with a message that mentions there was a calendar conflict and then tries to reschedule. That follow up message also includes the phrase: “Give me a few minutes.” The process of finding a new appointment to reschedule will sometimes take longer than a few minutes, especially if there are no further appointments available. As a result, “give me a few minutes” has been removed to prevent setting incorrect expectations.

## 🐛 Bug Fixes

#### Fix time error (#10945) -
With the new ‘Agent late’ QAs, an error was found where the QA would not consistently appear as an option when needed. This would happen when the appointment duration carried over into the next hour from the start of the appointment. Ex: a 10:30am appointment lasting 45 minutes will end at 11:15am. This has been fixed so that Lisa can understand appointments that cover more than one hour.

#### Fix task page charts (#11024) -
Each firm and property task page has a ‘Stats’ section that shows graphs for information like messages, showings, and unit counts. Those graphs were not appearing in CC for a short time, but have since returned.

#### Fix Guest Card Reopening (#11020) -
Clients have reported that Lisa would reopen a guest card for their property with no apparent reason. It was discovered that if a contact is updated, Lisa will sync all threads for that contact if they've been updated in the last 60 days. Moving forward, if a contact is updated, Lisa will only sync threads for that contact if they've been updated in the last 15 days.

#### Fix language with agent calendar QA (#11002) -
The copy in the ‘Calendar’ QA previously said to click ‘refresh’ to sync the calendar. The word "refresh" has been updated to "sync" to match UI.

#### Clear QA bar (#11001) -
The QA box in CC was retaining the search text, even after selecting and sending the desired QA. This has since been fixed.

#### Better search bar UX (#10995) -
The keyboard arrow keys were not working as a navigation method when using the search bar in CC. This has been fixed so that the up and down arrow keys can be used when navigating the drop down menu of the search bar.

#### Lobby handles overlapping shifts (#10992) -
The Lobby in CC was briefly down and unable to load due to overlapping Operator shifts. The Lobby can now handle overlapping shifts and will display who is logged in for their shifts as normal.


# 9/18/20

## 🚀 Features

#### Optimization: Make CC even faster (#10516, #10777) -
In an effort to help Lisa and CC work faster while being as accurate as possible, Graph Query Language (GQL) subscriptions have been implemented. These subscriptions will allow Lisa to receive client updates/requests, process any changes, and publish or send messages/information to clients in a faster way.

#### GQL Calview subscriptions (#10899) -
Fetching calendar queues tend to be a costly and slow process. Using GQL subscriptions to update Agent thread CalViews rather than the fetch queries has been implemented to help Lisa check calendar information more efficiently. CC will still display agent blocks, appointments with their appointment status, time of day, etc.

#### Application Status Update (#10766, #10931, #10938) -
A feature was briefly introduced where Lisa would send an automatic message to prospects to provide a status update once their application status changed. This has since been rolled back due to legal concerns along with the ‘Application Status’ QA .

#### Added sync button to Calendar Widget (#10895, #10476) -
A calendar widget is in the works to help debug calendar issues with agents. There is now a sync button in the calendar widget that, when clicked, will result in a full calendar sync.

#### Agent Calendar "prev day" and "next day" buttons (#10897) -
The calendar widget now has arrow buttons next to the date header, which look like « and ». These change the date to the previous or next days, respectively.

#### Agent Calendar Widget UI: date picker (#10752) -
Currently in the works for the calendar widget is a date picker feature to help jump to a specific date. The idea for this feature is that it will be a dropdown menu near the current displayed date and is still being designed.

#### Calendar {date} {agent} QA (#10812, #10893) -
A new Agent/Boss QA has been added to CC that will pull a calendar link for a specific date, connecting the agent or boss to the calendar widget. In CC, simply type “calendar date agentName” into the QA bar to pull a calendar link for the specified date and agent. If no date or agent name is entered, a calendar link for the current day and agent thread will be generated.

#### Always write showing details in CalEvent description (#10917) -
Lisa provides appointment details in the calendar event for all scheduled appointments. However, once an appointment is cancelled some of those details are removed; such as the transcript link. Moving forward, Lisa will always write a calendar event with a description that includes, where available: contact name + phone/email, status: cancelled, etc., questions answered, rent quoted, unit to see, and the transcript link.

#### Add ‘Reject All’ QA (#10804) -
Sometimes a Boss or Agent tells Lisa that a full day of showings needs to be Rejected; meaning "cancelled permanently with all prospects rescheduled." Previously each appointment needed to be rejected individually, which led to a slower workflow and many individual messages to agents. With the ‘Reject All’ QA, all appointments for a specified date can be rejected and followed up with in one go. Lisa will also message the agent or boss with a message like: “OK, I've cancelled the two showings tomorrow Woodside Apts as you asked. I've messaged all three prospects to reschedule them for another available time.” [Read more here.](https://dynastycom.slack.com/archives/CB757SQ4S/p1600301007326500)

#### Add ‘Help’ Question QA (#10885) -
The question “Is there anything else I can help you with at this time?” or a similar variation gets typed out manually by Operators a fair amount when communicating with prospects. Now there is a QA in CC called ‘Want help?’ that will automatically populate one of two variations on the help question: “Anything else I can help you with today?” or “Anything else I can help you with at this time?”

#### Adding period for Agent reach out Request (#10886) -
The previous language for Lisa’s message to a prospect after the ‘Reach Out’ QA was used had a run on sentence. A period has been added to resolve this and the message now reads: “Hi, this is Lisa. I got your contact info from our team at Southview Park Apartments. Which unit for Southview Park Apartments are you interested in? We have a 2BR and a 3BR.”

#### Links open in new tab again (#10881, #10854) -
Links opening in the same tab made it difficult to check external websites and other task pages against the starting CC tab. Now, external links to property pages as well as internal links to linkified task pages will open in a new tab again.

#### Update ‘Agent Late’ language (#10874) -
The language for the ‘Agent Late’ QA has been updated to account for the tour type and the start time of the appointment.
- Virtual: “Looks like your appointment is starting soon. Our agent may be finishing another task, but should log on shortly. If you haven't connected with them by (time), please let me know!” or “I'll let the agent know that you've logged on. Our agent may be finishing another task, but should log on shortly. If you haven't connected with them by (time), please let me know!”
- In-person: “Looks like your appointment is starting soon. Our agent may be finishing another task, but should arrive shortly. If you haven't connected with them by (time), please let me know!” or “I'll let the agent know that you've arrived. Our agent may be finishing another task, but should arrive shortly. If you haven't connected with them by (time), please let me know!”

#### Improved shift reassigning (#10773) -
Previously, an Operator could not reassign a partial shift to another Operator who is scheduled on the preceding or following shift. Because Operators frequently split shifts, it is now possible to reassign partial shifts in the Lobby of CC, with or without overlapping shifts. [Read more here.](https://dynastycom.slack.com/archives/CB757SQ4S/p1600719343010800)

#### Add ‘Can’t Call’ QA (#10873) -
Prospects will often ask for a phone call from Lisa, but unfortunately Lisa is only able to communicate via SMS or email. The ‘Can’t Call’ QA has been added to CC so that Operators can more quickly send a message like: “I can't make or receive calls on this line. I'm happy to help via text.”

#### Activate app status for Entrata and Resman  (#10863) -
Lisa has been able to see application status information for Entrata and Resman for a couple of weeks and can now provide that information to prospects when available. Simply using the ‘Application Status’ QA in CC will return with the requested information when available and will refer prospects to the leasing office for further assistance/updates regarding the application process.

#### Better trigger for ‘Future Availability’ QA (#10867) -
The word “availability” is no longer a trigger word for the ‘Future Availability’ QA. This will help reduce clutter in the QA bar when combining multiple QAs and was helped along by some great Operator feedback!

#### Filter out Boss threads when forwarding emails (#10861) -
Lisa recently started forwarding admin emails from Craigslist.com, Facebook.com, and RentCafé.com to all Agents and Bosses listed on property tasks. Bosses, however, did not like receiving these extra messages. Moving forward, Lisa will only forward those admin emails to Agents on a given property.

#### Add QAs for simple questions (#10794) -
It’s been observed that Operators will add a question when answering prospect questions in an effort to elicit a response or continue the conversation. This will now be easier with two simple question QAs: ‘Work for you?’ and ‘Want to schedule?’
- The ‘Work for you?’ QA is triggered on "work", "ok", "you"
  - Rotates between these different copies: ”Does that work for you?”, “Is that ok for you?”, and”Would that work for you?”
- The ‘Want to Schedule?’ QA that is triggered on "want", "schedule"
  - Rotates between these different copies: “Would you like to schedule one?”, “Would you like to schedule?”, and “Would you like to set one up?” [Read more here.](https://dynastycom.slack.com/archives/CB757SQ4S/p1600123790267200)

## 🐛 Bug Fixes

#### Allow blank threads to write guest cards to APM (#10862, #10843) -
Prospect threads that were blank or only had one outbound message from Lisa did not create guest cards automatically. This led to some confusion with clients as several new threads were created from the ‘Reach Out’ Agent QA, but guest cards were not created if the prospect did not reply. Now, these blank threads will create guest cards automatically.

#### Fix: Show agent off hours (#10921) -
The agent calendar widget had a bug where an agent looked available for an entire day, even though they had an All Day calendar block. Moving forward, if an agent has no work hours, the calendar widget will show the entire day as off hours.

#### Fix email parsing test failures (#10909) -
There were test failures in one of Lisa’s email parsing files due to a small problem with name recognition. The file name has been updated to LeasingEmailParserTestGeneric.java so that moving forward these failures will be caught and Lisa can properly parse inbound messages.

#### Fix Next Showing (#10903, #10882) -
The ‘Next Showings’ section of CC was showing appointments, even after the appointments had passed. The first fix that was implemented caused CC to be unusably slow, so that was rolled back. This was probably because Lisa no longer pulls the appointment duration directly, and was fetching and loading more data in order to calculate the end of each appointment. Moving forward, Lisa is set to assume that all NextShowings end T+45mins after the start time of each appointment. This will cause 20 minutes appointments to linger for a little while longer, but allows appointments to drop off once completed without slowing CC as a whole.

#### Fix ‘Reach Out’ QA not working (#10901,  #10898) -
Recently, the ‘Reach Out’ QA in CC returned with an error even when the entered phone number or email address was valid. This has since been fixed so that Lisa can automatically reach out to prospects using the ‘Reach Out’ QA.

#### Fix: Restore Craigslist, Rentcafe, Facebook forwarding (#10857) -
Clients reported that they were not receiving forwarded verification emails from listing sites, even when they used Lisa’s property email to post vacancies. This was due to a bug in the code that did not recognize the syntax of those verification messages. The bug has been resolved by using a named email parser that can also handle the email forwarding.

#### Fix unparsed leads from Eenhoorn (#10919) -
Lisa can now parse inbound messages from support@eenhoorn.com for Eenhoorn properties.

#### Fix Sojiapts unparsed leads (#10910) -
Lisa can now parse inbound messages from wordpress@sojiapts.com for The Soji Apartments.


# 9/4/20

## 🚀 Features

#### Improvement to Support QA (#10739) -
The Support QA in CC now allows for an optional comment to be provided in an effort to give more context about why the thread was escalated. An example could be: “support their calendar blocks don’t seem to show up.” The support ticket will still pull the last inbound message from the agent or Boss, but will also now include the context about calendar blocks. [Read more here.](https://dynastycom.slack.com/archives/CB757SQ4S/p1599238016186100)

#### Release VT experiment to all users (#10771) -
The Dynasty Virtual Tour link, aka accident proof URL for virtual tours, has been released to all firms! As a refresher, the URL will cross reference current and upcoming appointments with the current time to ensure that the correct virtual tour is joined. If there is not a virtual tour around the time the URL is clicked, a message will display saying: “There is no current or upcoming virtual showing.”

#### Update to "new tab" behavior (#10764) -
When clicking certain links in CC, such as a different station from the Lobby or any linkified text in a thread, the link would open in a new tab. Now, link default to opening in the same tab.

#### Better floor plan understanding (#10762, #10767) -
Properties with all hidden units or non rentable units should have no floor plans available, but CC previously still showed those units. Moving forward, if all units for an APM property are hidden or non-rentable, the task in CC will show no available floor plans, and the bot will cancel any prospect appointments.

#### Exempt "affordable housing" units (#10675) -
Some units have very low rents, but that's because they're reserved for local affordable housing programs. We don't want to include these units in our floor plans because they're not available for everyone. Move forward, Lisa will filter out any units that are marked for Affordable Housing, so that they don't get included in the calculated floor plans and so that Lisa doesn't quote their availability (or rent) to anyone publicly. This is currently only with RealPage properties.

#### Link to other threads (#10660) -
Sometimes leads end up with multiple threads in CC. This can happen naturally as a result of prospect interest or because of cross selling. Now, there is a ‘Threads’ section in CC on the right side of each prospect thread that can link to other threads that the prospect has also messaged.

#### Organized multiple threads (#10761) -
If a prospect has messaged Lisa regarding multiple properties, the threads for each property can be seen on the right hand side in CC. Those threads are now organized by the firm name of each property.

#### CC Lobby link to stations (#10756) -
During high-stress scenarios when Lisa is receiving a lot of messages, it's useful to be able to jump into an Operator's station to provide assistance. The station assignments in the Lobby of CC are now linkified so that clicking them shows the inbox of the selected station.

#### Update ‘Service Animal’ QA Language for MAA  (#10757) -
MAA requested a change to the Service Animal message. The QA previously read: “We do allow service animals at the property. You'll need to get a letter from a licensed medical professional and tell our staff on your application. Now, the QA reads: “We do accept reasonable requests for service animals: just mention it on your application. Our team approves requests that follow Fair Housing laws.”

#### Application status for Entrata and Resman (#10740) -
We previously only had application status information for APM customers. Now Lisa can see application status information for Entrata and Resman as well.

#### New QA: ‘Prospect Running Late’ QA (#10673, #10732) -
There is new QA in CC for when a prospect is running late. It takes into account when the prospect messaged Lisa and, if it's still close to the appointment time, sends a reply to the prospect as well as a notification to the agent. If nearly half the appointment is over, then we will ask them to reschedule so that we make sure they get the full tour. [Read more here.](https://dynastycom.slack.com/archives/CB757SQ4S/p1599081664143700)

#### Bulk Update: Agents (#10646) -
The bulk update admin job can now cover: removing an agent from a property, changing their hours or precedence, adding them to a property.

#### More ‘ask for name’ (#10696) -
Lisa will automatically ask for a prospect’s name during the normal scheduling conversation flow. However, not everyone provides their name by the time an appointment is scheduled. Now, if there is not already a name on the thread, Lisa will automatically ask for a name further during the qualification process after an appointment is scheduled. [Read more here.](https://dynastycom.slack.com/archives/CB757SQ4S/p1599152031167200)

#### Improved ‘List Agents’ QA (#10715) -
The List Agents QA in CC was previously only accessible on a Boss thread. However, property managers will often ask about the showing order for their properties. Now, List Agents is an accessible QA on both Agent and Boss threads to help provide both parties with the requested information.

#### Language change to ‘Maybe Qualify’ (#10548) -
A prospect may ask for the qualifications before they book an appointment, and their response might be classified as a ‘Maybe Qualify.’ However, the previous language did not always apply. Now, Lisa can respond differently depending on whether or not an appointment has been book.
 - If a prospect is scheduled: “An agent can discuss our leasing qualifications with you at the appointment, let me know if that works!
 - If a prospect is not yet scheduled: An agent can discuss our leasing qualifications with you at the appointment. What time works best for you?

#### Operator Survey updates (#10691, #10712, #10713) -
CC now Imports useful operator insights from our Post Shift Surveys into the Slack feedback channel for easier tracking. These operator survey posts are now nicer looking to visually match feedback and survey posts.

#### Improved ‘Reach Out’ QA (#10687) -
The Reach Out QA in CC was previously only accessible on Agent threads. However, some Bosses also help with reach out and scheduling requests. Now, Reach Out can be accessed on both Agent and Boss threads in CC.

#### Change language for early morning appointments (#10688) -
The recent change to confirm early morning appointments on the night before their appointment seems to be going well. However, the language needed to be updated to account for “the night before” instead of “3 hours before” when informing the prospect of when Lisa will follow up.

#### Add ‘Square Footage’ QA (#10650) -
Sometimes prospects ask about the square footage for a property, but Lisa could not directly answer and an Operator could not always quickly pull those details. With the ‘Sqft’ QA, Lisa and Operators can quickly provide those numbers where firms allow. [Read more here.](https://dynastycom.slack.com/archives/CB757SQ4S/p1598974851102500)

#### Higher contrast for all type toggles (#10693) -
Recent Operator feedback mentioned certain aspects in CC can be hard to see. To help with this, some text-shadow has been added to increase contrast for the unitType chooser and others like it.

#### Send Delay Show addition (#10682) -
Recent improvements to Send Delay Show did not account for a prospect no longer being interested. Now, if a prospect is no longer interested they will also be updated to “Not waiting on a showing.”

#### Delete voice handler (#10674) -
In the past, Lisa would provide a single Twilio endpoint for clients to use when calling cold leads from the Morning Report. However, there are no longer any users of this feature, so it has been removed.

#### Add RentCafe email forwarding (#10721) -
Lisa's email setup often replaces some agent email list. That means notifications about application status / other admin notifications don't get through to the property staff anymore. Lisa will now use an email forwarding infrastructure to pass along necessary messages to prevent slower turn around with applications and other admin duties.

#### Add speaker token (#10737) -
A ‘speaker token’ has been added to Lisa's code so that conversation data will highlight if LISA or PROSPECT is the speaker during the dialogue. This is a part of the AI training dataset and will further help Lisa understand and respond to messages.

#### Reorder questions labels (#10711) -
A new tagger is in the works to help Lisa better understand the contents of a message. The question tag order seemed to be incorrect before, leading to incorrect message predictions. The question labels have since been reordered for better predictions and bot flow.

## 🐛 Bug Fixes

#### Better thread evaluation (#10734) -
‘SEND ACK ACCEPT SHOW’, the function that Lisa uses to accept an appointment, was not sending the correct date and time of the appointment that a prospect was intending to accept. This led to confusion for everyone involved around which appointment was actually scheduled. Now, Lisa can better reference what appointment is being referenced to properly display the correct date and time.

#### Fix: ‘Reject Showing’ QA (#10770) -
There were two obvious copy problems with the Reject QA: it said “messaged all prospects” when there's only one prospect and it said dates in a strange way; “canceled the showing on tomorrow at 8am.” Moving forward: the copy pluralization is correct, the time preposition is correct, the attending agent is marked ‘Cancelled by them’ instead of Cancelled by us’, and the QA will not try to guess the date and offer extra options.

#### Less inbox skipping for Not Interested (#10731) -
Lisa has, at times, labeled a message as Not Interested a little too quickly and not pushed the message to the inbox for review. Now, more messages that may fall into the Not Interested category will appear in the inbox for an Operator to review and process.

#### Fixed null pointer bug (#10710) -
All messages that come through Lisa receive a classification so that Lisa and Operators are able to proceed through the scheduling process as smoothly as possible. There has been a bug where messages don’t have a classification, which prevented the thread in CC from being viewed. This fixes the bug where messages don't have any classification, so that everything can be viewed and processed.

#### Middletownplace ILS parser (#10667, #10730) -
Lisa can now parse @middletownplace.com ILS messages for Middletown Place Apartments with Fairlawn.
